import React, { useEffect, useState, Fragment, useContext, useRef, useMemo, useCallback } from 'react';
import Widget from '../widgets/Widget';
import { useAuth } from '../hooks/AuthProvider';
import useWindowDimensions from '../hooks/useWindowDimensions';
import { capitalize } from '@mui/material';
import ToggleOff from '@mui/icons-material/ToggleOffOutlined';
import ToggleOn from '@mui/icons-material/ToggleOn';
import ExpandIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import '../Components/modal.css';
import './details.css';
import './WebDashboard.css';
import '../widgets/SegmentSwitch.css';
import ColorPicker from '../Components/ColorPicker';
import { EditDatastream } from './EditDatastream';
import Select from 'react-select';
import DragDropOptionList from '../Components/DragDropOptionList';
import ImageButtonSettings from '../Components/ImageButtonSettings';
import TextTitleSettings from '../Components/TextTitleSettings';
import BackgroundImageSettings from '../Components/BackgroundImageSettings';
import LabelColorSlider from '../Components/LabelColorSlider';
import TimeRangeSettings from '../Components/TimeRangeSettings';
import EnvironmentalStatusSettings from '../Components/EnvironmentalStatusSettings';
import { LanguageContext } from '../context/LanguageContext';
import webDashboardTranslations from '../languages/webDashboard.json';
import Alert from '../Components/Alert';
import AppDialog from '../Components/AppDialog';
import { WidgetBoxDivider, WidgetBoxFirstLayer } from '../svg/TemplateIcons';
import { CustomSwitch, DeleteIcon } from '../svg/UtilsIcons';
import BoxWidgetSettings from '../Components/BoxWidgetSettings';
import { buildDeviceDropdownOption } from '../utils/dropdownLabels';
import { getThemeColorKey, getThemeColorValue, normalizeThemeMode } from '../utils/themeColors';
import { PERIOD_COLUMN_WIDGET_TYPE, normalizePeriodColumnPeriod } from '../utils/periodColumnWidget';
import { buildDefaultTimeRangeStreams } from '../widgets/timeRangeParts';
import {
	ENVIRONMENTAL_STATUS_WIDGET_TYPE,
	buildDefaultEnvironmentalStatusMetrics,
} from '../widgets/environmentalStatusConfig';
import {
	normalizeWebDashboardLayoutMode,
	WEB_DASHBOARD_LAYOUT_MODES,
} from '../utils/webDashboardLayout';
import { getNextTemporaryDatastreamId } from '../utils/datastreamIds';
import WaterNetworkOverlay from '../widgets/WaterNetworkOverlay';
import {
	buildWaterNetworkConnections,
	canWaterWidgetsShareSpace,
	getWaterNetworkMinDimensions,
	isWidgetFreeMovementEnabled,
	isWaterNetworkWidgetType,
	isWaterNetworkLinkEnabled,
	snapWaterNetworkMoveRect,
	snapWaterNetworkResizeRect,
	WATER_NETWORK_FREEFORM_SEARCH_STEP,
} from '../utils/waterNetworkLayout';

const pinCategories = ['Virtual Pin', 'Enum', 'Location', 'Digital Pin', 'Analog Pin'];

const cloneDashboardValue = (value) => {
	if (value instanceof Blob) {
		return value;
	}

	if (Array.isArray(value)) {
		return value.map(cloneDashboardValue);
	}

	if (value && typeof value === 'object') {
		return Object.entries(value).reduce((acc, [key, nestedValue]) => {
			acc[key] = cloneDashboardValue(nestedValue);
			return acc;
		}, {});
	}

	return value;
};

const EDITABLE_TRAILING_ROWS = 2;
const WATER_TANK_WIDGET_TYPE = 'water-tank';
const HIDDEN_WIDGET_EDITOR_PROP_KEYS = new Set(['themeMode', 'freeMovement', 'linkToNearby']);
const VALUE_COLOR_WIDGET_TYPES = ['gauge', 'liquid', WATER_TANK_WIDGET_TYPE, 'radial-gauge', 'label'];
const SHOW_UNIT_WIDGET_TYPES = ['gauge', 'liquid', WATER_TANK_WIDGET_TYPE, 'radial-gauge', 'label', 'number'];
const LIQUID_STYLE_PROTECTED_COLOR_KEYS = [
	'cardBackgroundColor',
	'titleColor',
	'valueColor',
	'unitColor',
	'glowColor',
];
const NUMERIC_VALUE_WIDGET_TYPES = new Set([
	'gauge',
	'radial-gauge',
	'liquid',
	WATER_TANK_WIDGET_TYPE,
	'label',
	'number',
	'slider',
	'led',
	'switch',
	'push-button',
	'pipe',
	'elbow',
	'water-pump',
]);
const DEFAULT_WIDGET_GRID_GAP = 8;
const SLIDER_NUMBER_CONTENT_ALIGNMENT_OPTIONS = [
	{ label: 'Auto', value: 'auto' },
	{ label: 'Top', value: 'top' },
	{ label: 'Center', value: 'center' },
	{ label: 'Bottom', value: 'bottom' },
	{ label: 'Title Line', value: 'title-line' },
];
const getSpannedWidgetSize = (baseSize, spanCount, gap = DEFAULT_WIDGET_GRID_GAP) =>
	(baseSize * spanCount) + (Math.max(0, spanCount - 1) * gap);
const getMinimumWidgetSizeForType = (type, defaultWidth, defaultHeight) => {
	const waterWidgetMinimum = getWaterNetworkMinDimensions(type);
	if (waterWidgetMinimum) {
		return waterWidgetMinimum;
	}

	return {
		width: defaultWidth,
		height: defaultHeight,
	};
};

const getDefaultWidgetDimensions = (type, defaultWidth, defaultHeight) =>
	type === WATER_TANK_WIDGET_TYPE
		? { width: defaultWidth, height: defaultWidth }
		: type === ENVIRONMENTAL_STATUS_WIDGET_TYPE
			? {
				width: getSpannedWidgetSize(defaultWidth, 2),
				height: getSpannedWidgetSize(defaultHeight, 2),
			}
			: { width: defaultWidth, height: defaultHeight };

const isHiddenWidgetEditorPropKey = (key) =>
	HIDDEN_WIDGET_EDITOR_PROP_KEYS.has(key) || String(key || '').endsWith('Light');

const getElementViewportHeight = (element) => {
	if (!element) return 1;

	return Math.max(
		1,
		Math.round(element.getBoundingClientRect()?.height || 0),
		element.clientHeight || 0,
		element.offsetHeight || 0,
	);
};

const getCanvasBottomBufferPx = ({ viewportHeight, defaultHeight, rowUnit }) =>
	Math.max(
		Math.max(1, rowUnit),
		Math.min(defaultHeight, Math.round(viewportHeight * 0.15)),
	);

const normalizeRect = (rect) => {
	const left = Math.min(rect.left, rect.left + rect.width);
	const top = Math.min(rect.top, rect.top + rect.height);
	const width = Math.abs(rect.width);
	const height = Math.abs(rect.height);
	return { left, top, width, height };
};

const rectsIntersect = (a, b) => (
	a.left < b.left + b.width &&
	a.left + a.width > b.left &&
	a.top < b.top + b.height &&
	a.top + a.height > b.top
);

const sameRect = (a, b) => {
	if (!a && !b) return true;
	if (!a || !b) return false;
	return (
		Math.abs((Number(a.left) || 0) - (Number(b.left) || 0)) < 0.5 &&
		Math.abs((Number(a.top) || 0) - (Number(b.top) || 0)) < 0.5 &&
		Math.abs((Number(a.width) || 0) - (Number(b.width) || 0)) < 0.5 &&
		Math.abs((Number(a.height) || 0) - (Number(b.height) || 0)) < 0.5
	);
};

const sameIdList = (a = [], b = []) => {
	if (a.length !== b.length) return false;
	return a.every((id, index) => String(id) === String(b[index]));
};

const sameOffset = (a, b) => {
	if (!a && !b) return true;
	if (!a || !b) return false;
	return (
		Math.abs((Number(a.x) || 0) - (Number(b.x) || 0)) < 0.5 &&
		Math.abs((Number(a.y) || 0) - (Number(b.y) || 0)) < 0.5
	);
};

const getRectBounds = (rects) => {
	if (!Array.isArray(rects) || rects.length === 0) return null;
	const left = Math.min(...rects.map((rect) => rect.left));
	const top = Math.min(...rects.map((rect) => rect.top));
	const right = Math.max(...rects.map((rect) => rect.left + rect.width));
	const bottom = Math.max(...rects.map((rect) => rect.top + rect.height));
	return { left, top, width: right - left, height: bottom - top };
};

const setElementPixelStyle = (element, property, value, priority = '') => {
	if (!element || !Number.isFinite(value)) return;
	element.style.setProperty(property, `${Math.max(1, Math.round(value))}px`, priority);
};

const sameMeasuredSize = (prev, next) => {
	if (!prev && !next) return true;
	if (!prev || !next) return false;
	return (
		Math.abs((Number(prev.width) || 0) - (Number(next.width) || 0)) < 1 &&
		Math.abs((Number(prev.height) || 0) - (Number(next.height) || 0)) < 1
	);
};

const CreateDatastream = ({ onSelect }) => {
	const { lang } = useContext(LanguageContext);
	const t = webDashboardTranslations[lang]?.createDatastream || {};
	return (
		<div className='form-item'>
			<p>{t.noDatastreams || 'No datastreams found.'}</p>
			<div style={{ position: 'relative' }}>
				<button className="add-btn"
					onClick={() => {
						const menu = document.querySelector('.categories');
						const display = document.defaultView.getComputedStyle(menu).display;
						menu.style.display = display === 'block' ? 'none' : 'block';
					}}>
					<span>+</span>
					<span>{t.newDatastreams || 'New Datastream'}</span>
				</button>
				<ul className='details-actions-menu categories' style={{ zIndex: 3 }}>
					{pinCategories.map((cat, index) => (
						<li key={index} onClick={() => {
							const menu = document.querySelector('.categories');
							menu.style.display = 'none';
							onSelect(cat);
						}}
							className='details-actions-menu-item categories-item'>{cat}</li>
					))}
				</ul>
			</div>
		</div>
	);
};

const WebDashboard = ({
	dashboard,
	setDashboard,
	datastreams,
	setDatastreams,
	mode,
	maxWidth,
	maxHeight,
	did,
	tid,
	devices,
	externalFixedWidthControl = false,
	onFixedWidthModeChange,
	onFixedWidthToggleHandlerReady,
	externalMultiSelectControl = false,
	onMultiSelectToggleHandlerReady,
	onMultiSelectStateChange,
}) => {

	const { lang } = useContext(LanguageContext);
	const t = webDashboardTranslations[lang]?.webDashboard || {};
	const directionLabels = t.directionOptions || {};
	const [deviceDatastream, setDeviceDatastream] = useState([]);
	const [divs, setDivs] = useState([]);
	const [addDatastream, setAddDatastream] = useState({
			edit: false,
			pinCategory: ''
		}),
		[showAlert, setShowAlert] = useState(false),
		[alertMessage, setAlertMessage] = useState(''),
		[alertType, setAlertType] = useState('error'),
		[alertPosition, setAlertPosition] = useState('right'),
		[isFixedWidthMode, setIsFixedWidthMode] = useState(dashboard?.fixedWidth ?? true),
		[fixedWidthGuide, setFixedWidthGuide] = useState(null),
		[lastWidgetsBoxWidth, setLastWidgetsBoxWidth] = useState(0),
		[fixedViewWidth, setFixedViewWidth] = useState(0),
		[viewPercentageCanvas, setViewPercentageCanvas] = useState({ width: 0, height: 0 }),
		[layoutMeasureVersion, setLayoutMeasureVersion] = useState(0);
	const [removeBackgroundDialog, setRemoveBackgroundDialog] = useState({
		open: false,
		backgroundId: null,
		affectedWidgetIds: [],
		affectedCount: 0,
	});
	const [multiSelectEnabled, setMultiSelectEnabled] = useState(false);
	const [selectedWidgetIds, setSelectedWidgetIds] = useState([]);
	const [selectionRect, setSelectionRect] = useState(null);
	const [selectedGroupDragOffset, setSelectedGroupDragOffset] = useState(null);
	const [widgetSearchQuery, setWidgetSearchQuery] = useState('');

	const [templateEvaluationData, setTemplateEvaluationData] = useState({
		evaluation_names: [],
		datastream_ranges: []
	});

	const handleTerminalCleared = useCallback((payload) => {
		if (!payload || !Array.isArray(dashboard?.widgets) || typeof setDashboard !== 'function') {
			return;
		}

		let hasUpdates = false;
		const payloadWidgetId = String(payload.widgetId || '');
		const payloadDeviceId = payload.deviceId == null || payload.deviceId === ''
			? null
			: Number(payload.deviceId);
		const payloadDataId = payload.dataId == null || payload.dataId === ''
			? null
			: Number(payload.dataId);

		const updatedWidgets = dashboard.widgets.map((widget) => {
			if (widget?.type !== 'terminal') {
				return widget;
			}

			const widgetId = String(widget.id || '');
			const widgetDeviceId = widget.device_id == null || widget.device_id === ''
				? null
				: Number(widget.device_id);
			const widgetDataId = widget.data_id == null || widget.data_id === ''
				? null
				: Number(widget.data_id);

			const matchesWidgetId = widgetId && payloadWidgetId && widgetId === payloadWidgetId;
			const matchesDeviceAndData =
				Number.isFinite(widgetDeviceId) &&
				Number.isFinite(payloadDeviceId) &&
				Number.isFinite(widgetDataId) &&
				Number.isFinite(payloadDataId) &&
				widgetDeviceId === payloadDeviceId &&
				widgetDataId === payloadDataId;
			const matchesDataOnly =
				!Number.isFinite(widgetDeviceId) &&
				!Number.isFinite(payloadDeviceId) &&
				Number.isFinite(widgetDataId) &&
				Number.isFinite(payloadDataId) &&
				widgetDataId === payloadDataId;

			if (!matchesWidgetId && !matchesDeviceAndData && !matchesDataOnly) {
				return widget;
			}

			const preservedLastLiveValue =
				widget?.extra_props?.__lastLiveValue == null
					? null
					: String(widget.extra_props.__lastLiveValue);

			hasUpdates = true;
			return {
				...widget,
				extra_props: {
					...widget.extra_props,
					value: '',
					__lastLiveValue: preservedLastLiveValue,
				},
			};
		});

		if (!hasUpdates) {
			return;
		}

		setDashboard({
			...dashboard,
			widgets: updatedWidgets,
		});
	}, [dashboard, setDashboard]);

	const alignments = t.alignments ?? [],
		aggregations = t.aggregations ?? [],
		chartTypes = t.chartTypes ?? [],
		periodOptions = (() => {
			const allowedValues = new Set(['day', 'week', 'month']);
			const defaults = [
				{ label: 'Daily', value: 'day' },
				{ label: 'Weekly', value: 'week' },
				{ label: 'Monthly', value: 'month' },
			];
			const translated = Array.isArray(t.periodOptions) ? t.periodOptions : [];
			const merged = new Map(defaults.map((option) => [option.value, option]));

			translated.forEach((option) => {
				const normalizedValue = String(option?.value || '').trim().toLowerCase();
				if (!allowedValues.has(normalizedValue)) return;
				merged.set(normalizedValue, {
					label: option?.label || merged.get(normalizedValue)?.label || normalizedValue,
					value: normalizedValue,
				});
			});

			return Array.from(merged.values()).filter((option) => allowedValues.has(option.value));
		})(),
		periodStats = t.periodStats ?? [
			{ label: 'Min', value: 'min' },
			{ label: 'Max', value: 'max' },
		],
		[edit, setEdit] = useState({
			open: false,
			type: '',
			title: '',
			transparentBackground: false,
			deviceId: 0,
			dataId: 0,
			dataPin: '',
			id: '',
			width: 0,
			height: 0,
			top: 0,
			left: 0,
			props: {},
			showCurves: []
		}),
		[widgetLiveProps, setWidgetLiveProps] = useState({}),
		{ permissions } = useAuth(),
		defaultDivWidth = 24, defaultDivHeight = 120,
		dims = useWindowDimensions(),
		defaultWidth = 248, defaultHeight = 120,
		defaultChartProps = {
			chart_type: "line",
			showUnit: true,
			xData: ["May", "Jun", "Jul", "Aug"],
			curves: [{
				yData: [3, 4, 5, 6],
				customize: false,
				min: 0,
				max: 0,
				yLabel: "Label",
				pin: "Pin",
				aggregation: "mean",
				bgColor: "#eb1518",
				hbgColor: "#fc2629",
				bdColor: "#030ae5"
			}]
		},
		defaultPeriodColumnProps = {
			period: 'day',
			stat: 'max',
			showUnit: true,
			color: '#3b82f6',
			changeColorBasedOnValue: false,
			colorGrading: false,
			colors: [],
			xData: [],
			yData: [],
		},
		previewPeriodColumnProps = {
			period: 'day',
			stat: 'max',
			showUnit: true,
			color: '#3b82f6',
			changeColorBasedOnValue: false,
			colorGrading: false,
			colors: [],
			xData: [
				'2025-01-01T00:00:00.000Z',
				'2025-01-02T00:00:00.000Z',
				'2025-01-03T00:00:00.000Z',
				'2025-01-04T00:00:00.000Z',
			],
			yData: [18, 24, 16, 27],
		},
		defaultGaugeProps = {
			value: 50,
			factor: 1,
			precision: 1,
			showUnit: true,
			color: "#FF928A",
			changeColorBasedOnValue: false,
			colorGrading: false,
			colors: []
		},
		defaultLiquidProps = {
			min: 0,
			max: 100,
			value: 50,
			showUnit: true,
			unit: '%',
			color: '#74C7FF',
			changeColorBasedOnValue: false,
			colorGrading: false,
			colors: [],
			cardBackgroundColor: 'var(--header-background)',
			titleColor: null,
			valueColor: null,
			unitColor: null,
			glowColor: 'rgba(116, 199, 255, 0.52)',
		},
		defaultWaterTankProps = {
			min: 0,
			max: 100,
			value: 50,
			showUnit: true,
			unit: '%',
			color: '#72B8F4',
			changeColorBasedOnValue: false,
			colorGrading: false,
			colors: [],
			cardBackgroundColor: 'var(--header-background)',
			titleColor: null,
			valueColor: null,
			unitColor: null,
			glowColor: 'rgba(114, 184, 244, 0.46)',
			linkToNearby: true,
		},
		defaultRadialGaugeProps = {
			value: 50,
			factor: 1,
			precision: 1,
			showUnit: true,
			color: "#FF928A",
			changeColorBasedOnValue: false,
			colorGrading: false,
			colors: []
		},
		defaultLabelProps = {
			value: 50,
			factor: 1,
			precision: 1,
			alignment: "left",
			showUnit: true,
			showValueLine: true,
			color: "#ff00ff",
			changeColorBasedOnValue: false,
			colorGrading: false,
			colors: [],
			labelShadowEnabled: false,
			labelShadowBlur: 28,
			labelShadowStrength: 2,
			labelShadowUseValueColor: true,
			labelShadowColor: "#00f0f0",
			labelShadowChangeColorBasedOnValue: false,
			labelShadowColorGrading: false,
			labelShadowColors: []
		},
		defaultLEDProps = { color: "#00f0f0", value: 50 },
		defaultTextTitleProps = {
			title: t.widgetTitles?.textTitle || 'Title',
			titleSize: 20,
			color: "#00f0f0",
			alignment: 'left',
			fontFamily: '',
			fontWeight: '',
			fontStyle: 'normal',
			textDecoration: 'none'
		},
		defaultTimeRangeProps = {
			streams: buildDefaultTimeRangeStreams(),
			showLeadingZeroHours: true,
			showLeadingZeroMinutes: true,
			borderRadius: 20,
			titleSize: 20,
			valueSize: 64
		},
		defaultEnvironmentalStatusProps = {
			metrics: buildDefaultEnvironmentalStatusMetrics(),
			borderRadius: 20,
			titleSize: 18,
			datastreamTitleSize: 14,
			valueSize: 64,
		},

		defaultNumberProps = {
			min: 0,
			max: 100,
			step: 1,
			value: 50,
			showUnit: false,
			loopValues: false,
			contentAlignment: 'auto',
			color: "#C52184",
			buttonBgColor: null,
			buttonHoverBgColor: null,
			valueColor: null,
			valueHoverColor: null,
			changeColorBasedOnValue: false,
			colorGrading: false,
			colors: [],
		},
		defaultSliderProps = {
			step: 1,
			value: 50,
			color: "#C52184",
			buttonBgColor: null,
			valueColor: null,
			changeColorBasedOnValue: false,
			colorGrading: false,
			colors: [],
			direction: 'ltr',
			contentAlignment: 'auto',
		},
		defaultSwitchProps = {
			color: "#C52184",
			inactiveColor: "#D1D1D6",
			onLabel: "On",
			offLabel: "Off",
			value: 50,
			showLabel: false,
			alignment: 'left'
		},
		defaultPushButtonProps = {
			color: "#4FC4D3",
			inactiveColor: "rgba(79, 196, 211, 0.48)",
			textColor: "#FFFFFF",
			inactiveTextColor: "#8CE7EF",
			onLabel: "on",
			offLabel: "off",
			value: 0,
			showLabel: true,
			alignment: 'start',
			interactionMode: 'push'
		},
		defaultTerminalProps = {
			hint: "Type Here...",
			newLine: false,
			autoScroll: false,
			value: "< Enabled",
			bgColor: "rgb(0, 0, 0)",
			fgColor: "rgb(255, 255, 255)"
		},
		defaultKeypadProps = {
			buttonColor: "rgb(94,94,99)",
			textColor: "rgb(255, 255, 255)",
			inputTextColor: "rgb(0, 0, 0)",
			inputBackgroundColor: "rgb(255, 255, 255)",

		},
		defaultMenuProps = {
			alignment: "left",
			options: [{
				label: "Option 0",
				value: 0,
			}, {
				label: "Option 1",
				value: 1,
			}]
		},
		defaultSegmentSwitchProps = {
			activeBackgroundColor: 'var(--accent-color)',
			activeTextColor: 'var(--switch-thumb-color, #ffffff)',
			inactiveTextColor: 'var(--text-color, #ffffff)',
			inactiveBackgroundColor: 'var(--switch-track-unchecked, rgba(29, 41, 61, 0.5))',
			fontSize: 16,
			options: [{
				label: "Option 0",
				value: 0,
			}, {
				label: "Option 1",
				value: 1,
			}]
		},
		defaultImageButtonProps = {
			images: [{
				imageUrl: null,
				file: null,
				value: 0,
			}, {
				imageUrl: null,
				file: null,
				value: 1,
			}]
		},
		defaultBackgroundImageProps = {
			imageUrl: null,
			file: null,
		},
		defaultTextProps = { hint: "Type Here...", value: 50 },
		defaultTableProps = {
			xData: defaultChartProps.xData,
			curves: defaultChartProps.curves,
			headerBgColor: 'var(--header-background)',
			headerTextColor: 'var(--header-color)',
			bodyBgColor: 'var(--cell-background)',
			bodyTextColor: 'var(--cell-color)',
		},
		defaultFanProps = {
			datastreams: [],
			maxSpeed: 10,
			showLabel: true,
			alignment: 'center'
		},
		defaultPipeProps = {
			color: "#76B6EA",
			pipeColor: "rgba(93, 133, 160, 0.78)",
			pipeBackgroundColor: "rgba(18, 28, 40, 0.55)",
			onLabel: "Water Flowing",
			offLabel: "No Flow",
			value: 0,
			showLabel: true,
			direction: 'horizontal',
			flowDirection: 'left-to-right',

			frameBg: "rgba(23, 37, 64, 0.96)",
			frameBorderColor: "rgba(24, 99, 129, 0.76)",
			frameBorderWidth: 2,
			frameRadius: 18,
			framePadding: 0,

			pipeHeight: 70,
			pipeWidthPercent: 100,
			pipeBorderColor: "rgba(156, 194, 216, 0.42)",
			pipeBorderWidth: 10,
			pipeRadius: 18,
			pipeGlassColor: "rgba(142, 190, 214, 0.44)",

			clampCount: 4,
			clampColor: "rgba(118, 152, 153, 0.92)",
			clampWidth: 24,
			clampRadius: 16,

			waterOpacity: 0.85,
			waveSpeed: 2.75,

			titleFontSize: 34,
			statusFontSize: 30,
			linkToNearby: true,
		},
		defaultElbowProps = {
			color: "#76B6EA",
			pipeColor: "rgba(93, 133, 160, 0.78)",
			pipeBackgroundColor: "rgba(18, 28, 40, 0.55)",
			pipeGlassColor: "rgba(142, 190, 214, 0.44)",
			pipeBorderColor: "rgba(156, 194, 216, 0.42)",
			clampColor: "rgba(118, 152, 153, 0.92)",
			pipeHeight: 70,
			onLabel: "Water Flowing",
			offLabel: "No Flow",
			value: 0,
			showLabel: true,
			direction: 'top-right',
			linkToNearby: true,
		},
		defaultWaterPumpProps = {
			color: "#76B6EA",
			pipeColor: "rgba(93, 133, 160, 0.78)",
			pipeBackgroundColor: "rgba(18, 28, 40, 0.55)",
			pipeGlassColor: "rgba(142, 190, 214, 0.44)",
			pipeBorderColor: "rgba(156, 194, 216, 0.42)",
			clampColor: "rgba(118, 152, 153, 0.92)",
			pipeHeight: 70,
			onLabel: "Water Flowing",
			offLabel: "No Flow",
			value: 0,
			showLabel: false,
			direction: 'left-to-right',
			linkToNearby: true,
		},
		defaultBoxWidgetProps = {
			backgroundType: 'gradient', // 'solid' | 'gradient'
			backgroundColor: 'rgba(18, 28, 40, 0.9)',
			gradientAngle: 135,
			gradientColors: ['rgba(56, 74, 94, 0.95)', 'rgba(22, 31, 45, 0.95)'],
			opacity: 1,
			borderRadius: 16,
			borderWidth: 1,
			borderColor: 'rgba(255, 255, 255, 0.08)',
			shadowEnabled: true,
			shadowOffsetX: 0,
			shadowOffsetY: 10,
			shadowBlur: 30,
			shadowSpread: 0,
			shadowColor: 'rgba(0, 0, 0, 0.35)',
			backdropBlur: 0,
		},
		rectanglesOverlap = (first, second) => (
			first.left < second.right &&
			first.right > second.left &&
			first.top < second.bottom &&
			first.bottom > second.top
		),
		getWidgetsOverlappingBackground = (backgroundWidget, widgetsToCheck = []) => {
			if (!backgroundWidget || !Array.isArray(widgetsToCheck)) return [];

			const metrics = getEditableMetrics();
			const backgroundRectPx = getWidgetRectPx(backgroundWidget, metrics);
			const bgLeft = backgroundRectPx.left;
			const bgTop = backgroundRectPx.top;
			const bgWidth = backgroundRectPx.width;
			const bgHeight = backgroundRectPx.height;
			const bgRect = {
				left: bgLeft,
				top: bgTop,
				right: bgLeft + bgWidth,
				bottom: bgTop + bgHeight,
			};

			return widgetsToCheck.filter((widget) => {
				if (!widget || widget.id === backgroundWidget.id) return false;
				if (widget.type === 'background-image' || widget.type === 'box-widget') return false;

				const widgetRectPx = getWidgetRectPx(widget, metrics);
				const widgetLeft = widgetRectPx.left;
				const widgetTop = widgetRectPx.top;
				const widgetWidth = widgetRectPx.width;
				const widgetHeight = widgetRectPx.height;
				const widgetRect = {
					left: widgetLeft,
					top: widgetTop,
					right: widgetLeft + widgetWidth,
					bottom: widgetTop + widgetHeight,
				};

				return rectanglesOverlap(bgRect, widgetRect);
			});
		},
		resetRemoveBackgroundDialog = () => {
			setRemoveBackgroundDialog({
				open: false,
				backgroundId: null,
				affectedWidgetIds: [],
				affectedCount: 0,
			});
		},
		continueRemoveBackground = () => {
			const bgId = removeBackgroundDialog?.backgroundId;
			if (!bgId) {
				resetRemoveBackgroundDialog();
				return;
			}

			const currentWidgets = dashboard?.widgets || [];
			const widgetsWithoutBackground = currentWidgets.filter((widget) => widget.id !== bgId);
			const affectedSet = new Set(removeBackgroundDialog?.affectedWidgetIds || []);
			const affectedWidgets = widgetsWithoutBackground.filter((widget) => affectedSet.has(widget.id));
			const stationaryWidgets = widgetsWithoutBackground.filter((widget) => !affectedSet.has(widget.id));
			const metrics = getEditableMetrics();

			const sortedAffectedWidgets = [...affectedWidgets].sort((a, b) => {
				const rectA = getWidgetRectPx(a, metrics);
				const rectB = getWidgetRectPx(b, metrics);
				const areaA = rectA.width * rectA.height;
				const areaB = rectB.width * rectB.height;
				return areaB - areaA;
			});

			const placedWidgets = [...stationaryWidgets];
			const repositionedWidgetsById = new Map();

			for (const widget of sortedAffectedWidgets) {
				const widgetRectPx = getWidgetRectPx(widget, metrics);
				const widgetWidth = widgetRectPx.width;
				const widgetHeight = widgetRectPx.height;
				const startLeft = widgetRectPx.left;
				const startTop = widgetRectPx.top;

				const { found, left, top } = findNearestEmptySpotForWidgets(
					startLeft,
					startTop,
					widgetWidth,
					widgetHeight,
					placedWidgets
				);

				if (!found) {
					setAlertMessage(
						t.removeBackgroundNoSpace ||
						'No free area is available to reposition widgets. Please empty some area and try again.'
					);
					setAlertType('warning');
					setShowAlert(true);
					resetRemoveBackgroundDialog();
					return;
				}

				const movedWidget = storeWidgetPosition(widget, {
					left,
					top,
					width: widgetWidth,
					height: widgetHeight,
				}, metrics);
				repositionedWidgetsById.set(widget.id, movedWidget);
				placedWidgets.push(movedWidget);
			}

			const nextWidgets = widgetsWithoutBackground.map((widget) => (
				repositionedWidgetsById.get(widget.id) || widget
			));

			setDashboard({
				...dashboard,
				widgets: sortWidgets(nextWidgets),
			});

			resetRemoveBackgroundDialog();
		},
		remove = (id) => {
			const currentWidgets = dashboard?.widgets || [];
			const widgetToRemove = currentWidgets.find((widget) => widget.id === id);
			if (!widgetToRemove) return;

			if (widgetToRemove.type !== 'background-image') {
				setDashboard({
					...dashboard,
					widgets: currentWidgets.filter((widget) => widget.id !== id),
				});
				return;
			}

			const overlappingWidgets = getWidgetsOverlappingBackground(widgetToRemove, currentWidgets);
			if (overlappingWidgets.length === 0) {
				setDashboard({
					...dashboard,
					widgets: currentWidgets.filter((widget) => widget.id !== id),
				});
				return;
			}

			setRemoveBackgroundDialog({
				open: true,
				backgroundId: id,
				affectedWidgetIds: overlappingWidgets.map((widget) => widget.id),
				affectedCount: overlappingWidgets.length,
			});
		},
		checkOverlap = (x, y, w, h, widgetsToCheck, currentWidgetId = null, subjectWidgetOrType = null) => {
			if (!Array.isArray(widgetsToCheck)) {
				return false;
			}

			const metrics = getEditableMetrics();
			const subjectWidget = subjectWidgetOrType
				|| (currentWidgetId
					? widgetsToCheck.find((widget) => widget?.id === currentWidgetId)
					: null);
			const newLeft = toPxNumber(x, 0);
			const newTop = toPxNumber(y, 0);
			const newWidth = toPxNumber(w, 0);
			const newHeight = toPxNumber(h, 0);
			const newRight = newLeft + newWidth;
			const newBottom = newTop + newHeight;

			for (const elem of widgetsToCheck) {
				if (currentWidgetId && elem.id === currentWidgetId) {
					continue;
				}
				if (elem.type === 'background-image' || elem.type === 'box-widget') {
					continue;
				}
				if (canWaterWidgetsShareSpace(subjectWidget, elem)) {
					continue;
				}

				const existingRectPx = getWidgetRectPx(elem, metrics);
				const existingLeft = existingRectPx.left;
				const existingTop = existingRectPx.top;
				const existingWidth = existingRectPx.width;
				const existingHeight = existingRectPx.height;
				const existingRight = existingLeft + existingWidth;
				const existingBottom = existingTop + existingHeight;

				if (
					newLeft < existingRight &&
					newRight > existingLeft &&
					newTop < existingBottom &&
					newBottom > existingTop
				) {
					return true;
				}
			}
			return false;
		},
		checkIsOnBackgroundImage = (x, y, w, h, widgetsToCheck, currentWidgetId = null) => {
			if (!Array.isArray(widgetsToCheck)) {
				return false;
			}

			const metrics = getEditableMetrics();
			const newLeft = toPxNumber(x, 0);
			const newTop = toPxNumber(y, 0);
			const newWidth = toPxNumber(w, 0);
			const newHeight = toPxNumber(h, 0);
			const newRight = newLeft + newWidth;
			const newBottom = newTop + newHeight;

			for (const elem of widgetsToCheck) {
				if (currentWidgetId && elem.id === currentWidgetId) {
					continue;
				}
				if (elem.type !== 'background-image') {
					continue;
				}

				const existingRectPx = getWidgetRectPx(elem, metrics);
				const existingLeft = existingRectPx.left;
				const existingTop = existingRectPx.top;
				const existingWidth = existingRectPx.width;
				const existingHeight = existingRectPx.height;
				const existingRight = existingLeft + existingWidth;
				const existingBottom = existingTop + existingHeight;

				if (
					newLeft < existingRight &&
					newRight > existingLeft &&
					newTop < existingBottom &&
					newBottom > existingTop
				) {
					return true;
				}
			}
			return false;
		},
		getContainingBackgroundImage = (x, y, widgetsToCheck, currentWidgetId = null) => {
			if (!Array.isArray(widgetsToCheck)) {
				return null;
			}

			const metrics = getEditableMetrics();
			const pointX = toPxNumber(x, 0);
			const pointY = toPxNumber(y, 0);

			for (const elem of widgetsToCheck) {
				if (currentWidgetId && elem.id === currentWidgetId) {
					continue;
				}
				if (elem.type !== 'background-image') {
					continue;
				}

				const backgroundRectPx = getWidgetRectPx(elem, metrics);
				const bgLeft = backgroundRectPx.left;
				const bgTop = backgroundRectPx.top;
				const bgWidth = backgroundRectPx.width;
				const bgHeight = backgroundRectPx.height;
				const bgRight = bgLeft + bgWidth;
				const bgBottom = bgTop + bgHeight;

				if (
					pointX >= bgLeft &&
					pointX <= bgRight &&
					pointY >= bgTop &&
					pointY <= bgBottom
				) {
					return elem;
				}
			}
			return null;
		},
		getOverlappingBackgroundImage = (x, y, w, h, widgetsToCheck, currentWidgetId = null) => {
			if (!Array.isArray(widgetsToCheck)) {
				return null;
			}

			const metrics = getEditableMetrics();
			const newLeft = toPxNumber(x, 0);
			const newTop = toPxNumber(y, 0);
			const newWidth = toPxNumber(w, 0);
			const newHeight = toPxNumber(h, 0);
			const newRight = newLeft + newWidth;
			const newBottom = newTop + newHeight;

			let bestMatch = null;
			let bestOverlapArea = 0;

			for (const elem of widgetsToCheck) {
				if (currentWidgetId && elem.id === currentWidgetId) {
					continue;
				}
				if (elem.type !== 'background-image') {
					continue;
				}

				const backgroundRectPx = getWidgetRectPx(elem, metrics);
				const bgLeft = backgroundRectPx.left;
				const bgTop = backgroundRectPx.top;
				const bgRight = bgLeft + backgroundRectPx.width;
				const bgBottom = bgTop + backgroundRectPx.height;

				const overlapWidth = Math.max(0, Math.min(newRight, bgRight) - Math.max(newLeft, bgLeft));
				const overlapHeight = Math.max(0, Math.min(newBottom, bgBottom) - Math.max(newTop, bgTop));
				const overlapArea = overlapWidth * overlapHeight;

				if (overlapArea > bestOverlapArea) {
					bestOverlapArea = overlapArea;
					bestMatch = elem;
				}
			}

			return bestMatch;
		},
		constrainRectToBackgroundImage = (x, y, w, h, widgetsToCheck, currentWidgetId = null) => {
			const rawLeft = toPxNumber(x, 0);
			const rawTop = toPxNumber(y, 0);
			const rawWidth = Math.max(defaultDivWidth, toPxNumber(w, defaultDivWidth));
			const rawHeight = Math.max(defaultDivHeight, toPxNumber(h, defaultDivHeight));
			const backgroundWidget = getContainingBackgroundImage(
				rawLeft,
				rawTop,
				widgetsToCheck,
				currentWidgetId,
			) || getOverlappingBackgroundImage(
				rawLeft,
				rawTop,
				rawWidth,
				rawHeight,
				widgetsToCheck,
				currentWidgetId,
			);

			if (!backgroundWidget) {
				return {
					left: rawLeft,
					top: rawTop,
					width: rawWidth,
					height: rawHeight,
					constrained: false,
				};
			}

			const metrics = getEditableMetrics();
			const backgroundRect = getWidgetRectPx(backgroundWidget, metrics);
			const maxWidth = Math.max(1, backgroundRect.width);
			const maxHeight = Math.max(1, backgroundRect.height);
			const constrainedWidth = Math.min(rawWidth, maxWidth);
			const constrainedHeight = Math.min(rawHeight, maxHeight);
			const constrainedLeft = Math.min(
				Math.max(rawLeft, backgroundRect.left),
				backgroundRect.left + backgroundRect.width - constrainedWidth,
			);
			const constrainedTop = Math.min(
				Math.max(rawTop, backgroundRect.top),
				backgroundRect.top + backgroundRect.height - constrainedHeight,
			);

			return {
				left: constrainedLeft,
				top: constrainedTop,
				width: constrainedWidth,
				height: constrainedHeight,
				constrained: true,
			};
		};

	const colorThemeMode = normalizeThemeMode(edit?.props?.themeMode);
	const deviceSelectOptions = useMemo(
		() => (Array.isArray(devices) ? devices : []).map((device) => buildDeviceDropdownOption(device)),
		[devices]
	);
	const themeColorValue = (obj, key, fallback) =>
		getThemeColorValue(obj, key, colorThemeMode, fallback);
	const themeColorKey = (key) => getThemeColorKey(key, colorThemeMode);
	const getNextDatastreamId = (list) => {
		return getNextTemporaryDatastreamId(list);
	};
	const propsColorKeys = Object.keys(edit?.props || {}).some((key) =>
		key.toLowerCase().includes('color')
	);
	const curvesColorKeys = Array.isArray(edit?.props?.curves)
		&& edit.props.curves.some((curve) =>
			Object.keys(curve || {}).some((key) => key.toLowerCase().includes('color'))
		);
	const stopsColorKeys = Array.isArray(edit?.props?.colors)
		&& edit.props.colors.some((stop) =>
			Object.keys(stop || {}).some((key) => key.toLowerCase().includes('color'))
		);
	const hasColorSettings = propsColorKeys || curvesColorKeys || stopsColorKeys;
	const updateEditProp = (key, value) => {
		setEdit(prev => ({
			...prev,
			props: { ...(prev.props || {}), [key]: value }
		}));
	};
	const removeEditProp = (key) => {
		setEdit(prev => {
			const nextProps = { ...(prev.props || {}) };
			delete nextProps[key];
			return {
				...prev,
				props: nextProps
			};
		});
	};
	const handleFontSizeChange = (key, rawValue) => {
		if (rawValue === '') {
			updateEditProp(key, '');
			return;
		}

		const parsed = Number(rawValue);
		if (!Number.isFinite(parsed)) return;
		updateEditProp(key, Math.round(parsed));
	};
	const handleFontSizeBlur = (key) => {
		if (edit?.props?.[key] === '') {
			removeEditProp(key);
		}
	};
	const sanitizeWidgetFontSizeProps = (props = {}) => {
		const nextProps = { ...props };
		['labelFontSize', 'valueFontSize'].forEach((key) => {
			const value = nextProps[key];
			if (value === '' || value === null || value === undefined) {
				delete nextProps[key];
				return;
			}

			const parsed = Number(value);
			if (!Number.isFinite(parsed) || parsed <= 0) {
				delete nextProps[key];
				return;
			}

			nextProps[key] = Math.round(parsed);
		});
		return nextProps;
	};
	const fontSizeFields = [
		{ key: 'labelFontSize', label: t.settingsLabels?.labelFontSize || 'Label Font Size' },
		{ key: 'valueFontSize', label: t.settingsLabels?.valueFontSize || 'Value Font Size' },
	];
	const layoutMode = normalizeWebDashboardLayoutMode(dashboard?.layoutMode);
	const isPercentageLayout = layoutMode === WEB_DASHBOARD_LAYOUT_MODES.PERCENTAGE;

const toPxNumber = useCallback((v, fallback = 0) => {
	if (v === null || v === undefined) return fallback;
	if (typeof v === 'number' && Number.isFinite(v)) return v;
	const n = Number(String(v).replace('px', '').trim());
			return Number.isFinite(n) ? n : fallback;
		}, []),
		getPercentageViewportMetrics = useCallback(() => {
			if (mode === 'edit') {
				const mainContainer = document.getElementById('web-dashboard-main-container');
				const dashboardElement = document.getElementById('diverted');
				const widgetsBox = document.querySelector('.widgets-box');
				const containerGap = 8;

				const availableWidth = mainContainer
					? Math.max(
						1,
						mainContainer.offsetWidth - (widgetsBox?.offsetWidth || 0) - containerGap,
					)
					: Math.max(
						1,
						dashboardElement?.clientWidth || dashboardElement?.getBoundingClientRect?.().width || 0,
					);
				const availableHeight = mainContainer
					? getElementViewportHeight(mainContainer)
					: Math.max(
						1,
						dashboardElement?.clientHeight || dashboardElement?.getBoundingClientRect?.().height || 0,
					);

				return {
					width: availableWidth,
					height: availableHeight,
				};
			}

			const dashboardElement = document.getElementById('diverted-view');
			if (!dashboardElement) {
				return null;
			}

			const rect = dashboardElement.getBoundingClientRect();
			return {
				width: Math.max(1, dashboardElement.clientWidth || rect.width || 0),
				height: Math.max(1, dashboardElement.clientHeight || rect.height || 0),
			};
		}, [mode]),
		formatPercentValue = useCallback((value) => {
			const numericValue = Number(value);
			if (!Number.isFinite(numericValue)) return '0%';
			const clamped = Math.max(0, numericValue);
			const rounded = Number(clamped.toFixed(4));
			return `${rounded}%`;
		}, []),
		isStoredPercentageValue = useCallback((value) => {
			if (typeof value !== 'string') return false;
			const normalized = value.trim();
			if (!normalized.endsWith('%')) return false;
			const numericValue = Number(normalized.slice(0, -1));
			return Number.isFinite(numericValue);
		}, []),
		hasStoredPercentageGeometry = useCallback((widget) => (
			isStoredPercentageValue(widget?.left)
			&& isStoredPercentageValue(widget?.top)
			&& isStoredPercentageValue(widget?.width)
			&& isStoredPercentageValue(widget?.height)
		), [isStoredPercentageValue]),
		parseStoredDimension = useCallback((value, total, fallback = 0) => {
			if (value === null || value === undefined) return fallback;
			if (typeof value === 'string' && value.trim().endsWith('%')) {
				const numericValue = Number(value.replace('%', '').trim());
				if (!Number.isFinite(numericValue) || total <= 0) {
					return fallback;
				}
				return (numericValue / 100) * total;
			}
			return toPxNumber(value, fallback);
		}, [toPxNumber]),
		getPercentageReferenceMetrics = useCallback(() => (
			getPercentageViewportMetrics()
		), [getPercentageViewportMetrics]),
		getEditableMetrics = useCallback(() => {
			const editableElement = document.getElementById('editable');
			const divertedElement = document.getElementById('diverted');

			if (!editableElement || !divertedElement) {
				return null;
			}

			const editableRect = editableElement.getBoundingClientRect();
			const divertedStyle = document.defaultView.getComputedStyle(divertedElement);
			const gap = parseInt(divertedStyle.gap, 10) || 0;
			const padLeft = parseInt(divertedStyle.paddingLeft, 10) || 0;
			const padRight = parseInt(divertedStyle.paddingRight, 10) || 0;
			const padTop = parseInt(divertedStyle.paddingTop, 10) || 0;
			const padBottom = parseInt(divertedStyle.paddingBottom, 10) || 0;
			const divertedRect = divertedElement.getBoundingClientRect();
			const visibleWidth = Math.max(
				1,
				divertedElement.clientWidth || 0,
				Math.round(divertedRect.width || 0),
			);
			const visibleHeight = Math.max(
				1,
				divertedElement.clientHeight || 0,
				Math.round(divertedRect.height || 0),
			);
			const scrollLeft = Math.max(0, divertedElement.scrollLeft || 0);
			const scrollTop = Math.max(0, divertedElement.scrollTop || 0);
			const editableWidth = Math.max(
				editableElement.offsetWidth || 0,
				editableRect.width || 0,
				scrollLeft + visibleWidth,
			);
			const editableHeight = Math.max(
				editableElement.offsetHeight || 0,
				editableRect.height || 0,
				scrollTop + visibleHeight,
			);

			return {
				editableRect,
				divertedRect,
				width: editableWidth,
				height: editableHeight,
				percentageReference: isPercentageLayout ? getPercentageReferenceMetrics() : null,
				gap,
				padLeft,
				padRight,
				padTop,
				padBottom,
				scrollLeft,
				scrollTop,
				gridColumnSize: defaultDivWidth + gap,
				gridRowSize: defaultDivHeight + gap,
				usableWidth: Math.max(0, editableWidth - padLeft - padRight),
				usableHeight: Math.max(0, editableHeight - padTop - padBottom),
			};
		}, [defaultDivHeight, defaultDivWidth, getPercentageReferenceMetrics, isPercentageLayout]),
		getWidgetRectPx = useCallback((widget, metrics = getEditableMetrics()) => {
			const percentageReference = isPercentageLayout
				? (metrics?.percentageReference || getPercentageReferenceMetrics())
				: null;
			const totalWidth = isPercentageLayout
				? (percentageReference?.width || metrics?.width || 0)
				: (metrics?.width || 0);
			const totalHeight = isPercentageLayout
				? (percentageReference?.height || metrics?.height || 0)
				: (metrics?.height || 0);
			return {
				left: isPercentageLayout
					? parseStoredDimension(widget?.left, totalWidth, 0)
					: toPxNumber(widget?.left, 0),
				top: isPercentageLayout
					? parseStoredDimension(widget?.top, totalHeight, 0)
					: toPxNumber(widget?.top, 0),
				width: isPercentageLayout
					? parseStoredDimension(widget?.width, totalWidth, defaultWidth)
					: toPxNumber(widget?.width, defaultWidth),
				height: isPercentageLayout
					? parseStoredDimension(widget?.height, totalHeight, defaultHeight)
					: toPxNumber(widget?.height, defaultHeight),
			};
		}, [
			defaultHeight,
			defaultWidth,
			getEditableMetrics,
			getPercentageReferenceMetrics,
			isPercentageLayout,
			parseStoredDimension,
			toPxNumber,
		]),
		serializeRectForStorage = useCallback((rect, metrics = getEditableMetrics()) => {
			if (!isPercentageLayout || !metrics) {
				return {
					left: rect.left,
					top: rect.top,
					width: rect.width,
					height: rect.height,
				};
			}

			const percentageReference = metrics?.percentageReference || getPercentageReferenceMetrics();
			const safeWidth = (percentageReference?.width || 0) > 0
				? percentageReference.width
				: (metrics.width > 0 ? metrics.width : 1);
			const safeHeight = (percentageReference?.height || 0) > 0
				? percentageReference.height
				: (metrics.height > 0 ? metrics.height : 1);

			return {
				left: formatPercentValue((rect.left / safeWidth) * 100),
				top: formatPercentValue((rect.top / safeHeight) * 100),
				width: formatPercentValue((rect.width / safeWidth) * 100),
				height: formatPercentValue((rect.height / safeHeight) * 100),
			};
		}, [formatPercentValue, getEditableMetrics, getPercentageReferenceMetrics, isPercentageLayout]),
		storeWidgetPosition = useCallback((widget, rect, metrics = getEditableMetrics()) => ({
			...widget,
			...serializeRectForStorage(rect, metrics),
		}), [getEditableMetrics, serializeRectForStorage]),
		getWidgetsCanvasBoundsPx = useCallback((widgetsToMeasure = [], metrics = getEditableMetrics()) => {
			if (!Array.isArray(widgetsToMeasure) || widgetsToMeasure.length === 0) {
				return { maxRight: 0, maxBottom: 0 };
			}

			return widgetsToMeasure.reduce((acc, widget) => {
				const rect = getWidgetRectPx(widget, metrics);
				return {
					maxRight: Math.max(acc.maxRight, rect.left + rect.width),
					maxBottom: Math.max(acc.maxBottom, rect.top + rect.height),
				};
			}, { maxRight: 0, maxBottom: 0 });
		}, [getEditableMetrics, getWidgetRectPx]),
		getViewWidgetRectPx = useCallback((widget) => {
			const totalWidth = isPercentageLayout
				? Math.max(1, viewPercentageCanvas?.width || 0)
				: 0;
			const totalHeight = isPercentageLayout
				? Math.max(1, viewPercentageCanvas?.height || 0)
				: 0;

			return {
				left: isPercentageLayout
					? parseStoredDimension(widget?.left, totalWidth, 0)
					: toPxNumber(widget?.left, 0),
				top: isPercentageLayout
					? parseStoredDimension(widget?.top, totalHeight, 0)
					: toPxNumber(widget?.top, 0),
				width: isPercentageLayout
					? parseStoredDimension(widget?.width, totalWidth, defaultWidth)
					: toPxNumber(widget?.width, defaultWidth),
				height: isPercentageLayout
					? parseStoredDimension(widget?.height, totalHeight, defaultHeight)
					: toPxNumber(widget?.height, defaultHeight),
			};
		}, [
			defaultHeight,
			defaultWidth,
			isPercentageLayout,
			parseStoredDimension,
			toPxNumber,
			viewPercentageCanvas?.height,
			viewPercentageCanvas?.width,
		]),

		findNearestEmptySpotForWidgets = (
			startLeft,
			startTop,
			widgetWidth,
			widgetHeight,
			widgetsToCheck = []
		) => {
			const metrics = getEditableMetrics();
			if (!metrics) return { found: false, left: 0, top: 0 };

			const edWidth = metrics.width;
			const edHeight = metrics.height;
			const gap = metrics.gap;
			const padLeft = metrics.padLeft;
			const padRight = metrics.padRight;
			const padTop = metrics.padTop;
			const padBottom = metrics.padBottom;
			const gridColumnSize = defaultDivWidth + gap;
			const gridRowSize = defaultDivHeight + gap;
			const ww = toPxNumber(widgetWidth, 0);
			const wh = toPxNumber(widgetHeight, 0);
			const minX = padLeft;
			const maxX = Math.max(minX, edWidth - padRight - ww);
			const minY = padTop;
			const maxY = Math.max(minY, edHeight - padBottom - wh);
			const usableWidth = metrics.usableWidth;
			const usableHeight = metrics.usableHeight;

			const sx = toPxNumber(startLeft, 0);
			const sy = toPxNumber(startTop, 0);

			const maxCols = Math.max(0, Math.floor((usableWidth - ww) / gridColumnSize) + 1);
			const maxRows = Math.max(0, Math.floor((usableHeight - wh) / gridRowSize) + 1);

			let bestX = null;
			let bestY = null;
			let minDist = Infinity;

			for (let r = 0; r < maxRows; r++) {
				for (let c = 0; c < maxCols; c++) {
					const x = minX + (c * gridColumnSize);
					const y = minY + (r * gridRowSize);

					if (x < minX || y < minY) continue;
					if (x > maxX || y > maxY) continue;
					if (checkOverlap(x, y, ww, wh, widgetsToCheck)) continue;

					const d = (x - sx) * (x - sx) + (y - sy) * (y - sy);
					if (d < minDist) {
						minDist = d;
						bestX = x;
						bestY = y;
					}
				}
			}

			if (bestX === null) return { found: false, left: 0, top: 0 };
			return { found: true, left: bestX, top: bestY };
		},
		findNearestEmptySpot = (startLeft, startTop, widgetWidth, widgetHeight) => {
			return findNearestEmptySpotForWidgets(
				startLeft,
				startTop,
				widgetWidth,
				widgetHeight,
				dashboard?.widgets || []
			);
		},
		getNearestGridRect = (rect, metrics = getEditableMetrics()) => {
			const gap = metrics?.gap || 0;
			const gridColumnSize = Math.max(1, defaultDivWidth + gap);
			const gridRowSize = Math.max(1, defaultDivHeight + gap);
			const minLeft = metrics?.padLeft || 0;
			const minTop = metrics?.padTop || 0;
			const maxWidth = Math.max(defaultDivWidth, (metrics?.width || 0) - minLeft - (metrics?.padRight || 0));
			const maxHeight = Math.max(defaultDivHeight, (metrics?.height || 0) - minTop - (metrics?.padBottom || 0));
			const rawWidth = Math.max(1, toPxNumber(rect?.width, defaultWidth));
			const rawHeight = Math.max(1, toPxNumber(rect?.height, defaultHeight));
			const cols = Math.max(1, Math.round((rawWidth + gap) / gridColumnSize));
			const rows = Math.max(1, Math.round((rawHeight + gap) / gridRowSize));
			const width = Math.min(maxWidth, (cols * defaultDivWidth) + (Math.max(0, cols - 1) * gap));
			const height = Math.min(maxHeight, (rows * defaultDivHeight) + (Math.max(0, rows - 1) * gap));
			const maxLeft = Math.max(minLeft, (metrics?.width || 0) - (metrics?.padRight || 0) - width);
			const maxTop = Math.max(minTop, (metrics?.height || 0) - (metrics?.padBottom || 0) - height);
			const left = minLeft + (
				Math.round((toPxNumber(rect?.left, minLeft) - minLeft) / gridColumnSize) * gridColumnSize
			);
			const top = minTop + (
				Math.round((toPxNumber(rect?.top, minTop) - minTop) / gridRowSize) * gridRowSize
			);

			return {
				left: Math.max(minLeft, Math.min(left, maxLeft)),
				top: Math.max(minTop, Math.min(top, maxTop)),
				width,
				height,
			};
		},
		sortWidgets = (widgets) => {
			const backgroundWidgets = (widgets || []).filter(
				w => w.type === 'background-image' || w.type === 'box-widget'
			);

			const otherWidgets = (widgets || []).filter(
				w => w.type !== 'background-image' && w.type !== 'box-widget'
			);

			return [...backgroundWidgets, ...otherWidgets];
		},
		duplicate = (id) => {
			const elem = (dashboard.widgets || []).find(e => e.id === id);
			if (!elem) return;

			const metrics = getEditableMetrics();
			const gap = metrics?.gap || 0;
			const elemRectPx = getWidgetRectPx(elem, metrics);
			const minimumWidgetSize = getMinimumWidgetSizeForType(
				elem?.type,
				defaultDivWidth,
				defaultDivHeight,
			);
			const isFreeformLayoutWidget = isWidgetFreeMovementEnabled(elem);
			const rawW = Math.max(minimumWidgetSize.width, elemRectPx.width);
			const rawH = Math.max(minimumWidgetSize.height, elemRectPx.height);

			let widgetWidth = rawW;
			let widgetHeight = rawH;

			if (!isFreeformLayoutWidget) {
				const colUnit = defaultDivWidth + gap;
				const rowUnit = defaultDivHeight + gap;

				const cols = Math.max(1, Math.round((rawW + gap) / Math.max(1, colUnit)));
				const rows = Math.max(1, Math.round((rawH + gap) / Math.max(1, rowUnit)));

				widgetWidth = (cols * defaultDivWidth) + (Math.max(0, cols - 1) * gap);
				widgetHeight = (rows * defaultDivHeight) + (Math.max(0, rows - 1) * gap);
			}

			const startLeft = elemRectPx.left;
			const startTop = elemRectPx.top;

			let duplicationSpot = null;
			if (isFreeformLayoutWidget) {
				const currentWidgets = dashboard?.widgets || [];
				const usableWidth = metrics?.usableWidth || metrics?.width || 0;
				const usableHeight = metrics?.usableHeight || metrics?.height || 0;
				const minX = metrics?.padLeft || 0;
				const minY = metrics?.padTop || 0;
				const maxX = Math.max(minX, (metrics?.width || 0) - (metrics?.padRight || 0) - widgetWidth);
				const maxY = Math.max(minY, (metrics?.height || 0) - (metrics?.padBottom || 0) - widgetHeight);
				let bestX = null;
				let bestY = null;
				let minDistance = Infinity;
				const maxCols = Math.max(0, Math.floor((usableWidth - widgetWidth) / WATER_NETWORK_FREEFORM_SEARCH_STEP) + 1);
				const maxRows = Math.max(0, Math.floor((usableHeight - widgetHeight) / WATER_NETWORK_FREEFORM_SEARCH_STEP) + 1);

				for (let row = 0; row < maxRows; row += 1) {
					for (let col = 0; col < maxCols; col += 1) {
						const testLeft = minX + (col * WATER_NETWORK_FREEFORM_SEARCH_STEP);
						const testTop = minY + (row * WATER_NETWORK_FREEFORM_SEARCH_STEP);
						if (testLeft < minX || testTop < minY || testLeft > maxX || testTop > maxY) continue;
						if (checkOverlap(testLeft, testTop, widgetWidth, widgetHeight, currentWidgets, null, elem)) continue;

						const dx = testLeft - startLeft;
						const dy = testTop - startTop;
						const distance = (dx * dx) + (dy * dy);
						if (distance < minDistance) {
							minDistance = distance;
							bestX = testLeft;
							bestY = testTop;
						}
					}
				}

				if (bestX !== null && bestY !== null) {
					duplicationSpot = { found: true, left: bestX, top: bestY };
				}
			}

			const { found, left: newLeft, top: newTop } = duplicationSpot || findNearestEmptySpot(startLeft, startTop, widgetWidth, widgetHeight);

			if (found) {
				const duplicatedSource = cloneDashboardValue(elem);
				const duplicatedWidget = storeWidgetPosition(
					{
						...duplicatedSource,
						id: elem.type + Date.now(),
					},
					{
						left: newLeft,
						top: newTop,
						width: widgetWidth,
						height: widgetHeight,
					},
					metrics,
				);
				const updatedWidgets = [
					...(dashboard.widgets || []),
					duplicatedWidget,
				];

				setDashboard({
					...dashboard,
					widgets: sortWidgets(updatedWidgets)
				});
			} else {
				setAlertMessage(t.noSpaceForDuplication || 'No empty space available on the dashboard for duplication.');
				setAlertType('warning');
				setShowAlert(true);
			}
		},
		resizeWidget = (e, id, setDims) => {
			const widget = e.target.closest('.widget'),
				style = document.defaultView.getComputedStyle(widget),
				startWidth = parseFloat(style.width), startHeight = parseFloat(style.height),
				dashboardWidgets = dashboard?.widgets || [],
				widgetBeingResized = dashboardWidgets.find(wd => wd.id === id),
				minimumWidgetSize = getMinimumWidgetSizeForType(
					widgetBeingResized?.type,
					defaultDivWidth,
					defaultDivHeight,
				),
				moveListener = (event) => {
					const newWidth = Math.max(minimumWidgetSize.width, startWidth + event.clientX - e.clientX),
						newHeight = Math.max(minimumWidgetSize.height, startHeight + event.clientY - e.clientY);
					if (isPercentageLayout) {
						widget.style.width = `${newWidth}px`;
						widget.style.height = `${newHeight}px`;
					}
					setDims({ width: newWidth, height: newHeight });
				};

			document.documentElement.addEventListener('mousemove', moveListener);

			document.documentElement.addEventListener('mouseup', function upListener(ev) {
				const editableElement = document.getElementById('editable');
				if (!editableElement) {
					document.documentElement.removeEventListener('mousemove', moveListener);
					document.documentElement.removeEventListener('mouseup', upListener);
					return;
				}

				const editableRect = editableElement.getBoundingClientRect();
				const widgetRect = widget.getBoundingClientRect();

				const currentWidgetRelativeLeft = widgetRect.left - editableRect.left;
				const currentWidgetRelativeTop = widgetRect.top - editableRect.top;
				const widgetBeingResizedRect = getWidgetRectPx(widgetBeingResized, getEditableMetrics());
				const isResizingBackgroundImage = widgetBeingResized && (widgetBeingResized.type === 'background-image' || widgetBeingResized.type === 'box-widget');
				const isFreeformResize = isWidgetFreeMovementEnabled(widgetBeingResized);

				const divertedElement = document.getElementById('diverted');
				const styleOfDiverted = document.defaultView.getComputedStyle(divertedElement);
				const gap = parseInt(styleOfDiverted.gap) || 0;
				const gridColumnSize = defaultDivWidth + gap;
				const gridRowSize = defaultDivHeight + gap;

				const mouseRelativeX = ev.clientX - editableRect.left;
				const mouseRelativeY = ev.clientY - editableRect.top;

				let rawPotentialWidth = mouseRelativeX - currentWidgetRelativeLeft;
				let rawPotentialHeight = mouseRelativeY - currentWidgetRelativeTop;

				rawPotentialWidth = Math.max(minimumWidgetSize.width, rawPotentialWidth);
				rawPotentialHeight = Math.max(minimumWidgetSize.height, rawPotentialHeight);

				const isOnBackgroundImage = !isResizingBackgroundImage && checkIsOnBackgroundImage(
					currentWidgetRelativeLeft,
					currentWidgetRelativeTop,
					rawPotentialWidth,
					rawPotentialHeight,
					dashboardWidgets,
					id
				);
				const containingBackground = isOnBackgroundImage
					? (
						getContainingBackgroundImage(
						currentWidgetRelativeLeft,
						currentWidgetRelativeTop,
						dashboardWidgets,
						id
					) || getOverlappingBackgroundImage(
						currentWidgetRelativeLeft,
						currentWidgetRelativeTop,
						rawPotentialWidth,
						rawPotentialHeight,
						dashboardWidgets,
						id,
					))
					: null;

				if (containingBackground) {
					const containingBackgroundRect = getWidgetRectPx(containingBackground, getEditableMetrics());
					const backgroundLeft = containingBackgroundRect.left;
					const backgroundTop = containingBackgroundRect.top;
					const backgroundRight = backgroundLeft + containingBackgroundRect.width;
					const backgroundBottom = backgroundTop + containingBackgroundRect.height;

					const maxWidthInsideBackground = Math.max(minimumWidgetSize.width, backgroundRight - currentWidgetRelativeLeft);
					const maxHeightInsideBackground = Math.max(minimumWidgetSize.height, backgroundBottom - currentWidgetRelativeTop);

					const finalFreeWidth = Math.max(
						minimumWidgetSize.width,
						Math.min(rawPotentialWidth, maxWidthInsideBackground)
					);
					const finalFreeHeight = Math.max(
						minimumWidgetSize.height,
						Math.min(rawPotentialHeight, maxHeightInsideBackground)
					);

					let finalWidthOnBackground = finalFreeWidth;
					let finalHeightOnBackground = finalFreeHeight;
					let maxRightEdge = currentWidgetRelativeLeft + finalWidthOnBackground;
					let maxBottomEdge = currentWidgetRelativeTop + finalHeightOnBackground;

					dashboardWidgets.forEach(otherWidget => {
						if (otherWidget.id === id) return;
						if (otherWidget.type === 'background-image' || otherWidget.type === 'box-widget') return;
						if (canWaterWidgetsShareSpace(widgetBeingResized, otherWidget)) return;

						const otherRectPx = getWidgetRectPx(otherWidget, getEditableMetrics());
						const otherLeft = otherRectPx.left;
						const otherTop = otherRectPx.top;
						const otherWidth = otherRectPx.width;
						const otherHeight = otherRectPx.height;
						const otherRight = otherLeft + otherWidth;
						const otherBottom = otherTop + otherHeight;

						const hasVerticalOverlap = (
							currentWidgetRelativeTop < otherBottom &&
							currentWidgetRelativeTop + finalHeightOnBackground > otherTop
						);
						if (hasVerticalOverlap && otherLeft > currentWidgetRelativeLeft) {
							maxRightEdge = Math.min(maxRightEdge, otherLeft);
						}

						const hasHorizontalOverlap = (
							currentWidgetRelativeLeft < otherRight &&
							currentWidgetRelativeLeft + finalWidthOnBackground > otherLeft
						);
						if (hasHorizontalOverlap && otherTop > currentWidgetRelativeTop) {
							maxBottomEdge = Math.min(maxBottomEdge, otherTop);
						}
					});

					finalWidthOnBackground = Math.max(minimumWidgetSize.width, maxRightEdge - currentWidgetRelativeLeft);
					finalHeightOnBackground = Math.max(minimumWidgetSize.height, maxBottomEdge - currentWidgetRelativeTop);
					finalWidthOnBackground = Math.min(finalWidthOnBackground, finalFreeWidth);
					finalHeightOnBackground = Math.min(finalHeightOnBackground, finalFreeHeight);

					if (isFreeformResize && isWaterNetworkWidgetType(widgetBeingResized)) {
						const snappedResizeRect = snapWaterNetworkResizeRect(
							{
								left: currentWidgetRelativeLeft,
								top: currentWidgetRelativeTop,
								width: finalWidthOnBackground,
								height: finalHeightOnBackground,
							},
							widgetBeingResized,
							dashboardWidgets,
							getWidgetRectPx,
							getEditableMetrics(),
							id,
						);
						finalWidthOnBackground = snappedResizeRect.width;
						finalHeightOnBackground = snappedResizeRect.height;
					}

					const stillOverlaps = checkOverlap(
						currentWidgetRelativeLeft,
						currentWidgetRelativeTop,
						finalWidthOnBackground,
						finalHeightOnBackground,
						dashboardWidgets,
						id
						,
						widgetBeingResized,
					);

					if (stillOverlaps) {
						const previousWidth = widgetBeingResizedRect.width || minimumWidgetSize.width;
						const previousHeight = widgetBeingResizedRect.height || minimumWidgetSize.height;
						if (isPercentageLayout) {
							widget.style.width = `${previousWidth}px`;
							widget.style.height = `${previousHeight}px`;
						}
						setDims({ width: previousWidth, height: previousHeight });
						setAlertMessage(
							t.noOverlapOnBackgroundImage ||
							'Widgets cannot overlap, even on top of a background image.'
						);
						setAlertType('warning');
						setShowAlert(true);
						document.documentElement.removeEventListener('mousemove', moveListener);
						document.documentElement.removeEventListener('mouseup', upListener);
						return;
					}

					setDims({ width: finalWidthOnBackground, height: finalHeightOnBackground });
					setDashboard({
						...dashboard,
						widgets: dashboardWidgets.map(wd => wd.id === id ? {
							...wd,
							...serializeRectForStorage({
								left: currentWidgetRelativeLeft,
								top: currentWidgetRelativeTop,
								width: finalWidthOnBackground,
								height: finalHeightOnBackground,
							})
						} : wd)
					});
					document.documentElement.removeEventListener('mousemove', moveListener);
					document.documentElement.removeEventListener('mouseup', upListener);
					return;
				}

				let potentialWidth = isFreeformResize
					? mouseRelativeX - currentWidgetRelativeLeft
					: (Math.round(mouseRelativeX / gridColumnSize) * gridColumnSize) - currentWidgetRelativeLeft;
				let potentialHeight = isFreeformResize
					? mouseRelativeY - currentWidgetRelativeTop
					: (Math.round(mouseRelativeY / gridRowSize) * gridRowSize) - currentWidgetRelativeTop;

				potentialWidth = Math.max(minimumWidgetSize.width, potentialWidth);
				potentialHeight = Math.max(minimumWidgetSize.height, potentialHeight);

				let actualMaxRightEdge = editableRect.width;
				let actualMaxBottomEdge = editableRect.height;

				if (!isResizingBackgroundImage) {
					dashboardWidgets.forEach(otherWidget => {
						if (otherWidget.id === id) {
							return;
						}
						if (canWaterWidgetsShareSpace(widgetBeingResized, otherWidget)) {
							return;
						}

						const otherRectPx = getWidgetRectPx(otherWidget, getEditableMetrics());
						const otherLeft = otherRectPx.left;
						const otherTop = otherRectPx.top;
						const otherWidth = otherRectPx.width;
						const otherHeight = otherRectPx.height;
						const otherRight = otherLeft + otherWidth;
						const otherBottom = otherTop + otherHeight;

						const verticalOverlapWithPotential = (currentWidgetRelativeTop < otherBottom && currentWidgetRelativeTop + potentialHeight > otherTop);
						if (verticalOverlapWithPotential && otherLeft > currentWidgetRelativeLeft) {
							actualMaxRightEdge = Math.min(actualMaxRightEdge, otherLeft);
						}

						const horizontalOverlapWithPotential = (currentWidgetRelativeLeft < otherRight && currentWidgetRelativeLeft + potentialWidth > otherLeft);
						if (horizontalOverlapWithPotential && otherTop > currentWidgetRelativeTop) {
							actualMaxBottomEdge = Math.min(actualMaxBottomEdge, otherTop);
						}
					});
				}

				let finalAdjustedWidth = actualMaxRightEdge - currentWidgetRelativeLeft;
				let finalAdjustedHeight = actualMaxBottomEdge - currentWidgetRelativeTop;

				finalAdjustedWidth = Math.min(finalAdjustedWidth, potentialWidth);
				finalAdjustedHeight = Math.min(finalAdjustedHeight, potentialHeight);

				finalAdjustedWidth = Math.max(minimumWidgetSize.width, finalAdjustedWidth);
				finalAdjustedHeight = Math.max(minimumWidgetSize.height, finalAdjustedHeight);

				let finalSnappingWidth = finalAdjustedWidth;
				let finalSnappingHeight = finalAdjustedHeight;

				if (!isFreeformResize && gridColumnSize > 0) {
					const mouseRightRelative = ev.clientX - editableRect.left;
					let numCols = Math.ceil((mouseRightRelative - currentWidgetRelativeLeft) / gridColumnSize);
					numCols = Math.max(1, numCols);

					finalSnappingWidth = (numCols * defaultDivWidth) + (Math.max(0, numCols - 1) * gap);

					finalSnappingWidth = Math.min(finalSnappingWidth, actualMaxRightEdge - currentWidgetRelativeLeft);
					finalSnappingWidth = Math.max(minimumWidgetSize.width, finalSnappingWidth);
				}

				if (!isFreeformResize && gridRowSize > 0) {
					const mouseBottomRelative = ev.clientY - editableRect.top;
					let numRows = Math.ceil((mouseBottomRelative - currentWidgetRelativeTop) / gridRowSize);
					numRows = Math.max(1, numRows);

					finalSnappingHeight = (numRows * defaultDivHeight) + (Math.max(0, numRows - 1) * gap);

					finalSnappingHeight = Math.min(finalSnappingHeight, actualMaxBottomEdge - currentWidgetRelativeTop);
					finalSnappingHeight = Math.max(minimumWidgetSize.height, finalSnappingHeight);
				}

				finalAdjustedWidth = finalSnappingWidth;
				finalAdjustedHeight = finalSnappingHeight;

				if (isFreeformResize && isWaterNetworkWidgetType(widgetBeingResized)) {
					const snappedResizeRect = snapWaterNetworkResizeRect(
						{
							left: currentWidgetRelativeLeft,
							top: currentWidgetRelativeTop,
							width: finalAdjustedWidth,
							height: finalAdjustedHeight,
						},
						widgetBeingResized,
						dashboardWidgets,
						getWidgetRectPx,
						getEditableMetrics(),
						id,
					);
					finalAdjustedWidth = snappedResizeRect.width;
					finalAdjustedHeight = snappedResizeRect.height;
				}


				setDims({ width: finalAdjustedWidth, height: finalAdjustedHeight });
				setDashboard({
					...dashboard,
					widgets: dashboardWidgets.map(wd => wd.id === id ? {
						...wd,
						...serializeRectForStorage({
							left: currentWidgetRelativeLeft,
							top: currentWidgetRelativeTop,
							width: finalAdjustedWidth,
							height: finalAdjustedHeight,
						})
					} : wd)
				});
				document.documentElement.removeEventListener('mousemove', moveListener);
				document.documentElement.removeEventListener('mouseup', upListener);
			});
		},
		propsFromType = (type) => {
			const nextProps = cloneDashboardValue(type === 'chart' ? defaultChartProps : type === PERIOD_COLUMN_WIDGET_TYPE ? defaultPeriodColumnProps : type === 'gauge' ? defaultGaugeProps : type === 'liquid' ? defaultLiquidProps : type === WATER_TANK_WIDGET_TYPE ? defaultWaterTankProps : type === 'radial-gauge' ? defaultRadialGaugeProps : type === 'label' ? defaultLabelProps : type === 'led' ? defaultLEDProps : type === 'number' ? defaultNumberProps : type === 'slider' ? defaultSliderProps
				: type === 'switch' ? defaultSwitchProps : type === 'push-button' ? defaultPushButtonProps : type === 'terminal' ? defaultTerminalProps : type === 'text' ? defaultTextProps : type === 'menu' ? defaultMenuProps : type === 'segment-switch' ? defaultSegmentSwitchProps : type === 'background-image' ? defaultBackgroundImageProps : type === 'image-button' ? defaultImageButtonProps
					: type === 'keypad' ? defaultKeypadProps : type === 'table' ? defaultTableProps : type === 'fan' ? defaultFanProps : type === 'pipe' ? defaultPipeProps : type === 'elbow' ? defaultElbowProps : type === 'water-pump' ? defaultWaterPumpProps : type === 'text-title' ? defaultTextTitleProps : type === 'time-range' ? defaultTimeRangeProps : type === ENVIRONMENTAL_STATUS_WIDGET_TYPE ? defaultEnvironmentalStatusProps : type === 'box-widget' ? defaultBoxWidgetProps :
						{});
			if (typeof nextProps.showTitle !== 'boolean') {
				nextProps.showTitle = true;
			}
			return nextProps;
		},
		widgets = [
			<Widget draggable={true} title={t.widgetTitles?.chart} resizeWidget={resizeWidget} resize={false} id={'base-chart'} key={0} width={defaultWidth}
				height={defaultHeight} type={'chart'} defaultDivHeight={defaultDivHeight} extraProps={defaultChartProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.periodColumn || 'Period Column'} resizeWidget={resizeWidget} resize={false} id={'base-period-column'} key={'base-period-column'} width={defaultWidth}
				height={defaultHeight} type={PERIOD_COLUMN_WIDGET_TYPE} defaultDivHeight={defaultDivHeight} extraProps={previewPeriodColumnProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.gauge} resizeWidget={resizeWidget} resize={false} id={'base-gauge'} key={1} width={defaultWidth}
				height={defaultHeight} type={'gauge'} defaultDivHeight={defaultDivHeight} extraProps={defaultGaugeProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.liquid || 'Liquid Widget'} resizeWidget={resizeWidget} resize={false} id={'base-liquid'} key={'base-liquid'} width={defaultWidth}
				height={defaultHeight} type={'liquid'} defaultDivHeight={defaultDivHeight} extraProps={defaultLiquidProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.waterTank || 'Water Tank'} resizeWidget={resizeWidget} resize={false} id={'base-water-tank'} key={'base-water-tank'} width={defaultWidth}
				height={defaultWidth} type={WATER_TANK_WIDGET_TYPE} defaultDivHeight={defaultDivHeight} extraProps={defaultWaterTankProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.radialGauge} resizeWidget={resizeWidget} resize={false} id={'base-radial-gauge'} key={2} width={defaultWidth}
				height={defaultHeight} type={'radial-gauge'} defaultDivHeight={defaultDivHeight} extraProps={defaultRadialGaugeProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.label} resizeWidget={resizeWidget} resize={false} id={'base-label'} key={3} width={defaultWidth}
				height={defaultHeight} type={'label'} defaultDivHeight={defaultDivHeight} extraProps={defaultLabelProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.led} resizeWidget={resizeWidget} resize={false} id={'base-led'} key={4} width={defaultWidth}
				height={defaultHeight} type={'led'} defaultDivHeight={defaultDivHeight} extraProps={defaultLEDProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.number} resizeWidget={resizeWidget} resize={false} id={'base-number'} key={5} width={defaultWidth}
				height={defaultHeight} type={'number'} defaultDivHeight={defaultDivHeight} extraProps={defaultNumberProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.slider} resizeWidget={resizeWidget} resize={false} id={'base-slider'} key={6} width={defaultWidth}
				height={defaultHeight} type={'slider'} defaultDivHeight={defaultDivHeight} extraProps={defaultSliderProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.switch} resizeWidget={resizeWidget} resize={false} id={'base-switch'} key={7} width={defaultWidth}
				height={defaultHeight} type={'switch'} defaultDivHeight={defaultDivHeight} extraProps={defaultSwitchProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.pushButton || 'Push Button'} resizeWidget={resizeWidget} resize={false} id={'base-push-button'} key={71} width={defaultWidth}
				height={defaultHeight} type={'push-button'} defaultDivHeight={defaultDivHeight} extraProps={defaultPushButtonProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.terminal} resizeWidget={resizeWidget} resize={false} id={'base-terminal'} key={8} width={defaultWidth}
				height={defaultHeight} type={'terminal'} defaultDivHeight={defaultDivHeight} extraProps={defaultTerminalProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.text} resizeWidget={resizeWidget} resize={false} id={'base-text'} key={9} width={defaultWidth}
				height={defaultHeight} type={'text'} defaultDivHeight={defaultDivHeight} extraProps={defaultTextProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.menu} resizeWidget={resizeWidget} resize={false} id={'base-menu'} key={10} width={defaultWidth}
				height={defaultHeight} type={'menu'} defaultDivHeight={defaultDivHeight} extraProps={defaultMenuProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.segmentSwitch} resizeWidget={resizeWidget} resize={false} id={'base-segment-switch'} key={11} width={defaultWidth}
				height={defaultHeight} type={'segment-switch'} defaultDivHeight={defaultDivHeight} extraProps={defaultSegmentSwitchProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.imageButton} resizeWidget={resizeWidget} resize={false} id={'base-image-button'} key={12} width={defaultWidth}
				height={defaultHeight} type={'image-button'} defaultDivHeight={defaultDivHeight} extraProps={defaultImageButtonProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.backgroundImage} resizeWidget={resizeWidget} resize={false} id={'base-background-image'} key={13} width={defaultWidth}
				height={defaultHeight} type={'background-image'} defaultDivHeight={defaultDivHeight} extraProps={defaultBackgroundImageProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.keypad} resizeWidget={resizeWidget} resize={false} id={'base-keypad'} key={14} width={defaultWidth}
				height={defaultHeight} type={'keypad'} defaultDivHeight={defaultDivHeight} extraProps={defaultKeypadProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget
				draggable
				resize={false}
				resizeWidget={resizeWidget}
				id="base-table"
				key="base-table"
				type="table"
				title={t.widgetTitles?.table}
				width={defaultWidth}
				height={defaultHeight}
				extraProps={defaultTableProps}
				remove={remove}
				duplicate={duplicate}
			/>,
			<Widget
				draggable
				title={t.widgetTitles?.fan}
				resizeWidget={resizeWidget}
				resize={false}
				id="base-fan"
				key={15}
				width={defaultWidth}
				height={defaultHeight}
				type="fan"
				defaultDivHeight={defaultDivHeight}
				extraProps={defaultFanProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove}
			/>,
			<Widget
				draggable
				title={t.widgetTitles?.pipe}
				resizeWidget={resizeWidget}
				resize={false}
				id="base-pipe"
				key={16}
				width={defaultWidth}
				height={defaultHeight}
				type="pipe"
				defaultDivHeight={defaultDivHeight}
				extraProps={defaultPipeProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove}
			/>,
			<Widget
				draggable
				title={t.widgetTitles?.waterPump || 'PUMP Widget'}
				resizeWidget={resizeWidget}
				resize={false}
				id="base-water-pump"
				key={170}
				width={defaultWidth}
				height={defaultHeight}
				type="water-pump"
				defaultDivHeight={defaultDivHeight}
				extraProps={defaultWaterPumpProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove}
			/>,
			<Widget
				draggable
				title={t.widgetTitles?.elbow}
				resizeWidget={resizeWidget}
				resize={false}
				id="base-elbow"
				key={17}
				width={defaultWidth}
				height={defaultHeight}
				type="elbow"
				defaultDivHeight={defaultDivHeight}
				extraProps={defaultElbowProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove}
			/>, <Widget draggable={true} title={t.widgetTitles?.textTitle || 'Title Widget'} resizeWidget={resizeWidget} resize={false} id={'base-text-title'} key={18} width={defaultWidth}
				height={defaultHeight} type={'text-title'} defaultDivHeight={defaultDivHeight} extraProps={defaultTextTitleProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
				<Widget draggable={true} title={t.widgetTitles?.timeRange || 'Digital Clock'} resizeWidget={resizeWidget} resize={false} id={'base-time-range'} key={19} width={defaultWidth}
				height={defaultHeight} type={'time-range'} defaultDivHeight={defaultDivHeight} extraProps={defaultTimeRangeProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
				<Widget draggable={true} title={t.widgetTitles?.environmentalStatus || 'Environmental Status'} resizeWidget={resizeWidget} resize={false} id={'base-environmental-status'} key={132} width={defaultWidth}
				height={defaultHeight} type={ENVIRONMENTAL_STATUS_WIDGET_TYPE} defaultDivHeight={defaultDivHeight} extraProps={defaultEnvironmentalStatusProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
			<Widget draggable={true} title={t.widgetTitles?.boxWidget || 'Box Widget'} resizeWidget={resizeWidget} resize={false} id={'base-box-widget'} key={131} width={defaultWidth}
				height={defaultHeight} type={'box-widget'} defaultDivHeight={defaultDivHeight} extraProps={defaultBoxWidgetProps}
				defaultDivWidth={defaultDivWidth} duplicate={duplicate} remove={remove} />,
		];

	const filteredWidgetOptions = widgets.filter((widgetOption) => {
		const query = widgetSearchQuery.trim().toLowerCase();
		if (!query) return true;

		const props = widgetOption?.props || {};
		return [props.title, props.type, props.id]
			.filter(Boolean)
			.some((value) => String(value).toLowerCase().includes(query));
	});

	const prevFixedWidthRef = useRef(dashboard?.fixedWidth);
	const dragPointerOffsetRef = useRef(null);
	const selectionDragRef = useRef(null);
	const selectedGroupDragRef = useRef(null);
	const selectedGroupDragOffsetRef = useRef(null);
	const suppressSelectionClickRef = useRef(false);
	const layoutMeasureSignatureRef = useRef('');
	const fixedWidthToggleHandlerRef = useRef(null);
	const multiSelectToggleHandlerRef = useRef(null);

	const externalFixedWidthToggleHandler = useCallback((nextChecked) => {
		if (typeof fixedWidthToggleHandlerRef.current === 'function') {
			fixedWidthToggleHandlerRef.current(nextChecked);
		}
	}, []);

	const externalMultiSelectToggleHandler = useCallback((nextChecked) => {
		if (typeof multiSelectToggleHandlerRef.current === 'function') {
			multiSelectToggleHandlerRef.current(nextChecked);
		}
	}, []);

	const clearMultiSelection = useCallback(() => {
		setSelectedWidgetIds((currentIds) => (currentIds.length === 0 ? currentIds : []));
		setSelectionRect((currentRect) => (currentRect === null ? currentRect : null));
		setSelectedGroupDragOffset((currentOffset) => (currentOffset === null ? currentOffset : null));
	}, []);

	const rememberDragPointerOffset = useCallback((e) => {
		const widgetElement = e?.target?.closest?.('.widget');
		if (!widgetElement || !widgetElement.id) {
			return;
		}

		const rect = widgetElement.getBoundingClientRect();
		if (!Number.isFinite(e.clientX) || !Number.isFinite(e.clientY)) {
			return;
		}
		dragPointerOffsetRef.current = {
			id: widgetElement.id,
			offsetX: e.clientX - rect.left,
			offsetY: e.clientY - rect.top,
		};
	}, []);

	const clearDragPointerOffset = useCallback(() => {
		dragPointerOffsetRef.current = null;
	}, []);

	useEffect(() => {
		if (mode !== 'edit') {
			layoutMeasureSignatureRef.current = '';
			return undefined;
		}

		const getLayoutSignature = () => {
			const mainContainer = document.getElementById('web-dashboard-main-container');
			const dashboardElement = document.getElementById('diverted');
			const editable = document.getElementById('editable');
			const widgetsBox = document.querySelector('.widgets-box');

			return [
				mainContainer?.offsetWidth || 0,
				mainContainer?.offsetHeight || 0,
				dashboardElement?.clientWidth || 0,
				dashboardElement?.clientHeight || 0,
				editable?.offsetWidth || 0,
				editable?.offsetHeight || 0,
				widgetsBox?.offsetWidth || 0,
				widgetsBox?.offsetHeight || 0,
			].join(':');
		};

		if (typeof ResizeObserver === 'undefined' || typeof window === 'undefined') {
			const nextSignature = getLayoutSignature();
			if (nextSignature !== layoutMeasureSignatureRef.current) {
				layoutMeasureSignatureRef.current = nextSignature;
				setLayoutMeasureVersion((prev) => prev + 1);
			}
			return undefined;
		}

		let rafId = 0;
		const scheduleLayoutUpdate = () => {
			if (rafId) {
				window.cancelAnimationFrame(rafId);
			}

			rafId = window.requestAnimationFrame(() => {
				const nextSignature = getLayoutSignature();
				if (nextSignature === layoutMeasureSignatureRef.current) {
					return;
				}

				layoutMeasureSignatureRef.current = nextSignature;
				setLayoutMeasureVersion((prev) => prev + 1);
			});
		};

		const mainContainer = document.getElementById('web-dashboard-main-container');
		const dashboardElement = document.getElementById('diverted');
		const editable = document.getElementById('editable');
		const widgetsBox = document.querySelector('.widgets-box');
		const observedElements = [mainContainer, dashboardElement, editable, widgetsBox].filter(Boolean);

		const resizeObserver = new ResizeObserver(() => {
			scheduleLayoutUpdate();
		});

		observedElements.forEach((element) => resizeObserver.observe(element));
		scheduleLayoutUpdate();

		return () => {
			if (rafId) {
				window.cancelAnimationFrame(rafId);
			}
			resizeObserver.disconnect();
		};
	}, [mode]);

	const validDatastreams = (datastreams || []).filter(ds => ds);
	const findDatastreamById = useCallback((list, rawId) => {
		const safeList = Array.isArray(list) ? list : [];
		if (rawId === null || rawId === undefined || rawId === '') return null;
		const numericId = Number(rawId);
		if (Number.isFinite(numericId)) {
			const byNumericId = safeList.find(ds => Number(ds?.id) === numericId);
			if (byNumericId) return byNumericId;
		}
		return safeList.find(ds => String(ds?.id) === String(rawId)) || null;
	}, []);
	const normalizeDatastreamPin = useCallback((rawPin) => {
		const normalizedPin = String(rawPin ?? '').trim();
		if (!normalizedPin || normalizedPin.toLowerCase() === 'pin') {
			return null;
		}
		return normalizedPin;
	}, []);
	const findDatastreamByPin = useCallback((list, rawPin) => {
		const safeList = Array.isArray(list) ? list : [];
		const normalizedPin = normalizeDatastreamPin(rawPin);
		if (!normalizedPin) return null;
		return safeList.find(ds => normalizeDatastreamPin(ds?.pin) === normalizedPin) || null;
	}, [normalizeDatastreamPin]);
	const findDatastreamByReference = useCallback((list, rawId, rawPin) => {
		return findDatastreamById(list, rawId) || findDatastreamByPin(list, rawPin);
	}, [findDatastreamById, findDatastreamByPin]);

	const selectedDatastream = useMemo(() => {
		const rawId = edit?.dataId;
		const rawPin = edit?.dataPin;
		const fromTemplate = findDatastreamByReference(validDatastreams, rawId, rawPin);
		if (fromTemplate) return fromTemplate;
		const fromDevice = findDatastreamByReference(deviceDatastream, rawId, rawPin);
		return fromDevice || null;
	}, [deviceDatastream, edit?.dataId, edit?.dataPin, findDatastreamByReference, validDatastreams]);

	const getDeviceTemplateSignature = useCallback((device) => {
		if (!device) return '';
		const tidValue = device?.template?.tid;
		if (tidValue !== null && tidValue !== undefined && String(tidValue).trim() !== '') {
			return `tid:${String(tidValue)}`;
		}
		const templateIdValue = device?.template_id ?? device?.template?.id;
		if (templateIdValue === null || templateIdValue === undefined || String(templateIdValue).trim() === '') {
			return '';
		}
		return `id:${String(templateIdValue)}`;
	}, []);

	const isStringDatastream = useMemo(() => {
		const dt = selectedDatastream?.dataType ?? selectedDatastream?.type ?? selectedDatastream?.datatype;
		return String(dt || '').toLowerCase() === 'string';
	}, [selectedDatastream]);

	const getDsMinMax = useCallback((ds) => {
		if (!ds) return { min: null, max: null };
		const rawMin = ds.min ?? ds.minimum ?? ds.minValue ?? ds.min_value;
		const rawMax = ds.max ?? ds.maximum ?? ds.maxValue ?? ds.max_value;
		const min = rawMin === '' || rawMin === null || rawMin === undefined ? null : Number(rawMin);
		const max = rawMax === '' || rawMax === null || rawMax === undefined ? null : Number(rawMax);
		return {
			min: Number.isFinite(min) ? min : null,
			max: Number.isFinite(max) ? max : null,
		};
	}, []);

	const getEffectiveMinMax = useCallback((ds, fallbackProps) => {
		const { min: dsMin, max: dsMax } = getDsMinMax(ds);
		const propMin = fallbackProps && Number.isFinite(Number(fallbackProps.min)) ? Number(fallbackProps.min) : null;
		const propMax = fallbackProps && Number.isFinite(Number(fallbackProps.max)) ? Number(fallbackProps.max) : null;
		let min = dsMin ?? propMin;
		let max = dsMax ?? propMax;

		if (min !== null && max !== null && max < min) {
			const lower = Math.min(min, max);
			const upper = Math.max(min, max);
			min = lower;
			max = upper;
		}

		return { min, max };
	}, [getDsMinMax]);

	const clampToMinMax = useCallback((val, ds, fallbackProps) => {
		if (val === '' || val === null || val === undefined) return val;
		const num = typeof val === 'number' ? val : Number(val);
		const { min, max } = getEffectiveMinMax(ds, fallbackProps);
		if (!Number.isFinite(num)) {
			const raw = typeof val === 'string' ? val.trim() : '';
			const looksNumeric = raw !== '' && /^[-+]?(\d+(\.\d*)?|\.\d+)(e[-+]?\d+)?$/i.test(raw);
			if (looksNumeric) {
				if (raw.startsWith('-') && min !== null) return min;
				if (!raw.startsWith('-') && max !== null) return max;
				if (min !== null && max !== null && min === max) return min;
			}
			return val;
		}
		if (min !== null && num < min) return min;
		if (max !== null && num > max) return max;
		return num;
	}, [getEffectiveMinMax]);

	const getNumericStepPrecision = useCallback((value) => {
		const raw = String(value ?? '').trim().toLowerCase();
		if (!raw) return 0;
		if (raw.includes('e-')) {
			const scientificPrecision = Number(raw.split('e-')[1]);
			return Number.isFinite(scientificPrecision) ? scientificPrecision : 0;
		}
		const fractional = raw.split('.')[1] || '';
		return fractional.length;
	}, []);

	const snapValueToStep = useCallback((value, stepValue, minValue = 0) => {
		const numericValue = Number(value);
		const numericStep = Number(stepValue);
		const numericMin = Number(minValue);

		if (!Number.isFinite(numericValue) || !Number.isFinite(numericStep) || numericStep <= 0) {
			return value;
		}

		const baseValue = Number.isFinite(numericMin) ? numericMin : 0;
		const precision = Math.max(
			getNumericStepPrecision(numericStep),
			getNumericStepPrecision(baseValue)
		);
		const snappedValue = Math.round((numericValue - baseValue) / numericStep) * numericStep + baseValue;
		return Number(snappedValue.toFixed(precision));
	}, [getNumericStepPrecision]);

	const normalizeSliderValueInputChange = useCallback((nextValue, previousValue, options = {}) => {
		const {
			stepValue,
			ds,
			fallbackProps,
			nativeInputType,
			nativeInputData,
		} = options;

		const clampedValue = clampToMinMax(nextValue, ds, fallbackProps);
		const numericStep = Number(stepValue);
		const numericNextValue = Number(clampedValue);
		const numericPreviousValue = Number(previousValue);

		if (
			!Number.isFinite(numericStep) ||
			numericStep <= 0 ||
			!Number.isFinite(numericNextValue) ||
			!Number.isFinite(numericPreviousValue)
		) {
			return clampedValue;
		}

		const delta = numericNextValue - numericPreviousValue;
		if (delta === 0 || Math.abs(delta) >= numericStep) {
			return clampedValue;
		}

		const normalizedInputType = typeof nativeInputType === 'string' ? nativeInputType : '';
		const isDirectTextEntry = normalizedInputType === 'insertText'
			|| normalizedInputType === 'insertFromPaste'
			|| normalizedInputType === 'deleteContentBackward'
			|| normalizedInputType === 'deleteContentForward';
		const likelyNativeStepper = nativeInputData == null && !isDirectTextEntry;

		if (!likelyNativeStepper) {
			return clampedValue;
		}

		const steppedValue = numericPreviousValue + (Math.sign(delta) * numericStep);
		return clampToMinMax(steppedValue, ds, fallbackProps);
	}, [clampToMinMax]);

	const updateWidgetLivePropsById = useCallback((widgetId, partialProps, options = {}) => {
		if (!widgetId || !partialProps || typeof partialProps !== 'object') return;

		const { syncEdit = false } = options;

		setWidgetLiveProps((prevLiveProps) => {
			const previousWidgetProps = prevLiveProps?.[widgetId] || {};
			const nextWidgetProps = { ...previousWidgetProps, ...partialProps };
			const changed = Object.keys(partialProps).some(
				(key) => previousWidgetProps?.[key] !== nextWidgetProps[key]
			);

			if (!changed) {
				return prevLiveProps;
			}

			return {
				...(prevLiveProps || {}),
				[widgetId]: nextWidgetProps,
			};
		});

		if (syncEdit) {
			setEdit((prev) => {
				if (!prev?.open || String(prev.id) !== String(widgetId)) {
					return prev;
				}

				return {
					...prev,
					props: {
						...(prev.props || {}),
						...partialProps,
					},
				};
			});
		}
	}, [setEdit]);

	const clearWidgetLivePropsById = useCallback((widgetId) => {
		if (!widgetId) return;

		setWidgetLiveProps((prevLiveProps) => {
			if (!prevLiveProps || !Object.prototype.hasOwnProperty.call(prevLiveProps, widgetId)) {
				return prevLiveProps;
			}

			const nextLiveProps = { ...prevLiveProps };
			delete nextLiveProps[widgetId];
			return nextLiveProps;
		});
	}, []);

	const stripDatastreamManagedProps = useCallback((props, widgetType) => {
		const nextProps = cloneDashboardValue(props || {});
		if (!nextProps || typeof nextProps !== 'object' || Array.isArray(nextProps)) {
			return nextProps;
		}

		delete nextProps.min;
		delete nextProps.max;
		delete nextProps.unit;

		return nextProps;
	}, []);

	const remapColorStopsForDatastream = useCallback((stops, previousDs, nextDs) => {
		if (!Array.isArray(stops) || stops.length === 0) return [];

		const normalizedStops = stops
			.filter((s) => s && Number.isFinite(Number(s.value)))
			.map((s) => ({ ...s, value: Number(s.value) }))
			.sort((a, b) => a.value - b.value);

		if (normalizedStops.length === 0) return [];

		const { min: nextMin, max: nextMax } = getDsMinMax(nextDs);
		if (!Number.isFinite(nextMin) || !Number.isFinite(nextMax) || nextMax <= nextMin) {
			return normalizedStops;
		}

		const { min: prevDsMin, max: prevDsMax } = getDsMinMax(previousDs);
		let prevMin = Number.isFinite(prevDsMin) ? prevDsMin : null;
		let prevMax = Number.isFinite(prevDsMax) ? prevDsMax : null;

		if (!(prevMax > prevMin)) {
			const allPercent = normalizedStops.every((s) => s.value >= 0 && s.value <= 100);
			if (allPercent) {
				prevMin = 0;
				prevMax = 100;
			} else {
				prevMin = normalizedStops[0].value;
				prevMax = normalizedStops[normalizedStops.length - 1].value;
			}
		}

		const prevRange = prevMax - prevMin;
		if (!(prevRange > 0)) return normalizedStops;

		const nextRange = nextMax - nextMin;
		return normalizedStops
			.map((s) => {
				const ratio = Math.max(0, Math.min(1, (s.value - prevMin) / prevRange));
				const remappedValue = nextMin + (ratio * nextRange);
				return { ...s, value: Number(remappedValue.toFixed(2)) };
			})
			.sort((a, b) => a.value - b.value);
	}, [getDsMinMax]);

	const applyDatastreamDefaultColors = (props, widgetType, ds) => {
		if (!ds?.color || !VALUE_COLOR_WIDGET_TYPES.includes(widgetType)) {
			return props;
		}

		const nextProps = { ...props };
		nextProps.color = ds.color;
		nextProps[themeColorKey('color')] = ds.color;

		if (widgetType === 'gauge' || widgetType === 'radial-gauge') {
			nextProps.startColor = ds.color;
			nextProps[themeColorKey('startColor')] = ds.color;
			nextProps.stopColor = ds.color;
			nextProps[themeColorKey('stopColor')] = ds.color;
		}

		return nextProps;
	};

	const formatPinForTitle = (pin) => {
		if (!pin) return '';
		const m = String(pin).match(/^([A-Za-z]+)\s*(\d+)$/);
		return m ? `[${m[1]}${m[2]}]` : `[${pin}]`;
	};

	const preserveTitleSpaces = (txt) => String(txt ?? '').replace(/ /g, '\u00A0');

	const toLengthInputNumber = (value, fallback = 0) => {
		if (value === null || value === undefined || value === '') return fallback;
		if (typeof value === 'number' && Number.isFinite(value)) return value;
		const normalized = String(value).replace('%', '').replace('px', '').trim();
		const parsed = Number(normalized);
		return Number.isFinite(parsed) ? parsed : fallback;
	};

	const roundGeometryValue = (value) => Number(Number(value).toFixed(4));

	const editWidget = (dashboard?.widgets || []).find((widget) => widget.id === edit.id) || null;
	const editWidgetMetrics = edit.open ? getEditableMetrics() : null;
	const editWidgetRectPx = editWidget ? getWidgetRectPx(editWidget, editWidgetMetrics || undefined) : null;

	const editContainingBackground = (() => {
		if (!editWidget) return null;
		if (editWidget.type === 'background-image' || editWidget.type === 'box-widget') return null;
		if (!editWidgetRectPx) return null;
		return getContainingBackgroundImage(
			editWidgetRectPx.left,
			editWidgetRectPx.top,
			dashboard?.widgets || [],
			editWidget.id,
		);
	})();
	const editContainingBackgroundRect = editContainingBackground
		? getWidgetRectPx(editContainingBackground, editWidgetMetrics || undefined)
		: null;

	const isEditWidgetOnBackgroundImage = Boolean(editContainingBackgroundRect);

	const editWidgetLayoutTarget = edit.open && editWidget
		? {
			...editWidget,
			type: edit.type || editWidget.type,
			extra_props: edit.props || editWidget.extra_props || {},
		}
		: null;
	const editFreeMovementEnabled = isWidgetFreeMovementEnabled(editWidgetLayoutTarget);

	const showFreeGeometrySettings = Boolean(
		edit.open && editWidget && (isEditWidgetOnBackgroundImage || editFreeMovementEnabled),
	);

	const geometryOriginLeft = editContainingBackgroundRect
		? editContainingBackgroundRect.left
		: 0;
	const geometryOriginTop = editContainingBackgroundRect
		? editContainingBackgroundRect.top
		: 0;

	const geometryContainerWidth = Math.max(0, editContainingBackgroundRect?.width || editWidgetMetrics?.width || 0);
	const geometryContainerHeight = Math.max(0, editContainingBackgroundRect?.height || editWidgetMetrics?.height || 0);

	const geometryWidthValue = roundGeometryValue(Math.max(
		0.0001,
		toLengthInputNumber(
			edit.width,
			editWidgetRectPx?.width ?? defaultWidth,
		),
	));
	const geometryHeightValue = roundGeometryValue(Math.max(
		0.0001,
		toLengthInputNumber(
			edit.height,
			editWidgetRectPx?.height ?? defaultHeight,
		),
	));
	const geometryLeftValue = roundGeometryValue(Math.max(
		0,
		toLengthInputNumber(
			edit.left,
			editWidgetRectPx?.left ?? geometryOriginLeft,
		) - geometryOriginLeft,
	));
	const geometryTopValue = roundGeometryValue(Math.max(
		0,
		toLengthInputNumber(
			edit.top,
			editWidgetRectPx?.top ?? geometryOriginTop,
		) - geometryOriginTop,
	));

	const geometryRightValue = roundGeometryValue(Math.max(
		0,
		geometryContainerWidth - geometryLeftValue - geometryWidthValue,
	));
	const geometryBottomValue = roundGeometryValue(Math.max(
		0,
		geometryContainerHeight - geometryTopValue - geometryHeightValue,
	));
	const geometryUnitLabel = 'px';
	const [activeGeometryField, setActiveGeometryField] = useState(null);
	const [geometryDrafts, setGeometryDrafts] = useState({
		width: '',
		height: '',
		top: '',
		left: '',
		right: '',
		bottom: '',
	});
	const formatGeometryDraftValue = useCallback((value) => String(roundGeometryValue(value)), []);

	useEffect(() => {
		setActiveGeometryField(null);
	}, [edit.id, edit.open]);

	useEffect(() => {
		if (!showFreeGeometrySettings) {
			setGeometryDrafts({
				width: '',
				height: '',
				top: '',
				left: '',
				right: '',
				bottom: '',
			});
			setActiveGeometryField(null);
			return;
		}

		const nextDrafts = {
			width: formatGeometryDraftValue(geometryWidthValue),
			height: formatGeometryDraftValue(geometryHeightValue),
			top: formatGeometryDraftValue(geometryTopValue),
			left: formatGeometryDraftValue(geometryLeftValue),
			right: formatGeometryDraftValue(geometryRightValue),
			bottom: formatGeometryDraftValue(geometryBottomValue),
		};

		setGeometryDrafts((prev) => ({
			width: activeGeometryField === 'width' ? prev.width : nextDrafts.width,
			height: activeGeometryField === 'height' ? prev.height : nextDrafts.height,
			top: activeGeometryField === 'top' ? prev.top : nextDrafts.top,
			left: activeGeometryField === 'left' ? prev.left : nextDrafts.left,
			right: activeGeometryField === 'right' ? prev.right : nextDrafts.right,
			bottom: activeGeometryField === 'bottom' ? prev.bottom : nextDrafts.bottom,
		}));
	}, [
		activeGeometryField,
		formatGeometryDraftValue,
		geometryBottomValue,
		geometryHeightValue,
		geometryLeftValue,
		geometryRightValue,
		geometryTopValue,
		geometryWidthValue,
		showFreeGeometrySettings,
	]);

	const handleGeometryDraftChange = useCallback((field, nextValue) => {
		setActiveGeometryField(field);
		setGeometryDrafts((prev) => ({ ...prev, [field]: nextValue }));

		const parsed = Number(nextValue);
		if (!Number.isFinite(parsed)) return;

		if (field === 'width') {
			setEdit((prev) => ({
				...prev,
				width: roundGeometryValue(Math.max(0.0001, parsed)),
			}));
			return;
		}

		if (field === 'height') {
			setEdit((prev) => ({
				...prev,
				height: roundGeometryValue(Math.max(0.0001, parsed)),
			}));
			return;
		}

		if (field === 'top') {
			setEdit((prev) => ({
				...prev,
				top: roundGeometryValue(geometryOriginTop + Math.max(0, parsed)),
			}));
			return;
		}

		if (field === 'left') {
			setEdit((prev) => ({
				...prev,
				left: roundGeometryValue(geometryOriginLeft + Math.max(0, parsed)),
			}));
			return;
		}

		if (field === 'right') {
			const nextRight = Math.max(0, parsed);
			const nextLeft = Math.max(0, geometryContainerWidth - geometryWidthValue - nextRight);
			setEdit((prev) => ({
				...prev,
				left: roundGeometryValue(geometryOriginLeft + nextLeft),
			}));
			return;
		}

		if (field === 'bottom') {
			const nextBottom = Math.max(0, parsed);
			const nextTop = Math.max(0, geometryContainerHeight - geometryHeightValue - nextBottom);
			setEdit((prev) => ({
				...prev,
				top: roundGeometryValue(geometryOriginTop + nextTop),
			}));
		}
	}, [
		geometryContainerHeight,
		geometryContainerWidth,
		geometryHeightValue,
		geometryOriginLeft,
		geometryOriginTop,
		geometryWidthValue,
	]);

	const handleGeometryDraftBlur = useCallback((field) => {
		setActiveGeometryField((prev) => (prev === field ? null : prev));
		setGeometryDrafts((prev) => ({
			...prev,
			[field]:
				field === 'width' ? formatGeometryDraftValue(geometryWidthValue)
					: field === 'height' ? formatGeometryDraftValue(geometryHeightValue)
						: field === 'top' ? formatGeometryDraftValue(geometryTopValue)
							: field === 'left' ? formatGeometryDraftValue(geometryLeftValue)
								: field === 'right' ? formatGeometryDraftValue(geometryRightValue)
									: formatGeometryDraftValue(geometryBottomValue),
		}));
	}, [
		formatGeometryDraftValue,
		geometryBottomValue,
		geometryHeightValue,
		geometryLeftValue,
		geometryRightValue,
		geometryTopValue,
		geometryWidthValue,
	]);


	useEffect(() => {
		setIsFixedWidthMode(dashboard?.fixedWidth ?? true);
	}, [dashboard?.fixedWidth]);

	const handleFixedWidthToggle = useCallback((nextChecked) => {
		if (!nextChecked) {
			setIsFixedWidthMode(false);
			setDashboard({
				...dashboard,
				fixedWidth: false,
				widgets: dashboard?.widgets || [],
			});
			return;
		}

		const mainContainer = document.getElementById('web-dashboard-main-container');
		const dashboardElement = document.getElementById('diverted');
		const editable = document.getElementById('editable');
		const widgetsBox = document.querySelector('.widgets-box');

		if (!mainContainer || !dashboardElement || !editable || !widgetsBox) {
			console.warn('DOM elements not ready for fixed width calculation, falling back to simple toggle.');
			setIsFixedWidthMode(true);
			setDashboard({ ...dashboard, fixedWidth: true, widgets: dashboard?.widgets || [] });
			return;
		}

		const style = document.defaultView.getComputedStyle(dashboardElement);
		const gap = parseInt(style.gap, 10) || 0;

		const editableStyle = document.defaultView.getComputedStyle(editable);
		const paddingLeft = parseInt(editableStyle.paddingLeft, 10) || 0;
		const paddingRight = parseInt(editableStyle.paddingRight, 10) || 0;
		const paddingTop = parseInt(editableStyle.paddingTop, 10) || 0;
		const paddingBottom = parseInt(editableStyle.paddingBottom, 10) || 0;
		const totalHorizontalPadding = paddingLeft + paddingRight;
		const totalVerticalPadding = paddingTop + paddingBottom;

		const containerGap = 8;

		const widgetsBoxWidth = widgetsBox.offsetWidth + containerGap;
		if (widgetsBoxWidth !== lastWidgetsBoxWidth) setLastWidgetsBoxWidth(widgetsBoxWidth);

		const mainContainerWidth = mainContainer.offsetWidth;
		if (mainContainerWidth - containerGap !== fixedViewWidth) setFixedViewWidth(mainContainerWidth - containerGap);

		const availableWidthForDiverted = Math.max(1, mainContainerWidth - widgetsBoxWidth);
		const availableHeightForDiverted = getElementViewportHeight(mainContainer);
		const gridRowUnit = Math.max(1, defaultDivHeight + gap);
		const bottomBufferPx = getCanvasBottomBufferPx({
			viewportHeight: availableHeightForDiverted,
			defaultHeight,
			rowUnit: gridRowUnit,
		});
		const currentWidgets = dashboard?.widgets || [];
		const widgetMetrics = getEditableMetrics();
		const { maxBottom: maxWidgetBottom } = getWidgetsCanvasBoundsPx(currentWidgets, widgetMetrics);

		let currentEditableWidthPxForFixed;
		let currentEditableHeightPxForFixed;

		if (isPercentageLayout) {
			currentEditableWidthPxForFixed = Math.max(1, availableWidthForDiverted);
			currentEditableHeightPxForFixed = Math.max(
				defaultHeight + totalVerticalPadding,
				maxWidgetBottom + totalVerticalPadding + gap + bottomBufferPx,
			);
		} else {
			const minGridColsForDefaultWidget = Math.ceil(defaultWidth / (defaultDivWidth + gap));
			const minGridRowsForDefaultWidget = Math.ceil(defaultHeight / (defaultDivHeight + gap));

			let calculatedColumnsForFixed = Math.max(
				1,
				minGridColsForDefaultWidget,
				Math.floor((availableWidthForDiverted - totalHorizontalPadding) / (defaultDivWidth + gap))
			);
			calculatedColumnsForFixed = Math.max(1, calculatedColumnsForFixed);

			currentEditableWidthPxForFixed =
				calculatedColumnsForFixed * (defaultDivWidth + gap) + totalHorizontalPadding;
			currentEditableWidthPxForFixed = Math.max(
				currentEditableWidthPxForFixed,
				defaultWidth + totalHorizontalPadding
			);

			let calculatedRowsForFixed = Math.max(
				minGridRowsForDefaultWidget,
				Math.ceil((maxWidgetBottom + gap) / gridRowUnit) + EDITABLE_TRAILING_ROWS
			);
			calculatedRowsForFixed = Math.max(1, calculatedRowsForFixed);

			currentEditableHeightPxForFixed =
				calculatedRowsForFixed * gridRowUnit + totalVerticalPadding;
			currentEditableHeightPxForFixed = Math.max(
				currentEditableHeightPxForFixed,
				defaultHeight + totalVerticalPadding,
			);
		}

		const fixedCanvasTargetWidth = Math.max(currentEditableWidthPxForFixed, availableWidthForDiverted);
		const maxRight = isPercentageLayout ? currentEditableWidthPxForFixed : fixedCanvasTargetWidth;
		const maxBottom = currentEditableHeightPxForFixed;

		const anyOutside = currentWidgets.some((w) => {
			const rect = getWidgetRectPx(w, widgetMetrics);
			const left = rect.left;
			const top = rect.top;
			const width = rect.width;
			const height = rect.height;

			if (left < 0 || top < 0) return true;
			if (left + width > maxRight) return true;
			if (top + height > maxBottom) return true;
			return false;
		});

		if (anyOutside) {
			setAlertMessage(t.fixedWidthResizeAlert);
			setAlertType('warning');
			setShowAlert(true);
			return;
		}

		setIsFixedWidthMode(true);
		setDashboard({
			...dashboard,
			fixedWidth: true,
			widgets: dashboard?.widgets || [],
		});
	}, [
		dashboard,
		defaultDivHeight,
		defaultDivWidth,
		defaultHeight,
		defaultWidth,
		fixedViewWidth,
		getEditableMetrics,
		getWidgetRectPx,
		getWidgetsCanvasBoundsPx,
		lastWidgetsBoxWidth,
		setDashboard,
		t.fixedWidthResizeAlert,
		isPercentageLayout,
	]);

	useEffect(() => {
		if (typeof onFixedWidthModeChange === 'function') {
			onFixedWidthModeChange(isFixedWidthMode);
		}
	}, [isFixedWidthMode, onFixedWidthModeChange]);

	useEffect(() => {
		if (mode !== 'edit' || !isPercentageLayout) {
			return undefined;
		}

		const timeout = setTimeout(() => {
			const editableElement = document.getElementById('editable');
			const referenceMetrics = getPercentageReferenceMetrics();
			const currentWidgets = dashboard?.widgets || [];
			const needsLegacyGeometryMigration = currentWidgets.some(
				(widget) => !hasStoredPercentageGeometry(widget),
			);

			if (
				!editableElement
				|| !referenceMetrics
				|| currentWidgets.length === 0
				|| !needsLegacyGeometryMigration
			) {
				return;
			}

			const editableRect = editableElement.getBoundingClientRect();
			const safeWidth = Math.max(1, referenceMetrics.width);
			const safeHeight = Math.max(1, referenceMetrics.height);

			let changed = false;
			const normalizedWidgets = currentWidgets.map((widget) => {
				if (hasStoredPercentageGeometry(widget)) {
					return widget;
				}

				const widgetElement = document.getElementById(widget?.id);
				if (!widgetElement) {
					return widget;
				}

				const rect = widgetElement.getBoundingClientRect();
				const nextGeometry = {
					left: formatPercentValue(((rect.left - editableRect.left) / safeWidth) * 100),
					top: formatPercentValue(((rect.top - editableRect.top) / safeHeight) * 100),
					width: formatPercentValue((rect.width / safeWidth) * 100),
					height: formatPercentValue((rect.height / safeHeight) * 100),
				};

				if (
					widget.left !== nextGeometry.left ||
					widget.top !== nextGeometry.top ||
					widget.width !== nextGeometry.width ||
					widget.height !== nextGeometry.height
				) {
					changed = true;
					return {
						...widget,
						...nextGeometry,
					};
				}

				return widget;
			});

			if (!changed) {
				return;
			}

			setDashboard({
				...dashboard,
				widgets: sortWidgets(normalizedWidgets),
			});
		}, 0);

		return () => clearTimeout(timeout);
	}, [
		mode,
		isPercentageLayout,
		dashboard,
		dashboard?.widgets,
		layoutMeasureVersion,
		dims.width,
		dims.height,
		formatPercentValue,
		hasStoredPercentageGeometry,
		getPercentageReferenceMetrics,
		setDashboard,
	]);

	useEffect(() => {
		fixedWidthToggleHandlerRef.current = handleFixedWidthToggle;
	}, [handleFixedWidthToggle]);

	useEffect(() => {
		if (typeof onFixedWidthToggleHandlerReady !== 'function') {
			return undefined;
		}

		onFixedWidthToggleHandlerReady(externalFixedWidthToggleHandler);
		return () => {
			onFixedWidthToggleHandlerReady(null);
		};
	}, [externalFixedWidthToggleHandler, onFixedWidthToggleHandlerReady]);


	useEffect(() => {
		if (mode !== 'edit') {
			setFixedWidthGuide((prev) => (prev === null ? prev : null));
			return;
		}
		if (isFixedWidthMode) {
			setFixedWidthGuide((prev) => (prev === null ? prev : null));
			return;
		}

		const mainContainer = document.getElementById('web-dashboard-main-container');
		const dashboardElement = document.getElementById('diverted');
		const editable = document.getElementById('editable');
		const widgetsBox = document.querySelector('.widgets-box');

		if (!mainContainer || !dashboardElement || !editable || !widgetsBox) {
			setFixedWidthGuide((prev) => (prev === null ? prev : null));
			return;
		}

		const style = document.defaultView.getComputedStyle(dashboardElement);
		const gap = parseInt(style.gap) || 0;

		const editableStyle = document.defaultView.getComputedStyle(editable);
		const paddingLeft = parseInt(editableStyle.paddingLeft) || 0;
		const paddingRight = parseInt(editableStyle.paddingRight) || 0;
		const totalHorizontalPadding = paddingLeft + paddingRight;

		const mainContainerGap = 8;
		const widgetsBoxActualWidth = widgetsBox ? widgetsBox.offsetWidth : 0;
		const rememberedWidgetsBoxWidth = widgetsBoxActualWidth + mainContainerGap;
		if (rememberedWidgetsBoxWidth !== lastWidgetsBoxWidth) setLastWidgetsBoxWidth(rememberedWidgetsBoxWidth);
		const rememberedTotalWidth = mainContainer.offsetWidth - mainContainerGap;
		if (rememberedTotalWidth !== fixedViewWidth) setFixedViewWidth(rememberedTotalWidth);
		const actualMainContainerWidth = mainContainer.offsetWidth;
		const availableWidthForDiverted = Math.max(
			1,
			actualMainContainerWidth - widgetsBoxActualWidth - mainContainerGap
		);
		let fixedWidthPx;
		if (isPercentageLayout) {
			fixedWidthPx = availableWidthForDiverted;
		} else {
			const minGridColsForDefaultWidget = Math.ceil(defaultWidth / (defaultDivWidth + gap));
			let calculatedColumnsForFixed = Math.max(
				1,
				minGridColsForDefaultWidget,
				Math.floor((availableWidthForDiverted - totalHorizontalPadding) / (defaultDivWidth + gap))
			);
			calculatedColumnsForFixed = Math.max(1, calculatedColumnsForFixed);

			fixedWidthPx = calculatedColumnsForFixed * (defaultDivWidth + gap) + totalHorizontalPadding;
			fixedWidthPx = Math.max(fixedWidthPx, defaultWidth + totalHorizontalPadding);
			const fixedCanvasTargetWidth = availableWidthForDiverted;
			fixedWidthPx = Math.max(fixedWidthPx, fixedCanvasTargetWidth);
		}

		const fixedHeightPx = getElementViewportHeight(dashboardElement) || editable.offsetHeight || 0;

		if (!Number.isFinite(fixedWidthPx) || fixedWidthPx <= 0) {
			setFixedWidthGuide((prev) => (prev === null ? prev : null));
			return;
		}

		const nextGuide = { width: fixedWidthPx, height: fixedHeightPx };
		setFixedWidthGuide((prev) => (sameMeasuredSize(prev, nextGuide) ? prev : nextGuide));
	}, [
		mode,
		isFixedWidthMode,
		dims.width,
		dims.height,
		defaultDivWidth,
		defaultDivHeight,
		defaultWidth,
		defaultHeight,
		dashboard?.widgets,
		fixedViewWidth,
		isPercentageLayout,
		lastWidgetsBoxWidth,
		layoutMeasureVersion,
	]);

	useEffect(() => {
		if (mode === 'edit' || !isPercentageLayout) {
			setViewPercentageCanvas((prev) => {
				if ((prev?.width || 0) === 0 && (prev?.height || 0) === 0) return prev;
				return { width: 0, height: 0 };
			});
			return;
		}

		const timeout = setTimeout(() => {
			const dashboardElement = document.getElementById('diverted-view');
			if (!dashboardElement) return;

			const viewportWidth = Math.max(1, dashboardElement.clientWidth);
			const viewportHeight = Math.max(1, dashboardElement.clientHeight);

			const nextWidth = viewportWidth;
			const nextHeight = viewportHeight;

			setViewPercentageCanvas((prev) => {
				const prevWidth = prev?.width || 0;
				const prevHeight = prev?.height || 0;
				if (
					Math.abs(prevWidth - nextWidth) < 1 &&
					Math.abs(prevHeight - nextHeight) < 1
				) {
					return prev;
				}
				return { width: nextWidth, height: nextHeight };
			});
		}, 0);

		return () => clearTimeout(timeout);
	}, [
		mode,
		isPercentageLayout,
		dashboard?.widgets,
		isFixedWidthMode,
		fixedViewWidth,
		defaultDivWidth,
		defaultDivHeight,
		defaultWidth,
		defaultHeight,
		dims.width,
		dims.height,
		getWidgetsCanvasBoundsPx,
	]);

	const editableWaterNetworkState = useMemo(() => {
		if (mode !== 'edit') {
			return null;
		}

		const overlayMetricsVersion = layoutMeasureVersion + lastWidgetsBoxWidth + (isPercentageLayout ? 1 : 0);
		const metrics = getEditableMetrics();
		if (!metrics || overlayMetricsVersion < 0) {
			return null;
		}

		const connections = buildWaterNetworkConnections(
			dashboard?.widgets || [],
			(widget) => getWidgetRectPx(widget, metrics),
			metrics,
		);

		return {
			width: metrics.width,
			height: metrics.height,
			connections,
		};
	}, [
		mode,
		dashboard?.widgets,
		getEditableMetrics,
		getWidgetRectPx,
		layoutMeasureVersion,
		lastWidgetsBoxWidth,
		isPercentageLayout,
	]);

	const viewWaterNetworkState = useMemo(() => {
		if (mode === 'edit') {
			return null;
		}

		const widgetsToMeasure = dashboard?.widgets || [];
		if (widgetsToMeasure.length === 0) {
			return null;
		}

		const canvasBounds = widgetsToMeasure.reduce((acc, widget) => {
			const rect = getViewWidgetRectPx(widget);
			return {
				maxRight: Math.max(acc.maxRight, rect.left + rect.width),
				maxBottom: Math.max(acc.maxBottom, rect.top + rect.height),
			};
		}, {
			maxRight: isPercentageLayout ? (viewPercentageCanvas?.width || 0) : 0,
			maxBottom: isPercentageLayout ? (viewPercentageCanvas?.height || 0) : 0,
		});

		const connections = buildWaterNetworkConnections(
			widgetsToMeasure,
			getViewWidgetRectPx,
			null,
		);

		return {
			width: canvasBounds.maxRight,
			height: canvasBounds.maxBottom,
			connections,
		};
	}, [
		mode,
		dashboard?.widgets,
		getViewWidgetRectPx,
		isPercentageLayout,
		viewPercentageCanvas?.height,
		viewPercentageCanvas?.width,
	]);

	useEffect(() => {
		const timeout = setTimeout(() => {
			if (mode === 'edit') {
				const mainContainer = document.getElementById('web-dashboard-main-container');
				const dashboardElement = document.getElementById('diverted');
				const editable = document.getElementById('editable');
				const widgetsBox = document.querySelector('.widgets-box');


				if (!mainContainer || !dashboardElement || !editable) return;

				const style = document.defaultView.getComputedStyle(dashboardElement);
				const gap = parseInt(style.gap) || 0;
				const editableStyle = document.defaultView.getComputedStyle(editable);
				const paddingLeft = parseInt(editableStyle.paddingLeft) || 0;
				const paddingRight = parseInt(editableStyle.paddingRight) || 0;
				const paddingTop = parseInt(editableStyle.paddingTop) || 0;
				const paddingBottom = parseInt(editableStyle.paddingBottom) || 0;
				const totalHorizontalPadding = paddingLeft + paddingRight;
				const totalVerticalPadding = paddingTop + paddingBottom;
				const gridColumnUnit = Math.max(1, defaultDivWidth + gap);
				const gridRowUnit = Math.max(1, defaultDivHeight + gap);

				let calculatedColumns;
				let calculatedRows;
				let currentEditableWidthPx;
				let currentEditableHeightPx;

				const minGridColsForDefaultWidget = Math.ceil(defaultWidth / (defaultDivWidth + gap));
				const minGridRowsForDefaultWidget = Math.ceil(defaultHeight / (defaultDivHeight + gap));

				const mainContainerGap = 8;
				const widgetsBoxActualWidth = widgetsBox ? widgetsBox.offsetWidth : 0;
				const actualMainContainerWidth = mainContainer.offsetWidth;
				const availableWidthForDiverted = Math.max(
					1,
					actualMainContainerWidth - widgetsBoxActualWidth - mainContainerGap,
				);
				const availableHeightForDiverted = getElementViewportHeight(mainContainer);
				const fixedCanvasTargetWidth = availableWidthForDiverted;
				const {
					maxRight: maxWidgetRight,
					maxBottom: maxWidgetBottom,
				} = getWidgetsCanvasBoundsPx(dashboard?.widgets || []);
				const percentageVerticalBuffer = getCanvasBottomBufferPx({
					viewportHeight: availableHeightForDiverted,
					defaultHeight,
					rowUnit: gridRowUnit,
				});
				const percentageMinimumVisibleHeight = Math.max(1, availableHeightForDiverted);
				let dashboardViewportHeightPx = availableHeightForDiverted;

				if (isFixedWidthMode) {
					if (isPercentageLayout) {
						calculatedColumns = 0;
						calculatedRows = 0;

						currentEditableWidthPx = Math.max(1, availableWidthForDiverted);
						currentEditableHeightPx = Math.max(
							percentageMinimumVisibleHeight,
							defaultHeight + totalVerticalPadding,
							maxWidgetBottom + totalVerticalPadding + gap + percentageVerticalBuffer,
						);
						dashboardViewportHeightPx = percentageMinimumVisibleHeight;

						dashboardElement.style.overflowX = 'hidden';
						dashboardElement.style.overflowY = 'auto';
						setElementPixelStyle(dashboardElement, 'width', availableWidthForDiverted);
						setElementPixelStyle(dashboardElement, 'height', dashboardViewportHeightPx, 'important');
						dashboardElement.style.flexGrow = 1;
						dashboardElement.style.flexShrink = 1;
						dashboardElement.style.justifyContent = 'flex-start';
						dashboardElement.style.alignContent = 'flex-start';
					} else {
						calculatedColumns = Math.max(
							1,
							minGridColsForDefaultWidget,
							Math.floor((availableWidthForDiverted - totalHorizontalPadding) / (defaultDivWidth + gap))
						);

						calculatedColumns = Math.max(1, calculatedColumns);


						currentEditableWidthPx = calculatedColumns * (defaultDivWidth + gap) + totalHorizontalPadding;
						currentEditableWidthPx = Math.max(currentEditableWidthPx, defaultWidth + totalHorizontalPadding);
						currentEditableWidthPx = Math.max(currentEditableWidthPx, fixedCanvasTargetWidth);


						calculatedColumns = Math.max(
							1,
							minGridColsForDefaultWidget,
							Math.floor((currentEditableWidthPx - totalHorizontalPadding + gap) / (defaultDivWidth + gap))
						);
						calculatedColumns = Math.max(1, calculatedColumns);
						dashboardElement.style.overflowX = 'hidden';
						dashboardElement.style.overflowY = 'auto';

						const { maxBottom: maxWidgetBottom } = getWidgetsCanvasBoundsPx(dashboard?.widgets || []);
						calculatedRows = Math.max(
							minGridRowsForDefaultWidget,
							Math.floor((availableHeightForDiverted - totalVerticalPadding) / gridRowUnit),
							Math.ceil((maxWidgetBottom + gap) / gridRowUnit) + EDITABLE_TRAILING_ROWS
						);
						calculatedRows = Math.max(1, calculatedRows);

						currentEditableHeightPx = calculatedRows * gridRowUnit + totalVerticalPadding;
						currentEditableHeightPx = Math.max(
							currentEditableHeightPx,
							defaultHeight + totalVerticalPadding,
						);
						dashboardViewportHeightPx = Math.max(
							1,
							Math.min(currentEditableHeightPx, availableHeightForDiverted),
						);

						setElementPixelStyle(dashboardElement, 'height', dashboardViewportHeightPx, 'important');
						setElementPixelStyle(dashboardElement, 'width', availableWidthForDiverted);
						dashboardElement.style.flexGrow = 1;
						dashboardElement.style.flexShrink = 1;
						dashboardElement.style.justifyContent = 'flex-start';
						dashboardElement.style.alignContent = 'flex-start';
					}
				} else {
					dashboardElement.style.removeProperty('flex-grow');
					dashboardElement.style.removeProperty('flex-shrink');
					dashboardElement.style.removeProperty('justify-content');
					dashboardElement.style.removeProperty('align-content');

					const viewportWidth = availableWidthForDiverted;
					const viewportHeight = availableHeightForDiverted;

					if (isPercentageLayout) {
						calculatedColumns = 0;
						calculatedRows = 0;

						const percentageViewportWidth = Math.max(1, availableWidthForDiverted);

						currentEditableWidthPx = percentageViewportWidth;
						currentEditableHeightPx = Math.max(
							percentageMinimumVisibleHeight,
							defaultHeight + totalVerticalPadding,
							maxWidgetBottom + totalVerticalPadding + gap + percentageVerticalBuffer,
						);
						dashboardViewportHeightPx = percentageMinimumVisibleHeight;

						dashboardElement.style.overflowX = 'hidden';
						dashboardElement.style.overflowY = 'auto';
						setElementPixelStyle(dashboardElement, 'width', percentageViewportWidth);
						setElementPixelStyle(dashboardElement, 'height', dashboardViewportHeightPx, 'important');
						dashboardElement.style.flexGrow = 1;
						dashboardElement.style.flexShrink = 1;
						dashboardElement.style.justifyContent = 'flex-start';
						dashboardElement.style.alignContent = 'flex-start';
					} else {
						calculatedColumns = Math.max(
							1,
							minGridColsForDefaultWidget,
							Math.floor((viewportWidth - totalHorizontalPadding) / gridColumnUnit),
							Math.ceil(maxWidgetRight / gridColumnUnit) + EDITABLE_TRAILING_ROWS
						);
						calculatedColumns = Math.max(1, calculatedColumns);

						calculatedRows = Math.max(
							1,
							minGridRowsForDefaultWidget,
							Math.floor((viewportHeight - totalVerticalPadding) / gridRowUnit),
							Math.ceil(maxWidgetBottom / gridRowUnit) + EDITABLE_TRAILING_ROWS
						);
						calculatedRows = Math.max(1, calculatedRows);

						currentEditableWidthPx = calculatedColumns * gridColumnUnit + totalHorizontalPadding;
						currentEditableWidthPx = Math.max(currentEditableWidthPx, maxWidgetRight + totalHorizontalPadding + gap);
						currentEditableWidthPx = Math.max(currentEditableWidthPx, defaultWidth + totalHorizontalPadding);

						currentEditableHeightPx = calculatedRows * gridRowUnit + totalVerticalPadding;
						currentEditableHeightPx = Math.max(currentEditableHeightPx, maxWidgetBottom + totalVerticalPadding + gap);
						currentEditableHeightPx = Math.max(currentEditableHeightPx, defaultHeight + totalVerticalPadding);
						dashboardViewportHeightPx = Math.max(
							1,
							Math.min(currentEditableHeightPx, availableHeightForDiverted),
						);


						dashboardElement.style.overflowX = 'auto';
						dashboardElement.style.overflowY = 'auto';
						setElementPixelStyle(dashboardElement, 'width', availableWidthForDiverted);
						setElementPixelStyle(dashboardElement, 'height', dashboardViewportHeightPx, 'important');
					}
				}

				if (isPercentageLayout) {
					const guideColumnUnit = Math.max(1, defaultDivWidth + gap);
					const guideRowUnit = Math.max(1, defaultDivHeight + gap);
					const renderedGuideHeight = Math.max(
						dashboardViewportHeightPx || 0,
						currentEditableHeightPx || 0,
					);
					const guideCanvasWidth = Math.max(
						1,
						currentEditableWidthPx || dashboardElement?.clientWidth || editable?.offsetWidth || availableWidthForDiverted,
					);
					const guideCanvasHeight = Math.max(
						defaultHeight + totalVerticalPadding,
						renderedGuideHeight,
					);

					calculatedColumns = Math.max(
						1,
						minGridColsForDefaultWidget,
						Math.ceil((guideCanvasWidth - totalHorizontalPadding + gap) / guideColumnUnit),
					);
					calculatedRows = Math.max(
						1,
						minGridRowsForDefaultWidget,
						Math.ceil((guideCanvasHeight - totalVerticalPadding + gap) / guideRowUnit),
					);
				}

				const guideViewportWidth = Math.max(
					1,
					dashboardElement?.clientWidth || availableWidthForDiverted,
				);
				const guideViewportOuterWidth = Math.max(
					guideViewportWidth,
					Math.round(dashboardElement?.getBoundingClientRect?.().width || 0),
					dashboardElement?.offsetWidth || 0,
				);
				const guideViewportHeight = Math.max(
					1,
					getElementViewportHeight(dashboardElement),
					dashboardViewportHeightPx || 0,
					availableHeightForDiverted,
				);
				const guideScrollLeft = Math.max(0, dashboardElement?.scrollLeft || 0);
				const guideScrollTop = Math.max(0, dashboardElement?.scrollTop || 0);
				const isFixedPixelLayout = isFixedWidthMode && !isPercentageLayout;
				const guideHorizontalBuffer = Math.max(
					gap,
					guideViewportOuterWidth - guideViewportWidth,
				);
				const guideCoverageWidth = isFixedPixelLayout
					? Math.max(1, availableWidthForDiverted)
					: Math.max(
						guideScrollLeft + guideViewportOuterWidth + guideHorizontalBuffer,
						currentEditableWidthPx || 0,
						availableWidthForDiverted,
					);
				const guideCoverageHeight = Math.max(
					guideScrollTop + guideViewportHeight,
					currentEditableHeightPx || 0,
					dashboardViewportHeightPx || 0,
					availableHeightForDiverted,
				);
				const visualGuideColumns = isFixedPixelLayout
					? Math.max(
						1,
						Math.floor((guideCoverageWidth - totalHorizontalPadding + gap) / gridColumnUnit),
					)
					: Math.max(
						1,
						Math.ceil((guideCoverageWidth - totalHorizontalPadding + gap) / gridColumnUnit) + 1,
					);
				const visualGuideRows = Math.max(
					1,
					Math.ceil((guideCoverageHeight - totalVerticalPadding + gap) / gridRowUnit),
				);

				if (dashboardElement && visualGuideColumns > 0) {
					dashboardElement.style.gridTemplateColumns = `repeat(${visualGuideColumns}, ${defaultDivWidth}px)`;
					dashboardElement.style.gridAutoRows = `${defaultDivHeight}px`;
				} else if (dashboardElement) {
					dashboardElement.style.removeProperty('grid-template-columns');
					dashboardElement.style.removeProperty('grid-auto-rows');
				}

				// Keep visual diversion guides, but cap node count to avoid render lag.
				const maxDiversionNodes = 12000;
				const safeColumns = Math.max(1, visualGuideColumns);
				const maxRowsByCap = Math.max(1, Math.floor(maxDiversionNodes / safeColumns));
				const rowsForGuides = Math.max(1, Math.min(visualGuideRows, maxRowsByCap));
				const totalGuides = Math.min(maxDiversionNodes, safeColumns * rowsForGuides);

				const diversions = [];
				for (let i = 0; i < totalGuides; i++) {
					diversions.push(
						<div
							key={`diversion-${i}`}
							className='divertion'
							style={{
								width: defaultDivWidth,
								height: defaultDivHeight,
								borderRadius: '9px',
								backgroundColor: 'var(--diveration-bg)'
							}}
						/>
					);
				}

				if (editable) {
					editable.style.width = `${currentEditableWidthPx}px`;
					editable.style.height = `${currentEditableHeightPx}px`;
				}

				setDivs((prev) => {
					const prevWidth = prev?.[0]?.props?.style?.width;
					const prevHeight = prev?.[0]?.props?.style?.height;
					if (
						prev.length === diversions.length &&
						prevWidth === defaultDivWidth &&
						prevHeight === defaultDivHeight
					) {
						return prev;
					}
					return diversions;
				});
			} else {
				setDivs((prev) => (prev.length === 0 ? prev : []));
			}
			prevFixedWidthRef.current = dashboard?.fixedWidth;

		}, 0);

		return () => clearTimeout(timeout);
	}, [
		dims.height,
		dims.width,
		mode,
		isFixedWidthMode,
		dashboard?.fixedWidth,
		dashboard?.widgets,
		defaultDivWidth,
		defaultDivHeight,
		defaultWidth,
		defaultHeight,
		fixedViewWidth,
		getWidgetsCanvasBoundsPx,
		isPercentageLayout,
		lastWidgetsBoxWidth,
		layoutMeasureVersion,
	]);

	const legacyEnvironmentalWidgetWidth = defaultWidth * 2;
	const legacyEnvironmentalWidgetHeight = defaultHeight * 2;
	const normalizedEnvironmentalWidgetWidth = getSpannedWidgetSize(defaultWidth, 2);
	const normalizedEnvironmentalWidgetHeight = getSpannedWidgetSize(defaultHeight, 2);

	useEffect(() => {
		if (!dashboard || !Array.isArray(dashboard.widgets) || typeof setDashboard !== 'function') {
			return;
		}

		const parseDimension = (value) => {
			if (typeof value === 'number' && Number.isFinite(value)) return value;
			if (typeof value === 'string') {
				const parsed = parseFloat(value);
				return Number.isFinite(parsed) ? parsed : null;
			}
			return null;
		};

		let hasLegacyEnvironmentalWidget = false;
		const normalizedWidgets = dashboard.widgets.map((widget) => {
			if (widget?.type !== ENVIRONMENTAL_STATUS_WIDGET_TYPE) {
				return widget;
			}

			const currentWidth = parseDimension(widget?.width);
			const currentHeight = parseDimension(widget?.height);
			if (
				currentWidth == null ||
				currentHeight == null ||
				Math.abs(currentWidth - legacyEnvironmentalWidgetWidth) > 0.5 ||
				Math.abs(currentHeight - legacyEnvironmentalWidgetHeight) > 0.5
			) {
				return widget;
			}

			hasLegacyEnvironmentalWidget = true;
			return {
				...widget,
				width: normalizedEnvironmentalWidgetWidth,
				height: normalizedEnvironmentalWidgetHeight,
			};
		});

		if (!hasLegacyEnvironmentalWidget) {
			return;
		}

		setDashboard({
			...dashboard,
			widgets: normalizedWidgets,
		});
	}, [
		dashboard,
		legacyEnvironmentalWidgetHeight,
		legacyEnvironmentalWidgetWidth,
		normalizedEnvironmentalWidgetHeight,
		normalizedEnvironmentalWidgetWidth,
		setDashboard,
	]);

	useEffect(() => {
		if (mode !== 'edit' || !multiSelectEnabled) {
			clearMultiSelection();
		}
	}, [clearMultiSelection, mode, multiSelectEnabled]);

	useEffect(() => {
		if (typeof onMultiSelectStateChange === 'function') {
			onMultiSelectStateChange(multiSelectEnabled);
		}
	}, [multiSelectEnabled, onMultiSelectStateChange]);

	useEffect(() => {
		const availableIds = new Set((dashboard?.widgets || []).map((widget) => String(widget.id)));
		setSelectedWidgetIds((currentIds) => {
			const filteredIds = currentIds.filter((id) => availableIds.has(String(id)));
			return filteredIds.length === currentIds.length ? currentIds : filteredIds;
		});
	}, [dashboard?.widgets]);

	const selectedWidgetIdSet = useMemo(
		() => new Set((selectedWidgetIds || []).map((id) => String(id))),
		[selectedWidgetIds],
	);

	const selectedWidgetBounds = useMemo(() => {
		if (mode !== 'edit' || !multiSelectEnabled || selectedWidgetIds.length === 0) {
			return null;
		}

		const metrics = getEditableMetrics();
		if (!metrics) return null;

		const rects = (dashboard?.widgets || [])
			.filter((widget) => selectedWidgetIdSet.has(String(widget.id)))
			.map((widget) => getWidgetRectPx(widget, metrics));

		return getRectBounds(rects);
	}, [
		dashboard?.widgets,
		getEditableMetrics,
		getWidgetRectPx,
		mode,
		multiSelectEnabled,
		selectedWidgetIdSet,
		selectedWidgetIds.length,
	]);

	const handleMultiSelectToggle = useCallback((checked) => {
		setMultiSelectEnabled((currentValue) => (currentValue === checked ? currentValue : checked));
		if (!checked) {
			clearMultiSelection();
		}
	}, [clearMultiSelection]);

	useEffect(() => {
		multiSelectToggleHandlerRef.current = handleMultiSelectToggle;
	}, [handleMultiSelectToggle]);

	useEffect(() => {
		if (typeof onMultiSelectToggleHandlerReady !== 'function') {
			return undefined;
		}

		onMultiSelectToggleHandlerReady(externalMultiSelectToggleHandler);
		return () => {
			onMultiSelectToggleHandlerReady(null);
		};
	}, [externalMultiSelectToggleHandler, onMultiSelectToggleHandlerReady]);

	const getSelectedGroupMoveContext = useCallback(() => {
		const metrics = getEditableMetrics();
		const divertedElement = document.getElementById('diverted');
		if (!metrics || !divertedElement || selectedWidgetIds.length === 0) return null;

		const divertedStyle = document.defaultView.getComputedStyle(divertedElement);
		const padLeft = parseInt(divertedStyle.paddingLeft, 10) || 0;
		const padRight = parseInt(divertedStyle.paddingRight, 10) || 0;
		const padTop = parseInt(divertedStyle.paddingTop, 10) || 0;
		const padBottom = parseInt(divertedStyle.paddingBottom, 10) || 0;
		const divertedRect = divertedElement.getBoundingClientRect();
		const scrollLeft = Math.max(0, divertedElement.scrollLeft || 0);
		const scrollTop = Math.max(0, divertedElement.scrollTop || 0);
		const effectiveWidth = Math.max(
			metrics.width || 0,
			scrollLeft + Math.max(1, divertedElement.clientWidth || Math.round(divertedRect.width || 0)),
		);
		const effectiveHeight = Math.max(
			metrics.height || 0,
			scrollTop + Math.max(1, divertedElement.clientHeight || Math.round(divertedRect.height || 0)),
		);
		const selectedIdSet = new Set(selectedWidgetIds.map((id) => String(id)));
		const selectedRects = (dashboard?.widgets || [])
			.filter((widget) => selectedIdSet.has(String(widget.id)))
			.map((widget) => ({
				widget,
				rect: getWidgetRectPx(widget, metrics),
			}));
		const bounds = getRectBounds(selectedRects.map((entry) => entry.rect));
		if (!bounds) return null;

		return {
			metrics,
			padLeft,
			padRight,
			padTop,
			padBottom,
			effectiveWidth,
			effectiveHeight,
			selectedIdSet,
			selectedRects,
			bounds,
		};
	}, [dashboard?.widgets, getEditableMetrics, getWidgetRectPx, selectedWidgetIds]);

	const clampSelectedGroupDelta = useCallback((deltaX, deltaY, context = getSelectedGroupMoveContext()) => {
		if (!context) return { x: 0, y: 0 };

		const minX = context.padLeft - context.bounds.left;
		const maxX = (context.effectiveWidth - context.padRight) - (context.bounds.left + context.bounds.width);
		const minY = context.padTop - context.bounds.top;
		const maxY = (context.effectiveHeight - context.padBottom) - (context.bounds.top + context.bounds.height);

		return {
			x: Math.max(minX, Math.min(deltaX, maxX)),
			y: Math.max(minY, Math.min(deltaY, maxY)),
		};
	}, [getSelectedGroupMoveContext]);

	const commitSelectedGroupMove = useCallback((deltaX, deltaY) => {
		const context = getSelectedGroupMoveContext();
		if (!context) return false;

		const clampedDelta = clampSelectedGroupDelta(deltaX, deltaY, context);
		const unselectedWidgets = (dashboard?.widgets || [])
			.filter((widget) => !context.selectedIdSet.has(String(widget.id)));
		const movedRectsById = new Map();
		let hasConflict = false;

		context.selectedRects.forEach(({ widget, rect }) => {
			if (hasConflict) return;

			const movedRect = {
				left: rect.left + clampedDelta.x,
				top: rect.top + clampedDelta.y,
				width: rect.width,
				height: rect.height,
			};

			const constrainedRect = constrainRectToBackgroundImage(
				movedRect.left,
				movedRect.top,
				movedRect.width,
				movedRect.height,
				dashboard?.widgets || [],
				widget.id,
			);

			if (
				constrainedRect.constrained &&
				(
					Math.abs(constrainedRect.left - movedRect.left) > 0.5 ||
					Math.abs(constrainedRect.top - movedRect.top) > 0.5
				)
			) {
				hasConflict = true;
				return;
			}

			if (
				widget.type !== 'background-image' &&
				widget.type !== 'box-widget' &&
				checkOverlap(
					movedRect.left,
					movedRect.top,
					movedRect.width,
					movedRect.height,
					unselectedWidgets,
					null,
					widget,
				)
			) {
				hasConflict = true;
				return;
			}

			movedRectsById.set(String(widget.id), movedRect);
		});

		if (hasConflict) {
			setAlertMessage(t.noSpaceForDuplication || 'No valid space available for the selected widgets.');
			setAlertType('warning');
			setShowAlert(true);
			return false;
		}

		const updatedWidgets = (dashboard?.widgets || []).map((widget) => {
			const movedRect = movedRectsById.get(String(widget.id));
			return movedRect ? storeWidgetPosition(widget, movedRect, context.metrics) : widget;
		});

		setDashboard({
			...dashboard,
			widgets: sortWidgets(updatedWidgets),
		});
		return true;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		checkOverlap,
		clampSelectedGroupDelta,
		constrainRectToBackgroundImage,
		dashboard,
		getSelectedGroupMoveContext,
		setDashboard,
		sortWidgets,
		storeWidgetPosition,
		t.noSpaceForDuplication,
	]);

	const startSelectedGroupDrag = useCallback((event) => {
		if (mode !== 'edit' || !multiSelectEnabled || selectedWidgetIds.length === 0 || event.button !== 0) {
			return false;
		}

		const context = getSelectedGroupMoveContext();
		if (!context) return false;

		selectedGroupDragRef.current = {
			startX: event.clientX,
			startY: event.clientY,
			context,
		};
		selectedGroupDragOffsetRef.current = { x: 0, y: 0 };
		suppressSelectionClickRef.current = true;
		setSelectedGroupDragOffset((currentOffset) => (
			sameOffset(currentOffset, { x: 0, y: 0 }) ? currentOffset : { x: 0, y: 0 }
		));

		const handlePointerMove = (moveEvent) => {
			const dragState = selectedGroupDragRef.current;
			if (!dragState) return;

			const rawDeltaX = moveEvent.clientX - dragState.startX;
			const rawDeltaY = moveEvent.clientY - dragState.startY;
			const nextOffset = clampSelectedGroupDelta(rawDeltaX, rawDeltaY, dragState.context);
			selectedGroupDragOffsetRef.current = nextOffset;
			setSelectedGroupDragOffset((currentOffset) => (
				sameOffset(currentOffset, nextOffset) ? currentOffset : nextOffset
			));
		};

		const handlePointerUp = () => {
			const dragState = selectedGroupDragRef.current;
			const finalOffset = selectedGroupDragOffsetRef.current;
			selectedGroupDragRef.current = null;
			selectedGroupDragOffsetRef.current = null;
			setSelectedGroupDragOffset((offset) => (offset === null ? offset : null));
			document.removeEventListener('pointermove', handlePointerMove);
			document.removeEventListener('pointerup', handlePointerUp);
			if (!dragState) return;
			if (finalOffset) {
				commitSelectedGroupMove(finalOffset.x, finalOffset.y);
			}
		};

		document.addEventListener('pointermove', handlePointerMove);
		document.addEventListener('pointerup', handlePointerUp);
		event.preventDefault();
		event.stopPropagation();
		return true;
	}, [
		clampSelectedGroupDelta,
		commitSelectedGroupMove,
		getSelectedGroupMoveContext,
		mode,
		multiSelectEnabled,
		selectedWidgetIds.length,
	]);

	const handleEditablePointerDown = useCallback((event) => {
		if (mode !== 'edit' || !multiSelectEnabled || event.button !== 0) return;
		const widgetElement = event.target?.closest?.('.widget');
		if (widgetElement) {
			if (
				selectedWidgetIdSet.has(String(widgetElement.id)) &&
				!event.target?.closest?.('.widget-buttons-container, .widget-resizer, .second-widget-resizer')
			) {
				startSelectedGroupDrag(event);
			}
			return;
		}

		const metrics = getEditableMetrics();
		const divertedElement = document.getElementById('diverted');
		if (!metrics || !divertedElement) return;

		const startX = event.clientX - metrics.divertedRect.left + metrics.scrollLeft;
		const startY = event.clientY - metrics.divertedRect.top + metrics.scrollTop;
		selectionDragRef.current = { startX, startY };
		setSelectionRect((currentRect) => {
			const nextRect = { left: startX, top: startY, width: 0, height: 0 };
			return sameRect(currentRect, nextRect) ? currentRect : nextRect;
		});
		setSelectedWidgetIds((currentIds) => (currentIds.length === 0 ? currentIds : []));

		const handlePointerMove = (moveEvent) => {
			if (!selectionDragRef.current) return;

			const currentMetrics = getEditableMetrics();
			if (!currentMetrics) return;

			const currentX = moveEvent.clientX - currentMetrics.divertedRect.left + currentMetrics.scrollLeft;
			const currentY = moveEvent.clientY - currentMetrics.divertedRect.top + currentMetrics.scrollTop;
			const nextRect = normalizeRect({
				left: selectionDragRef.current.startX,
				top: selectionDragRef.current.startY,
				width: currentX - selectionDragRef.current.startX,
				height: currentY - selectionDragRef.current.startY,
			});
			setSelectionRect((currentRect) => (sameRect(currentRect, nextRect) ? currentRect : nextRect));

			const nextSelectedIds = (dashboard?.widgets || [])
				.filter((widget) => rectsIntersect(getWidgetRectPx(widget, currentMetrics), nextRect))
				.map((widget) => widget.id);
			setSelectedWidgetIds((currentIds) => (
				sameIdList(currentIds, nextSelectedIds) ? currentIds : nextSelectedIds
			));
		};

		const handlePointerUp = () => {
			selectionDragRef.current = null;
			setSelectionRect(null);
			document.removeEventListener('pointermove', handlePointerMove);
			document.removeEventListener('pointerup', handlePointerUp);
		};

		document.addEventListener('pointermove', handlePointerMove);
		document.addEventListener('pointerup', handlePointerUp);
		event.preventDefault();
	}, [
		dashboard?.widgets,
		getEditableMetrics,
		getWidgetRectPx,
		mode,
		multiSelectEnabled,
		selectedWidgetIdSet,
		startSelectedGroupDrag,
	]);

	const handleEditableClickCapture = useCallback((event) => {
		if (mode !== 'edit' || !multiSelectEnabled) return;
		if (suppressSelectionClickRef.current) {
			suppressSelectionClickRef.current = false;
			event.preventDefault();
			event.stopPropagation();
			return;
		}
		const widgetElement = event.target?.closest?.('.widget');
		if (!widgetElement || !document.getElementById('editable')?.contains(widgetElement)) return;
		if (event.target?.closest?.('.widget-buttons-container, .widget-resizer, .second-widget-resizer')) return;

		const widgetId = widgetElement.id;
		if (!widgetId) return;

		setSelectedWidgetIds((currentIds) => {
			const alreadySelected = currentIds.some((id) => String(id) === String(widgetId));
			if (event.shiftKey || event.ctrlKey || event.metaKey) {
				return alreadySelected
					? currentIds.filter((id) => String(id) !== String(widgetId))
					: [...currentIds, widgetId];
			}
			return alreadySelected && currentIds.length === 1 ? currentIds : [widgetId];
		});
	}, [mode, multiSelectEnabled]);


	const handleDashboardDragOver = useCallback((e) => {
		e.preventDefault();
		if (e.dataTransfer) {
			e.dataTransfer.dropEffect = 'move';
		}
	}, []);

	const drop = (e) => {
		e.preventDefault();
		e.stopPropagation();

		const data = e.dataTransfer.getData('text/plain').split(';');
		if (data.length < 5) return;

		const type = data[0];
		const wid = data[1];
		const parsedOffsetX = Number(data[2]);
		const parsedOffsetY = Number(data[3]);
		const action = data[4];

		const mouseX = e.clientX;
		const mouseY = e.clientY;

		const dashBoardElement = document.querySelector('.editable-dashboard');
		const divertedElement = document.getElementById('diverted');
		if (!dashBoardElement || !divertedElement) return;
		const divertedRect = divertedElement.getBoundingClientRect();
		const metrics = getEditableMetrics();
		if (!metrics) return;

		const defaultWidgetDimensions = getDefaultWidgetDimensions(type, defaultWidth, defaultHeight);
		let widgetWidth = defaultWidgetDimensions.width;
		let widgetHeight = defaultWidgetDimensions.height;

		if (action === 'move') {
			const existingWidget = (dashboard?.widgets || []).find(w => w.id === wid);
			if (existingWidget) {
				const existingWidgetRect = getWidgetRectPx(existingWidget, metrics);
				widgetWidth = existingWidgetRect.width;
				widgetHeight = existingWidgetRect.height;
			}
		}

		const currentWidgets = dashboard?.widgets || [];
		const draggedWidget = action === 'move'
			? currentWidgets.find((widget) => widget.id === wid)
			: { type, extra_props: propsFromType(type) };
		const minimumWidgetSize = getMinimumWidgetSizeForType(
			draggedWidget?.type || type,
			defaultDivWidth,
			defaultDivHeight,
		);

		let offsetX = Number.isFinite(parsedOffsetX) ? parsedOffsetX : 0;
		let offsetY = Number.isFinite(parsedOffsetY) ? parsedOffsetY : 0;
		const capturedOffset = dragPointerOffsetRef.current;
		if (capturedOffset && String(capturedOffset.id) === String(wid)) {
			if (Number.isFinite(capturedOffset.offsetX)) offsetX = capturedOffset.offsetX;
			if (Number.isFinite(capturedOffset.offsetY)) offsetY = capturedOffset.offsetY;
		}
		const maxOffsetX = Number.isFinite(widgetWidth) && widgetWidth > 0 ? widgetWidth : defaultDivWidth;
		const maxOffsetY = Number.isFinite(widgetHeight) && widgetHeight > 0 ? widgetHeight : defaultDivHeight;
		offsetX = Math.max(0, Math.min(offsetX, maxOffsetX));
		offsetY = Math.max(0, Math.min(offsetY, maxOffsetY));

		const intendedLeftGlobal = mouseX - offsetX;
		const intendedTopGlobal = mouseY - offsetY;

		const divertedStyle = divertedElement ? document.defaultView.getComputedStyle(divertedElement) : null;
		const gap = divertedStyle ? (parseInt(divertedStyle.gap, 10) || 0) : 0;
		const padLeft = divertedStyle ? (parseInt(divertedStyle.paddingLeft, 10) || 0) : 0;
		const padRight = divertedStyle ? (parseInt(divertedStyle.paddingRight, 10) || 0) : 0;
		const padTop = divertedStyle ? (parseInt(divertedStyle.paddingTop, 10) || 0) : 0;
		const padBottom = divertedStyle ? (parseInt(divertedStyle.paddingBottom, 10) || 0) : 0;
		const dropScrollLeft = Math.max(0, divertedElement.scrollLeft || 0);
		const dropScrollTop = Math.max(0, divertedElement.scrollTop || 0);
		const effectiveDropWidth = Math.max(
			metrics.width || 0,
			dropScrollLeft + Math.max(1, divertedElement.clientWidth || Math.round(divertedRect.width || 0)),
		);
		const effectiveDropHeight = Math.max(
			metrics.height || 0,
			dropScrollTop + Math.max(1, divertedElement.clientHeight || Math.round(divertedRect.height || 0)),
		);
		widgetWidth = Math.max(minimumWidgetSize.width, widgetWidth);
		widgetHeight = Math.max(minimumWidgetSize.height, widgetHeight);
		widgetWidth = Math.min(widgetWidth, Math.max(minimumWidgetSize.width, effectiveDropWidth - padLeft - padRight));
		widgetHeight = Math.min(widgetHeight, Math.max(minimumWidgetSize.height, effectiveDropHeight - padTop - padBottom));
		const gridColumnSize = defaultDivWidth + gap;
		const gridRowSize = defaultDivHeight + gap;
		const minLeft = padLeft;
		const maxLeft = Math.max(minLeft, effectiveDropWidth - padRight - widgetWidth);
		const minTop = padTop;
		const maxTop = Math.max(minTop, effectiveDropHeight - padBottom - widgetHeight);
		const usableWidth = Math.max(0, effectiveDropWidth - padLeft - padRight);
		const usableHeight = Math.max(0, effectiveDropHeight - padTop - padBottom);

		let initialDroppedLeftRelative = intendedLeftGlobal - divertedRect.left + dropScrollLeft;
		let initialDroppedTopRelative = intendedTopGlobal - divertedRect.top + dropScrollTop;

		const isFreeformLayoutWidget = isWidgetFreeMovementEnabled(draggedWidget);
		const snappedToX = minLeft + (
			Math.round((initialDroppedLeftRelative - minLeft) / gridColumnSize) * gridColumnSize
		);
		const snappedToY = minTop + (
			Math.round((initialDroppedTopRelative - minTop) / gridRowSize) * gridRowSize
		);

		let finalDroppedLeft = isFreeformLayoutWidget ? initialDroppedLeftRelative : snappedToX;
		let finalDroppedTop = isFreeformLayoutWidget ? initialDroppedTopRelative : snappedToY;

		finalDroppedLeft = Math.max(minLeft, Math.min(finalDroppedLeft, maxLeft));
		finalDroppedTop = Math.max(minTop, Math.min(finalDroppedTop, maxTop));

		if (isFreeformLayoutWidget && isWaterNetworkWidgetType(draggedWidget) && isWaterNetworkLinkEnabled(draggedWidget)) {
			const snappedWaterRect = snapWaterNetworkMoveRect(
				{
					left: finalDroppedLeft,
					top: finalDroppedTop,
					width: widgetWidth,
					height: widgetHeight,
				},
				draggedWidget,
				currentWidgets,
				getWidgetRectPx,
				metrics,
				action === 'move' ? wid : null,
			);
			finalDroppedLeft = Math.max(minLeft, Math.min(snappedWaterRect.left, maxLeft));
			finalDroppedTop = Math.max(minTop, Math.min(snappedWaterRect.top, maxTop));
		}

		let foundValidSpot = false;

		const isInitialSpotValid = (
			finalDroppedLeft >= minLeft &&
			finalDroppedTop >= minTop &&
			finalDroppedLeft + widgetWidth <= effectiveDropWidth - padRight &&
			finalDroppedTop + widgetHeight <= effectiveDropHeight - padBottom &&
			!checkOverlap(finalDroppedLeft, finalDroppedTop, widgetWidth, widgetHeight, currentWidgets, wid, draggedWidget)
		);

		if (type === 'background-image' || type === 'box-widget' || isInitialSpotValid) {
			foundValidSpot = true;
		} else {
			let bestCandidateX = null;
			let bestCandidateY = null;
			let minDistance = Infinity;
			const horizontalSearchStep = isFreeformLayoutWidget ? WATER_NETWORK_FREEFORM_SEARCH_STEP : gridColumnSize;
			const verticalSearchStep = isFreeformLayoutWidget ? WATER_NETWORK_FREEFORM_SEARCH_STEP : gridRowSize;
			const maxCols = Math.max(0, Math.floor((usableWidth - widgetWidth) / Math.max(1, horizontalSearchStep)) + 1);
			const maxRows = Math.max(0, Math.floor((usableHeight - widgetHeight) / Math.max(1, verticalSearchStep)) + 1);

			for (let row = 0; row < maxRows; row++) {
				for (let col = 0; col < maxCols; col++) {
					const testLeft = minLeft + (col * horizontalSearchStep);
					const testTop = minTop + (row * verticalSearchStep);

					if (
						testLeft < minLeft ||
						testTop < minTop ||
						testLeft + widgetWidth > effectiveDropWidth - padRight ||
						testTop + widgetHeight > effectiveDropHeight - padBottom
					) {
						continue;
					}
					if (checkOverlap(testLeft, testTop, widgetWidth, widgetHeight, currentWidgets, wid, draggedWidget)) {
						continue;
					}

					const dx = initialDroppedLeftRelative - testLeft;
					const dy = initialDroppedTopRelative - testTop;
					const distance = (dx * dx) + (dy * dy);
					if (distance < minDistance) {
						minDistance = distance;
						bestCandidateX = testLeft;
						bestCandidateY = testTop;
						foundValidSpot = true;
					}
				}
			}

			if (foundValidSpot) {
				finalDroppedLeft = bestCandidateX;
				finalDroppedTop = bestCandidateY;
				if (isFreeformLayoutWidget && isWaterNetworkWidgetType(draggedWidget) && isWaterNetworkLinkEnabled(draggedWidget)) {
					const snappedFallbackWaterRect = snapWaterNetworkMoveRect(
						{
							left: finalDroppedLeft,
							top: finalDroppedTop,
							width: widgetWidth,
							height: widgetHeight,
						},
						draggedWidget,
						currentWidgets,
						getWidgetRectPx,
						metrics,
						action === 'move' ? wid : null,
					);
					finalDroppedLeft = Math.max(minLeft, Math.min(snappedFallbackWaterRect.left, maxLeft));
					finalDroppedTop = Math.max(minTop, Math.min(snappedFallbackWaterRect.top, maxTop));
				}
			}
		}

		if (!foundValidSpot && type !== 'background-image' && type !== 'box-widget') {
			setAlertMessage(t.noSpaceForDuplication || 'No valid space available for this widget.');
			setAlertType('warning');
			setShowAlert(true);
			return;
		}

		if (action === 'move') {
			const shouldMoveSelectedGroup = (
				mode === 'edit' &&
				multiSelectEnabled &&
				selectedWidgetIdSet.has(String(wid)) &&
				selectedWidgetIds.length > 1
			);

			if (shouldMoveSelectedGroup) {
				const selectedIdSet = new Set(selectedWidgetIds.map((id) => String(id)));
				const selectedWidgets = currentWidgets.filter((widget) => selectedIdSet.has(String(widget.id)));
				const selectedRects = selectedWidgets.map((widget) => ({
					widget,
					rect: getWidgetRectPx(widget, metrics),
				}));
				const draggedRect = selectedRects.find((entry) => String(entry.widget.id) === String(wid))?.rect;
				const selectedBounds = getRectBounds(selectedRects.map((entry) => entry.rect));

				if (!draggedRect || !selectedBounds) {
					return;
				}

				let groupDeltaX = finalDroppedLeft - draggedRect.left;
				let groupDeltaY = finalDroppedTop - draggedRect.top;

				const groupMinLeft = padLeft - selectedBounds.left;
				const groupMaxLeft = (effectiveDropWidth - padRight) - (selectedBounds.left + selectedBounds.width);
				const groupMinTop = padTop - selectedBounds.top;
				const groupMaxTop = (effectiveDropHeight - padBottom) - (selectedBounds.top + selectedBounds.height);
				groupDeltaX = Math.max(groupMinLeft, Math.min(groupDeltaX, groupMaxLeft));
				groupDeltaY = Math.max(groupMinTop, Math.min(groupDeltaY, groupMaxTop));

				const unselectedWidgets = currentWidgets.filter((widget) => !selectedIdSet.has(String(widget.id)));
				const movedRectsById = new Map();
				let groupHasConflict = false;

				selectedRects.forEach(({ widget, rect }) => {
					if (groupHasConflict) return;

					const movedRect = {
						left: rect.left + groupDeltaX,
						top: rect.top + groupDeltaY,
						width: rect.width,
						height: rect.height,
					};

					if (
						movedRect.left < padLeft ||
						movedRect.top < padTop ||
						movedRect.left + movedRect.width > effectiveDropWidth - padRight ||
						movedRect.top + movedRect.height > effectiveDropHeight - padBottom
					) {
						groupHasConflict = true;
						return;
					}

					const constrainedRect = constrainRectToBackgroundImage(
						movedRect.left,
						movedRect.top,
						movedRect.width,
						movedRect.height,
						currentWidgets,
						widget.id,
					);

					if (
						constrainedRect.constrained &&
						(
							Math.abs(constrainedRect.left - movedRect.left) > 0.5 ||
							Math.abs(constrainedRect.top - movedRect.top) > 0.5
						)
					) {
						groupHasConflict = true;
						return;
					}

					if (
						widget.type !== 'background-image' &&
						widget.type !== 'box-widget' &&
						checkOverlap(
							movedRect.left,
							movedRect.top,
							movedRect.width,
							movedRect.height,
							unselectedWidgets,
							null,
							widget,
						)
					) {
						groupHasConflict = true;
						return;
					}

					movedRectsById.set(String(widget.id), movedRect);
				});

				if (groupHasConflict) {
					setAlertMessage(t.noSpaceForDuplication || 'No valid space available for the selected widgets.');
					setAlertType('warning');
					setShowAlert(true);
					return;
				}

				const updatedWidgets = currentWidgets.map((widget) => {
					const movedRect = movedRectsById.get(String(widget.id));
					return movedRect ? storeWidgetPosition(widget, movedRect, metrics) : widget;
				});

				setDashboard({
					...dashboard,
					widgets: sortWidgets(updatedWidgets),
				});
				return;
			}

			const constrainedInitialRect = constrainRectToBackgroundImage(
				initialDroppedLeftRelative,
				initialDroppedTopRelative,
				widgetWidth,
				widgetHeight,
				currentWidgets,
				wid,
			);

			if (constrainedInitialRect.constrained) {
				const hasOverlapAtConstrainedSpot = checkOverlap(
					constrainedInitialRect.left,
					constrainedInitialRect.top,
					constrainedInitialRect.width,
					constrainedInitialRect.height,
					currentWidgets,
					wid,
					draggedWidget,
				);

				if (hasOverlapAtConstrainedSpot) {
					const constrainedFallbackRect = constrainRectToBackgroundImage(
						finalDroppedLeft,
						finalDroppedTop,
						widgetWidth,
						widgetHeight,
						currentWidgets,
						wid,
					);
					const hasOverlapAtFallbackSpot = checkOverlap(
						constrainedFallbackRect.left,
						constrainedFallbackRect.top,
						constrainedFallbackRect.width,
						constrainedFallbackRect.height,
						currentWidgets,
						wid,
						draggedWidget,
					);

					if (hasOverlapAtFallbackSpot) {
						setAlertMessage(
							t.noValidSpaceOnBackgroundImage
							|| 'No valid space available inside the background image for this widget.'
						);
						setAlertType('warning');
						setShowAlert(true);
						return;
					}

					const nearestWidgets = currentWidgets.map(wd => wd.id === wid
						? storeWidgetPosition(wd, constrainedFallbackRect, metrics)
						: wd);

					setDashboard({
						...dashboard,
						widgets: sortWidgets(nearestWidgets),
					});
					return;
				}

				const updatedWidgets = currentWidgets.map(wd => wd.id === wid
					? storeWidgetPosition(wd, constrainedInitialRect, metrics)
					: wd);

				setDashboard({
					...dashboard,
					widgets: sortWidgets(updatedWidgets),
				});
				return;
			}

			const updatedWidgets = currentWidgets.map(wd => wd.id === wid
				? storeWidgetPosition(wd, {
					left: finalDroppedLeft,
					top: finalDroppedTop,
					width: widgetWidth,
					height: widgetHeight,
				}, metrics)
				: wd);

			setDashboard({
				...dashboard,
				widgets: sortWidgets(updatedWidgets)
			});
		} else if (action === 'add') {
			const constrainedAddRect = constrainRectToBackgroundImage(
				finalDroppedLeft,
				finalDroppedTop,
				widgetWidth,
				widgetHeight,
				currentWidgets,
			);
			const addRect = constrainedAddRect.constrained
				? constrainedAddRect
				: {
					left: finalDroppedLeft,
					top: finalDroppedTop,
					width: widgetWidth,
					height: widgetHeight,
				};
			const hasOverlapAtAddRect = checkOverlap(
				addRect.left,
				addRect.top,
				addRect.width,
				addRect.height,
				currentWidgets,
				null,
				draggedWidget,
			);
			if (
				hasOverlapAtAddRect
				&& type !== 'background-image'
				&& type !== 'box-widget'
			) {
				setAlertMessage(t.noSpaceForDuplication || 'No valid space available for this widget.');
				setAlertType('warning');
				setShowAlert(true);
				return;
			}

			const newWidget = storeWidgetPosition({
				id: type + Date.now(),
				data_id: 0,
				type: type,
				transparentBackground: false,
				title: type === 'time-range'
					? (t.widgetTitles?.timeRange || 'Digital Clock')
					: type === ENVIRONMENTAL_STATUS_WIDGET_TYPE
						? (t.widgetTitles?.environmentalStatus || 'Environmental Status')
					: type === PERIOD_COLUMN_WIDGET_TYPE
						? (t.widgetTitles?.periodColumn || 'Period Column')
					: type === 'liquid'
						? (t.widgetTitles?.liquid || 'Liquid Widget')
					: type === WATER_TANK_WIDGET_TYPE
						? (t.widgetTitles?.waterTank || 'Water Tank')
					: type === 'push-button'
						? (t.widgetTitles?.pushButton || 'Push Button')
						: type === 'water-pump'
							? (t.widgetTitles?.waterPump || 'PUMP Widget')
							: capitalize(type),
				extra_props: propsFromType(type),
				...(devices?.length > 0 && { device_id: 0 })
			}, addRect, metrics);

			const updatedWidgets = [...currentWidgets, newWidget];
			setDashboard({
				...dashboard,
				widgets: sortWidgets(updatedWidgets)
			});
		}
	};

	const affectedWidgetsCount = Number(removeBackgroundDialog?.affectedCount || 0);
	const removeBackgroundDialogMessage = affectedWidgetsCount > 0
		? (
			(t.removeBackgroundDialogMessage
				|| '{count} widgets overlap this background. Remove it and auto-reposition them?')
				.replace('{count}', String(affectedWidgetsCount))
		)
		: (t.removeBackgroundDialogMessageEmpty || 'Remove this background image?');

	return (
		<>
			{mode === 'edit' && (!externalFixedWidthControl || !externalMultiSelectControl) && (
				<div className="web-dashboard-edit-toggles">
					{!externalFixedWidthControl ? (
						<div className="web-dashboard-edit-toggle">
							<label className="form-lbl" style={{ color: 'var(--header-color)', padding: '4px 0px' }}>
								{t.fixedWidthDashboard}
							</label>

							<CustomSwitch
								checked={isFixedWidthMode}
								onChange={handleFixedWidthToggle}
							/>
						</div>
					) : null}

					{!externalMultiSelectControl ? (
					<div className="web-dashboard-edit-toggle">
						<label className="form-lbl" style={{ color: 'var(--header-color)', padding: '4px 0px' }}>
							{t.multiSelectWidgets || 'Multi Select'}
						</label>

						<CustomSwitch
							checked={multiSelectEnabled}
							onChange={handleMultiSelectToggle}
						/>
					</div>
					) : null}
				</div>
			)}

			<div id='web-dashboard-main-container' style={{
				width: maxWidth,
				height: maxHeight,
				display: 'flex',
				alignItems: 'flex-start',
				gap: '8px',
				marginTop: '8px',
				overflowX: 'hidden',
				background: 'var(--web-dashboard-main-container-bg)',
				direction: 'ltr',
			}}
				onDragStartCapture={rememberDragPointerOffset}
				onDragEndCapture={clearDragPointerOffset}
			>

				{edit.open ? <div id='modal'>
					<div id='popup'
						className="dashboard-modal widget-settings-modal tall"
						style={{ animationName: 'zoom' }}>
						<div className='dashboard-header'>
							<h2 style={{ textTransform: 'uppercase' }}>{edit.type} {t.settingsSuffix}</h2>
						</div>
						<div className='modal-content' style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
							{addDatastream.edit ?
								<EditDatastream state={{ ...addDatastream, mode: 'edit' }} setState={setAddDatastream}
									datastream={{
										id: getNextDatastreamId(validDatastreams),
										max: 0,
										min: 0,
										pin: '',
										name: '',
										sync: false,
										unit: '',
										alias: '',
										color: 'rgb(0, 0, 0)',
										isRow: false,
										action: false,
										reports: false,
										thenSet: 'no thing',
										dataType: 'String',
										decimals: '',
										condition: false,
										invalidate: false,
										customChart: false,
										defaultValue: 0,
										enableHistory: false,
										automationType: '',
										invalidateUnit: 'sec',
										invalidateValue: 0
									}} setDatastream={
										(newDatastream) => setDatastreams(datastreams ? [...datastreams, newDatastream] : [newDatastream])}
									templateDatastreams={validDatastreams}
									templateEvaluationData={templateEvaluationData}
									setTemplateEvaluationData={setTemplateEvaluationData}
								/> : null}
							<div style={{ flex: 1, width: '100%', height: '100%', overflowY: 'auto' }}>
								<div className='form-item'>
									{edit.type !== 'text-title' && (
										<>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>{t.titleLabel}</label>
											<textarea className='add-column' rows={3} placeholder={t.titleLabel}
												style={{ padding: '8px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)', resize: 'vertical' }}
												value={edit.title.replace(/^\[.*?\]\s*/, '')} onChange={(e) => {
													setEdit({ ...edit, title: e.target.value })
												}} />
										</>
									)}
									<div style={{ display: 'flex', gap: '16px', marginBottom: '10px', flexWrap: 'wrap' }}>
										{edit.type !== 'background-image' && edit.type !== 'box-widget' && (
											<div className="form-item" style={{ display: 'flex', gap: '8px' }}>
												<label
													className="form-lbl"
													style={{ color: 'var(--header-color)', padding: '4px 0px' }}
												>
													{t.settingsLabels?.showTitle || 'Show Title'}
												</label>
												<input
													className="checkbox-input"
													type="checkbox"
													checked={edit.props.showTitle !== false}
													onChange={(e) => {
														setEdit({
															...edit,
															props: { ...edit.props, showTitle: e.target.checked },
														});
													}}
												/>
											</div>
										)}

										{edit.type !== 'background-image' && edit.type !== 'box-widget' && (
											<div className="form-item" style={{ display: 'flex', gap: '8px' }}>
												<label
													className="form-lbl"
													style={{ color: 'var(--header-color)', padding: '4px 0px' }}
												>
													{t.transparentBackground}
												</label>
												<input
													className="checkbox-input"
													type="checkbox"
													checked={edit.transparentBackground ?? false}
													onChange={(e) => {
														setEdit({ ...edit, transparentBackground: e.target.checked });
													}}
												/>
											</div>
										)}

										<div className="form-item">
											<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.freeMovement || 'Free Movement In Dashboard'}
											</label>
											{editFreeMovementEnabled ? (
												<ToggleOn
													style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
													onClick={() => setEdit(prev => ({
														...prev,
														props: { ...(prev.props || {}), freeMovement: false }
													}))}
												/>
											) : (
												<ToggleOff
													style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
													onClick={() => setEdit(prev => ({
														...prev,
														props: { ...(prev.props || {}), freeMovement: true }
													}))}
												/>
											)}
										</div>

										{edit.type === 'terminal' && (
											<div className="form-item" style={{ display: 'flex', gap: '8px' }}>
												<label
													className="form-lbl"
													style={{ color: 'var(--header-color)', padding: '4px 0px' }}
												>
													{t.addNewLine}
												</label>
												<input
													className="checkbox-input"
													type="checkbox"
													checked={edit.props.newLine ?? false}
													onChange={(e) => {
														setEdit({ ...edit, props: { ...edit.props, newLine: e.target.checked } });
													}}
												/>
											</div>
										)}

										{edit.type !== 'background-image' ? fontSizeFields.map(({ key, label }) => (
											<div className="form-item" key={key} style={{ minWidth: '150px' }}>
												<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
													{label}
												</label>
												<input
													type="number"
													step="1"
													className="add-column"
													placeholder={t.settingsLabels?.auto || 'Auto'}
													value={edit.props?.[key] ?? ''}
													onBlur={() => handleFontSizeBlur(key)}
													onChange={(e) => handleFontSizeChange(key, e.target.value)}
												/>
											</div>
										)) : null}

										{(edit.type === 'slider' || edit.type === 'number') ? (
											<div className="form-item" style={{ minWidth: '180px' }}>
												<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
													{t.settingsLabels?.contentAlignment || 'Content Alignment'}
												</label>
												<Select
													classNamePrefix="react-select"
													options={SLIDER_NUMBER_CONTENT_ALIGNMENT_OPTIONS}
													value={SLIDER_NUMBER_CONTENT_ALIGNMENT_OPTIONS.find(
														option => option.value === (edit.props?.contentAlignment || 'auto')
													) || SLIDER_NUMBER_CONTENT_ALIGNMENT_OPTIONS[0]}
													onChange={selectedOption => setEdit(prev => ({
														...prev,
														props: {
															...(prev.props || {}),
															contentAlignment: selectedOption ? selectedOption.value : 'auto'
														}
													}))}
													isClearable={false}
												/>
											</div>
										) : null}
									</div>

									{showFreeGeometrySettings ? (
										<div className="form-item" style={{ marginBottom: '12px' }}>
											<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.layoutGeometry || `Layout Geometry (${geometryUnitLabel})`}
											</label>
											<div
												style={{
													display: 'grid',
													gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
													gap: '10px',
												}}
											>
												<div className="form-item" style={{ marginBottom: 0 }}>
													<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
														{`${t.settingsLabels?.width || 'Width'} (${geometryUnitLabel})`}
													</label>
													<input
														className="add-column"
														type="number"
														step="0.01"
														value={geometryDrafts.width}
														onFocus={() => setActiveGeometryField('width')}
														onBlur={() => handleGeometryDraftBlur('width')}
														onChange={(e) => handleGeometryDraftChange('width', e.target.value)}
													/>
												</div>
												<div className="form-item" style={{ marginBottom: 0 }}>
													<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
														{`${t.settingsLabels?.height || 'Height'} (${geometryUnitLabel})`}
													</label>
													<input
														className="add-column"
														type="number"
														step="0.01"
														value={geometryDrafts.height}
														onFocus={() => setActiveGeometryField('height')}
														onBlur={() => handleGeometryDraftBlur('height')}
														onChange={(e) => handleGeometryDraftChange('height', e.target.value)}
													/>
												</div>
												<div className="form-item" style={{ marginBottom: 0 }}>
													<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
														{`${t.settingsLabels?.top || 'Top'} (${geometryUnitLabel})`}
													</label>
													<input
														className="add-column"
														type="number"
														step="0.01"
														value={geometryDrafts.top}
														onFocus={() => setActiveGeometryField('top')}
														onBlur={() => handleGeometryDraftBlur('top')}
														onChange={(e) => handleGeometryDraftChange('top', e.target.value)}
													/>
												</div>
												<div className="form-item" style={{ marginBottom: 0 }}>
													<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
														{`${t.settingsLabels?.left || 'Left'} (${geometryUnitLabel})`}
													</label>
													<input
														className="add-column"
														type="number"
														step="0.01"
														value={geometryDrafts.left}
														onFocus={() => setActiveGeometryField('left')}
														onBlur={() => handleGeometryDraftBlur('left')}
														onChange={(e) => handleGeometryDraftChange('left', e.target.value)}
													/>
												</div>
												<div className="form-item" style={{ marginBottom: 0 }}>
													<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
														{`${t.settingsLabels?.right || 'Right'} (${geometryUnitLabel})`}
													</label>
													<input
														className="add-column"
														type="number"
														step="0.01"
														value={geometryDrafts.right}
														onFocus={() => setActiveGeometryField('right')}
														onBlur={() => handleGeometryDraftBlur('right')}
														onChange={(e) => handleGeometryDraftChange('right', e.target.value)}
													/>
												</div>
												<div className="form-item" style={{ marginBottom: 0 }}>
													<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
														{`${t.settingsLabels?.bottom || 'Bottom'} (${geometryUnitLabel})`}
													</label>
													<input
														className="add-column"
														type="number"
														step="0.01"
														value={geometryDrafts.bottom}
														onFocus={() => setActiveGeometryField('bottom')}
														onBlur={() => handleGeometryDraftBlur('bottom')}
														onChange={(e) => handleGeometryDraftChange('bottom', e.target.value)}
													/>
												</div>
											</div>
										</div>
									) : null}

								</div>

								{hasColorSettings ? (
									<div className="form-item">
										<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
											Theme Mode
										</label>
										<div className="segment-switch segment-switch--transparent">
											<div
												className="segment-switch__wrap"
												style={{ marginTop: 0, padding: 0, maxWidth: '320px' }}
											>
												<ToggleButtonGroup
													value={colorThemeMode}
													exclusive
													onChange={(_, nextMode) => {
														if (!nextMode) return;
														setEdit((prev) => ({
															...prev,
															props: {
																...prev.props,
																themeMode: normalizeThemeMode(nextMode),
															},
														}));
													}}
													className="segment-switch__group"
												>
													<ToggleButton value="dark" size="small" className="segment-switch__btn">
														Dark
													</ToggleButton>
													<ToggleButton value="light" size="small" className="segment-switch__btn">
														Light
													</ToggleButton>
												</ToggleButtonGroup>
											</div>
										</div>
									</div>
								) : null}

								{edit.type === 'chart' ?
									Object.keys(edit.props).filter(k => !['xData', 'showTitle', 'labelFontSize', 'valueFontSize', 'contentAlignment'].includes(k) && !isHiddenWidgetEditorPropKey(k)).map((key, idx) => (
										<div className='form-item' key={idx}>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>{t.settingsLabels?.[key] || key}</label>
											{key === 'chart_type' ?
												<Select
													classNamePrefix="react-select"
													options={chartTypes.map(o => ({ label: o.label, value: o.value }))}
													value={chartTypes.map(o => ({ label: o.label, value: o.value })).find(o => o.value === edit.props[key])}
													onChange={selectedOption => setEdit({
														...edit,
														props: {
															...edit.props,
															[key]: selectedOption ? selectedOption.value : ''
														}
													})}
												/>
												: key === 'curves' ?
													<Fragment>
														<button className='details-add-btn' style={{ width: '120px' }}
															onClick={() => setEdit({
																...edit,
																props: {
																	...edit.props,
																	curves: [
																		...(edit.props.curves || []),
																		{
																			yData: [],
																			customize: false,
																			min: 0,
																			max: 0,
																			yLabel: "Label",
																			pin: "Pin",
																			aggregation: "mean",
																			bgColor: "#eb1518",
																			hbgColor: "#fc2629",
																			bdColor: "#030ae5"
																		}
																	]
																},
																showCurves: [...(edit.showCurves || []), false]
															})}>
															<span>+</span>
															<span>{t.addCurve}</span>
														</button>
														{(edit.props.curves || []).map((curve, index) => (
															<div className='details-curves-item' key={index}>
																<div className='details-hierarchy-tree' style={{
																	width: '100%', flexDirection: 'row'
																}}>
																	<button className='details-actions-btn'
																		style={{
																			margin: 0, borderRadius: 8,
																		}} onClick={() => setEdit({
																			...edit, showCurves: (edit.showCurves || []).map(
																				(sh, i) => i === index ? !sh : sh)
																		})}>
																		<ExpandIcon style={{
																			transform: (edit.showCurves || [])[index] ? 'rotate(180deg)' : 'rotate(0)',
																			transition: 'transform .2s linear'
																		}} />
																	</button>
																	<input type='text' className='add-column' placeholder={t.label}
																		style={{
																			width: 'calc(100% - 64px)',
																			padding: '4px 10px',
																			marginInlineEnd: 4,
																			color: 'var(--cell--color)',
																			caretColor: 'var(--cell--color)'
																		}}
																		value={curve.yLabel !== null ? curve.yLabel : ''}
																		onChange={(e) =>
																			setEdit(prev => ({
																				...prev,
																				props: {
																					...prev.props,
																					curves: (prev.props.curves || []).map((c, id) =>
																						id === index ? { ...c, yLabel: e.target.value } : { ...c }
																					)
																				}
																			}))
																		}
																	/>
																	<button className='details-delete-btn'
																		style={{ position: 'static' }} onClick={() => setEdit({
																			...edit,
																			showCurves: (edit.showCurves || []).filter((_sh, i) => i !== index),
																			props: {
																				...edit.props,
																				curves: (edit.props.curves || []).filter((_c, i) => i !== index)
																			}
																		})}>
																		<DeleteIcon color={'var(--part-background)'} />
																	</button>
																</div>
																{edit.showCurves[index] && Object.keys(curve).filter(
																	k => k !== 'yLabel' && k !== 'yData' && !k.endsWith('Light')).map((k, i) => (

																		<div className='form-item' key={i} style={
																			k.toLowerCase().includes('color') && k !== 'changeColorBasedOnValue' && k !== 'colors' ?
																				{ display: 'inline-block', width: '33%' }
																				: k === 'aggregation' || k === 'pin' ?
																					{ display: 'inline-block', width: '75%', marginInlineEnd: '1%' }
																					: { display: 'block', width: '100%' }}>
																			{!(!curve.customize && (k === 'min' || k === 'max')) && <label className='form-lbl' style={{ color: 'var(--header-color)' }}>
																				{(devices?.length > 0 && k === 'pin') ? '' : (t.settingsLabels?.[k] || k)}</label>}
																			{k.toLowerCase().includes('color') && k !== 'changeColorBasedOnValue' && k !== 'colors' ?
																				<ColorPicker
																					id={`curve-${k}-${index}`}
																					color={themeColorValue(curve, k)}
																					setColor={(color) => setEdit(prev => ({
																						...prev,
																						props: {
																							...prev.props,
																							curves: (prev.props.curves || []).map((c, id) => id === index ? {
																								...c, [themeColorKey(k)]: color
																							} : c)
																						}
																					}))}
																				/>
																				: k === 'pin' ?
																					(validDatastreams?.length > 0
																						? (
																							<>
																								<Select
																									classNamePrefix="react-select"
																									options={validDatastreams.map(ds => ({ label: `${ds.name} [${ds.pin}]`, value: ds.pin }))}
																									value={validDatastreams.map(ds => ({ label: `${ds.name} [${ds.pin}]`, value: ds.pin })).find(ds => ds.value === curve[k])}
																									onChange={selectedOption => {
																										const datastream = validDatastreams.find(ds => ds.pin === (selectedOption ? selectedOption.value : ''));
																										setEdit(prev => ({
																											...prev,
																											props: {
																												...prev.props,
																												curves: (prev.props.curves || []).map((c, id) => id === index ? {
																													...c, [k]: datastream?.pin || '', yLabel: datastream?.name || ''
																												} : c)
																											}
																										}));
																									}}
																									isClearable={true}
																								/>
																								<CreateDatastream
																									onSelect={(cat) => setAddDatastream({ edit: true, pinCategory: cat })}
																								/>
																							</>
																						)
																						: (devices?.length > 0 ?
																							<div>
																								<div className='form-item'>
																									<label className='form-lbl'>{t.deviceLabel}</label>
																									<Select
																										classNamePrefix="react-select"
																										options={deviceSelectOptions}
																										value={deviceSelectOptions.find(device => device.value === curve.deviceId)}
																										onChange={selectedOption => {
																											setEdit({
																												...edit,
																												props: {
																													...edit.props,
																													curves: (edit.props.curves || []).map((c, id) => id === index ? {
																														...c, deviceId: selectedOption ? selectedOption.value : 0
																													} : c)
																												}
																											});
																										}
																										}
																										isClearable={true}
																									/>
																								</div>
																								{curve.deviceId ?
																									<div>
																										<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
																											{t.pinLabel}</label>
																										<Select
																											classNamePrefix="react-select"
																											options={(((devices || []).find(device => device.id === curve.deviceId))?.template?.datastreams || []).filter(ds => ds).map(ds => ({ label: `${ds.name} [${ds.pin}]`, value: ds.pin }))}
																											value={(((devices || []).find(device => device.id === curve.deviceId))?.template?.datastreams || []).filter(ds => ds).map(ds => ({ label: `${ds.name} [${ds.pin}]`, value: ds.pin })).find(ds => ds.value === curve[k])}
																											onChange={selectedOption => {
																												const datastream = (((devices || []).find(device => device.id === curve.deviceId))?.template?.datastreams || []).find(
																													ds => ds && ds.pin === (selectedOption ? selectedOption.value : ''));
																												setEdit(prev => ({
																													...prev,
																													props: {
																														...prev.props,
																														curves: (prev.props.curves || []).map((c, id) => id === index ? {
																															...c, [k]: datastream?.pin || '', yLabel: datastream?.name || ''
																														} : c)
																													}
																												}));
																											}}
																											isClearable={true}
																										/>

																									</div>
																									: null}
																							</div> : (!((devices || []).length > 0) ? <CreateDatastream onSelect={(cat) => setAddDatastream({
																								edit: true,
																								pinCategory: cat
																							})} /> : null)))
																					: k === 'aggregation' ?
																						<Select
																							classNamePrefix="react-select"
																							options={aggregations.map(o => ({ label: o.label, value: o.value }))}
																							value={aggregations.map(o => ({ label: o.label, value: o.value })).find(o => o.value === curve[k])}
																							onChange={selectedOption => {
																								setEdit({
																									...edit,
																									props: {
																										...edit.props,
																										curves: (edit.props.curves || []).map((c, id) => id === index ? {
																											...c, [k]: selectedOption ? selectedOption.value : ''
																										} : c)
																									}
																								})
																							}}
																							isClearable={true}
																						/>
																						: k === 'customize'
																							? (curve[k] ?
																								<ToggleOn style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer', display: 'block' }}
																									onClick={() => setEdit({
																										...edit, props: {
																											...edit.props, curves: (edit.props.curves || []).map((c, id) => id === index ? {
																												...c, [k]: false
																											} : c)
																										}
																									})} /> :
																								<ToggleOff style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer', display: 'block' }}
																									onClick={() => setEdit({
																										...edit, props: {
																											...edit.props, curves: (edit.props.curves || []).map((c, id) => id === index ? {
																												...c, [k]: true
																											} : c)
																										}
																									})} />)
																							: (k === 'min' || k === 'max')
																								? curve.customize
																									? <input type='text' className='add-column' placeholder={capitalize(k)}
																										style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
																										value={curve[k] !== null ? curve[k] : ''}
																										onChange={(e) => setEdit({
																											...edit,
																											props: {
																												...edit.props,
																												curves: (edit.props.curves || []).map((c, id) => id === index ? {
																													...c, [k]: e.target.value
																												} : c)
																											}
																										})} />
																									: null
																								: null}
																		</div>
																	))}

															</div>))}
													</Fragment>
													: typeof edit.props[key] === 'boolean' ?
														(edit.props[key] ?
															<ToggleOn style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
																onClick={() => setEdit(prev => ({
																	...prev,
																	props: { ...prev.props, [key]: false }
																}))} /> :
															<ToggleOff style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
																onClick={() => setEdit(prev => ({
																	...prev,
																	props: { ...prev.props, [key]: true }
																}))} />)
													: key === 'xData' ? <input type='text' className='add-column' placeholder={capitalize(key)}
														style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
														value={edit.props[key] !== null ? edit.props[key] : ''}
														onChange={(e) => setEdit({
															...edit,
															props: {
																...edit.props,
																[key]: e.target.value.split(',')
															}
														})} />
														: null}:


										</div>
									)) : edit.type === 'table' ?
										<Fragment>
											{Object.keys(edit.props)
												.filter(k => !['xData', 'headerBgColor', 'headerTextColor', 'bodyBgColor', 'bodyTextColor', 'showTitle', 'labelFontSize', 'valueFontSize', 'contentAlignment'].includes(k) && !isHiddenWidgetEditorPropKey(k))
												.map((key, idx) => (
													<div className="form-item" key={idx}>
														<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
															{t.settingsLabels?.[key] || key}
														</label>
														{key === 'chart_type' ?
															<Select
																classNamePrefix="react-select"
																options={chartTypes.map(o => ({ label: o.label, value: o.value }))}
																value={chartTypes.map(o => ({ label: o.label, value: o.value })).find(o => o.value === edit.props.chart_type)}
																onChange={selectedOption =>
																	setEdit(prev => ({
																		...prev,
																		props: { ...prev.props, chart_type: selectedOption ? selectedOption.value : '' },
																	}))
																}
																isClearable={true}
															/>
															: key === 'curves' ?
																<Fragment>
																	<button
																		className="details-add-btn"
																		style={{ width: '120px' }}
																		onClick={() =>
																			setEdit(prev => ({
																				...prev,
																				props: {
																					...prev.props,
																					curves: [
																						...(prev.props.curves || []),
																						{
																							deviceId: 0,
																							pin: '',
																							aggregation: 'mean',
																							yData: [],
																							yLabel: 'Label',
																							customize: false,
																							min: 0,
																							max: 0
																						}
																					]
																				},
																				showCurves: [...(prev.showCurves || []), false]
																			}))
																		}
																	>
																		<span>+</span><span>{t.addColumn}</span>
																	</button>

																	{(edit.props.curves || []).map((column, index) => {
																		return (
																			<div className="details-curves-item" key={index}>
																				<div
																					className="details-hierarchy-tree"
																					style={{ width: '100%', flexDirection: 'row' }}
																				>
																					<button
																						className="details-actions-btn"
																						onClick={() =>
																							setEdit(prev => ({
																								...prev,
																								showCurves: (prev.showCurves || []).map((sh, i) =>
																									i === index ? !sh : sh
																								),
																							}))
																						}
																					>
																						<ExpandIcon
																							style={{
																								transform: (edit.showCurves || [])[index] ? 'rotate(180deg)' : 'rotate(0)',
																								transition: 'transform .2s linear',
																							}}
																						/>
																					</button>

																					<input
																						type="text"
																						className="add-column"
																						placeholder={t.label}
																						style={{
																							width: 'calc(100% - 64px)',
																							padding: '4px 10px',
																							marginInlineEnd: 4,
																							color: 'var(--cell--color)',
																							caretColor: 'var(--cell--color)',
																						}}
																						value={column.yLabel}
																						onChange={e =>
																							setEdit(prev => ({
																								...prev,
																								props: {
																									...prev.props,
																									curves: (prev.props.curves || []).map((c, i) =>
																										i === index ? { ...c, yLabel: e.target.value } : c
																									),
																								},
																							}))
																						}
																					/>

																					<button
																						className="details-delete-btn"
																						style={{ position: 'static' }}
																						onClick={() =>
																							setEdit(prev => ({
																								...prev,
																								showCurves: (prev.showCurves || []).filter((_, i) => i !== index),
																								props: {
																									...prev.props,
																									curves: (prev.props.curves || []).filter((_, i) => i !== index),
																								},
																							}))
																						}
																					>
																						<DeleteIcon  />
																					</button>
																				</div>

																				{edit.showCurves[index] &&
																					Object.keys(column)
																						.filter(k => k !== 'yLabel' && k !== 'yData' && !k.toLowerCase().includes('color'))
																						.map((k, i) => (
																							<div
																								className="form-item"
																								key={i}
																								style={
																									k.toLowerCase().includes('color')
																										? { display: 'inline-block', width: '33%' }
																										: k === 'aggregation' || k === 'pin'
																											? { display: 'inline-block', width: '75%', marginInlineEnd: '1%' }
																											: { display: 'block', width: '100%' }
																								}
																							>
																								{!(!column.customize && (k === 'min' || k === 'max')) && (
																									<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
																										{(devices?.length > 0 && k === 'pin') ? '' : (t.settingsLabels?.[k] || k)}
																									</label>
																								)}

																								{k === 'pin' ? (
																									validDatastreams?.length > 0 ? (
																										<Select
																											classNamePrefix="react-select"
																											options={validDatastreams.map(d => ({
																												label: `${d.name} [${d.pin}]`,
																												value: d.pin,
																											}))}
																											value={validDatastreams.map(d => ({ label: `${d.name} [${d.pin}]`, value: d.pin })).find(d => d.value === column.pin)}
																											onChange={selectedOption => {
																												const found = validDatastreams.find(d => d.pin === (selectedOption ? selectedOption.value : ''));
																												if (!found) return;
																												setEdit(prev => ({
																													...prev,
																													props: {
																														...prev.props,
																														curves: (prev.props.curves || []).map((c, id) =>
																															id === index
																																? { ...c, pin: found?.pin || '', yLabel: found?.name || '' }
																																: c
																														),
																													},
																												}));
																											}}
																											isClearable={true}
																										/>
																									) : devices?.length > 0 ? (
																										<div>
																											<div className="form-item">
																												<label className="form-lbl">{t.deviceLabel}</label>
																												<Select
																													classNamePrefix="react-select"
																													options={deviceSelectOptions}
																													value={deviceSelectOptions.find(dev => dev.value === column.deviceId)}
																													onChange={selectedOption => {
																														setEdit(prev => ({
																															...prev,
																															props: {
																																...prev.props,
																																curves: (prev.props.curves || []).map((c, i) =>
																																	i === index
																																		? { ...c, deviceId: selectedOption ? selectedOption.value : 0 }
																																		: c
																																),
																															},
																														}));
																													}}
																													isClearable={true}
																												/>
																											</div>

																											{(() => {
																												const thisDevice = ((devices || []).find(dev => dev.id === column.deviceId));
																												const thisDeviceDs = (thisDevice?.template?.datastreams || []).filter(ds => ds);
																												return thisDeviceDs.length > 0 ? (
																													<div>
																														<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
																															{t.pinLabel}
																														</label>
																														<Select
																															classNamePrefix="react-select"
																															options={thisDeviceDs.map(ds => ({
																																label: `${ds.name} [${ds.pin}]`,
																																value: ds.pin,
																															}))}
																															value={thisDeviceDs.map(ds => ({ label: `${ds.name} [${ds.pin}]`, value: ds.pin })).find(ds => ds.value === column.pin)}
																															onChange={selectedOption => {
																																const datastream = thisDeviceDs.find(
																																	ds => ds.pin === (selectedOption ? selectedOption.value : '')
																																);
																																if (!datastream) return;
																																setEdit(prev => ({
																																	...prev,
																																	props: {
																																		...prev.props,
																																		curves: (prev.props.curves || []).map((c, id) =>
																																			id === index
																																				? { ...c, pin: datastream?.pin || '', yLabel: datastream?.name || '' }
																																				: c
																																		),
																																	},
																																}));
																															}}
																															isClearable={true}
																														/>
																													</div>
																												) : null;
																											})()}
																										</div>
																									) : (
																										<CreateDatastream
																											onSelect={cat =>
																												setAddDatastream({ edit: true, pinCategory: cat })
																											}
																										/>
																									)
																								) : k === 'aggregation' ? (
																									<Select
																										classNamePrefix="react-select"
																										options={aggregations.map(o => ({ label: o.label, value: o.value }))}
																										value={aggregations.map(o => ({ label: o.label, value: o.value })).find(o => o.value === column.aggregation)}
																										onChange={selectedOption =>
																											setEdit(prev => ({
																												...prev,
																												props: {
																													...prev.props,
																													curves: (prev.props.curves || []).map((c, id) =>
																														id === index ? { ...c, aggregation: selectedOption ? selectedOption.value : '' } : c
																													),
																												},
																											}))
																										}
																										isClearable={true}
																									/>
																								) : k === 'customize' ? (
																									column.customize ? (
																										<ToggleOn
																											onClick={() =>
																												setEdit(prev => ({
																													...prev,
																													props: {
																														...prev.props,
																														curves: (prev.props.curves || []).map((c, id) =>
																															id === index ? { ...c, customize: false } : c
																														),
																													},
																												}))
																											}
																										/>
																									) : (
																										<ToggleOff
																											onClick={() =>
																												setEdit(prev => ({
																													...prev,
																													props: {
																														...prev.props,
																														curves: (prev.props.curves || []).map((c, id) =>
																															id === index ? { ...c, customize: true } : c
																														),
																													},
																												}))
																											}
																										/>
																									)
																								) : (k === 'min' || k === 'max') && column.customize ? (
																									<input
																										type="number"
																										className="add-column"
																										placeholder={capitalize(k)}
																										value={column[k] ?? ''}
																										onChange={e =>
																											setEdit(prev => ({
																												...prev,
																												props: {
																													...prev.props,
																													curves: (prev.props.curves || []).map((c, id) =>
																														id === index ? { ...c, [k]: Number(e.target.value) } : c
																													),
																												},
																											}))
																										}
																									/>
																								) : null}
																							</div>
																						))}
																			</div>
																		);
																	})}
																</Fragment>
																: null}
													</div>
												))}

											<div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
												{['headerBgColor', 'headerTextColor', 'bodyBgColor', 'bodyTextColor'].map((key, idx) => (
													<div className='form-item' key={idx}>
														<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
															{t.settingsLabels?.[key] || capitalize(key.replace(/([A-Z])/g, ' $1').trim())}
														</label>
														<ColorPicker
															id={key}
															color={themeColorValue(edit.props, key)}
															setColor={(c) => setEdit(prev => ({
																...prev,
																props: { ...prev.props, [themeColorKey(key)]: c }
															}))}
														/>
													</div>
												))}
											</div>
										</Fragment>
										: edit.type === 'fan' ? (
											<Fragment>
												<button
													className="details-add-btn"
													style={{ width: '180px', marginBottom: '10px' }}
													onClick={() => {
														const currentStreams = edit.props.datastreams || [];
														if (currentStreams.length >= 10) {
															setAlertMessage("Maximum of 10 datastreams reached.");
															setAlertType('warning');
															setShowAlert(true);
															return;
														}
														const newStream = {
															deviceId: (devices || [])?.[0]?.id || 0,
															pin: '',
															speed: 1,
															label: `Datastream ${currentStreams.length + 1}`,
															isActive: false
														};
														setEdit(prev => ({
															...prev,
															props: {
																...prev.props,
																datastreams: [...currentStreams, newStream]
															}
														}));
													}}
												>
													<span>+</span><span>Add Datastream</span>
												</button>

												{(edit.props.datastreams || []).map((stream, index) => {
													const handleToggle = () => {
														const updatedStreams = (edit.props.datastreams || []).map((s, i) => ({
															...s,
															isActive: i === index ? !s.isActive : false,
														}));
														setEdit(prev => ({
															...prev,
															props: { ...prev.props, datastreams: updatedStreams }
														}));
													};

													return (
														<div className="details-curves-item" key={index} style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingBottom: '10px', borderBottom: '1px solid var(--header-background)' }}>
															<div className='details-hierarchy-tree' style={{ width: '100%', flexDirection: 'row', alignItems: 'center' }}>
																{stream.isActive
																	? <ToggleOn style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }} onClick={handleToggle} />
																	: <ToggleOff style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }} onClick={handleToggle} />
																}
																<b style={{ color: 'var(--header-color)', flexGrow: 1 }}>{stream.label || `Datastream ${index + 1}`}</b>
																<button
																	className="details-delete-btn"
																	style={{ position: 'static' }}
																	onClick={() => {
																		setEdit(prev => ({
																			...prev,
																			props: {
																				...prev.props,
																				datastreams: (prev.props.datastreams || []).filter((_, i) => i !== index),
																			},
																		}))
																	}}
																>
																	<DeleteIcon  />
																</button>
															</div>

															{
																!devices && (
																	<>
																		{(validDatastreams?.length > 0) && (
																			<div className="form-item">
																				<label className="form-lbl" style={{ color: 'var(--header-color)' }}>{t.pinLabel}</label>
																				<Select
																					classNamePrefix="react-select"
																					options={validDatastreams.map(ds => ({ label: `${ds.name} [${ds.pin}]`, value: ds.pin }))}
																					value={validDatastreams.map(ds => ({ label: `${ds.name} [${ds.pin}]`, value: ds.pin })).find(ds => ds.value === stream.pin)}
																					onChange={selectedOption => {
																						const updatedStreams = [...(edit.props.datastreams || [])];
																						const dsInfo = validDatastreams.find(d => d.pin === (selectedOption ? selectedOption.value : ''));
																						updatedStreams[index].pin = selectedOption ? selectedOption.value : '';
																						if (dsInfo) updatedStreams[index].label = dsInfo.name;
																						setEdit(prev => ({ ...prev, props: { ...prev.props, datastreams: updatedStreams } }));
																					}}
																					isClearable={true}
																				/>
																			</div>
																		)}
																		<CreateDatastream onSelect={(cat) => setAddDatastream({ edit: true, pinCategory: cat })} />
																	</>
																)
															}

															{((devices?.length > 0) && Array.isArray(devices)) && (
																<Fragment>
																	<div className="form-item">
																		<label className="form-lbl" style={{ color: 'var(--header-color)' }}>{t.deviceLabel}</label>
																		<Select
																			classNamePrefix="react-select"
																			options={deviceSelectOptions}
																			value={deviceSelectOptions.find(d => d.value === stream.deviceId)}
																			onChange={selectedOption => {
																				const updatedStreams = [...(edit.props.datastreams || [])];
																				updatedStreams[index].deviceId = selectedOption ? selectedOption.value : 0;
																				updatedStreams[index].pin = '';
																				setEdit(prev => ({ ...prev, props: { ...prev.props, datastreams: updatedStreams } }));
																			}}
																			isClearable={true}
																		/>
																	</div>

																	{stream.deviceId && (
																		<div className="form-item">
																			<label className="form-lbl" style={{ color: 'var(--header-color)' }}>{t.pinLabel}</label>
																			<Select
																				classNamePrefix="react-select"
																				options={(((devices || [])).find(d => d.id === stream.deviceId)?.template?.datastreams || []).filter(ds => ds).map(ds => ({ label: `${ds.name} [${ds.pin}]`, value: ds.pin }))}
																				value={(((devices || [])).find(d => d.id === stream.deviceId)?.template?.datastreams || []).filter(ds => ds).map(ds => ({ label: `${ds.name} [${ds.pin}]`, value: ds.pin })).find(ds => ds.value === stream.pin)}
																				onChange={selectedOption => {
																					const updatedStreams = [...(edit.props.datastreams || [])];
																					const dsInfo = (((devices || [])).find(d => d.id === stream.deviceId)?.template?.datastreams || []).filter(d => d).find(d => d.pin === (selectedOption ? selectedOption.value : ''));
																					updatedStreams[index].pin = selectedOption ? selectedOption.value : '';
																					if (dsInfo) updatedStreams[index].label = dsInfo.name;
																					setEdit(prev => ({ ...prev, props: { ...prev.props, datastreams: updatedStreams } }));
																				}}
																				isClearable={true}
																			/>
																		</div>
																	)}
																</Fragment>
															)}


															<div className="form-item">
																<label className="form-lbl" style={{ color: 'var(--header-color)' }}>Speed (1-{edit.props.maxSpeed || 10})</label>
																<Select
																	classNamePrefix="react-select"
																	options={Array.from({ length: edit.props.maxSpeed || 10 }, (_, i) => ({ label: `Speed ${i + 1}`, value: i + 1 }))}
																	value={Array.from({ length: edit.props.maxSpeed || 10 }, (_, i) => ({ label: `Speed ${i + 1}`, value: i + 1 })).find(o => o.value === stream.speed)}
																	onChange={selectedOption => {
																		const updatedStreams = [...(edit.props.datastreams || [])];
																		updatedStreams[index].speed = selectedOption ? selectedOption.value : 0;
																		setEdit(prev => ({ ...prev, props: { ...prev.props, datastreams: updatedStreams } }));
																	}}
																	isClearable={true}
																/>
															</div>
														</div>
													)
												})}
											</Fragment>
										) : edit.type === 'background-image' ? (
											<BackgroundImageSettings
												initialFile={edit?.props?.file || ''}
												initialUrl={edit?.props?.imageUrl || ''}
												onFileChanged={(file) => {
													const nextFile = file instanceof Blob ? file : null;
													setEdit((prev) => ({
														...prev,
														props: {
															...prev.props,
															file: nextFile,
															imageUrl: prev.props?.imageUrl,
														},
													}));
												}}
											/>


										): edit.type === 'box-widget' ? (
											<BoxWidgetSettings edit={edit} setEdit={setEdit} />
										): edit.type === 'text-title' ? (
											<TextTitleSettings
												edit={edit}
												setEdit={setEdit} 
												alignments={alignments} 
												/>
										): edit.type === 'time-range' ? (
											<TimeRangeSettings
												edit={edit}
												setEdit={setEdit}
												devices={devices}
												validDatastreams={validDatastreams}
												deviceOptions={deviceSelectOptions}
											/>
										)
										: edit.type === ENVIRONMENTAL_STATUS_WIDGET_TYPE ? (
											<EnvironmentalStatusSettings
												edit={edit}
												setEdit={setEdit}
												devices={devices}
												validDatastreams={validDatastreams}
												deviceOptions={deviceSelectOptions}
											/>
										)

											: (validDatastreams?.length > 0) && edit.type !== 'fan'&& edit.type !== 'text-title' && edit.type !== ENVIRONMENTAL_STATUS_WIDGET_TYPE ? (
												<Fragment>
													<div className='form-item'>
														<label className='form-lbl' style={{ color: 'var(--header-color)' }}>{t.datastreamLabel}</label>
															<Select
																classNamePrefix="react-select"
															onChange={selectedOption => {
																const ds = findDatastreamById(
																	validDatastreams,
																	selectedOption ? selectedOption.value : null,
																);
															const newProps = stripDatastreamManagedProps(edit.props, edit.type);
															const protectedDatastreamColorKeys = [
																...LIQUID_STYLE_PROTECTED_COLOR_KEYS,
																...((edit.type === 'elbow' || edit.type === 'pipe' || edit.type === 'water-pump') ? ['color', 'pipeColor', 'pipeBackgroundColor', 'pipeGlassColor', 'frameBg', 'frameBorderColor', 'pipeBorderColor', 'clampColor'] : [])
															];
															Object.keys(newProps).forEach((key) => {
																	if (Object.keys(ds || {}).includes(key)) {
																	newProps[key] = ds[key];
																}
																const lowerKey = key.toLowerCase();
																	if (lowerKey.includes('color') && !lowerKey.endsWith('light') && key !== 'changeColorBasedOnValue' && key !== 'colors' && key !== 'colorGrading' && key !== 'inactiveColor' && key !== 'labelShadowUseValueColor' && key !== 'labelShadowColor' && key !== 'labelShadowChangeColorBasedOnValue' && key !== 'labelShadowColorGrading' && key !== 'labelShadowColors' && !protectedDatastreamColorKeys.includes(key)) {
																		newProps[key] = ds?.color;
																	}
									if (key === 'value') {
										newProps[key] = clampToMinMax(ds?.defaultValue, ds, newProps);
									}
																});
																const datastreamProps = applyDatastreamDefaultColors(newProps, edit.type, ds);
																if (VALUE_COLOR_WIDGET_TYPES.includes(edit.type)) {
																	const activeStops = themeColorValue(datastreamProps, 'colors', []);
																	datastreamProps[themeColorKey('colors')] = ds
																		? remapColorStopsForDatastream(activeStops, selectedDatastream, ds)
																		: [];
																	if (edit.type === 'label') {
																		const activeShadowStops = themeColorValue(datastreamProps, 'labelShadowColors', []);
																		datastreamProps[themeColorKey('labelShadowColors')] = ds
																			? remapColorStopsForDatastream(activeShadowStops, selectedDatastream, ds)
																			: [];
																	}
																}
																setEdit({
																	...edit,
																	dataId: selectedOption ? (ds?.id ?? null) : null,
																	dataPin: selectedOption ? (ds?.pin ?? null) : null,
																	title: ds?.name || '',
																	transparentBackground: false,
																	props: datastreamProps
																});
															}} options={validDatastreams.map(ds => ({ label: ds.name + ' [' + ds.pin + ']', value: ds.id }))}
															value={validDatastreams.map(ds => ({ label: ds.name + ' [' + ds.pin + ']', value: ds.id })).find(ds => String(ds.value) === String(selectedDatastream?.id ?? edit.dataId)) || null}
															isClearable={true}
														/>
													</div>
													<CreateDatastream onSelect={(cat) => setAddDatastream({
														edit: true,
														pinCategory: cat
													})} />
												</Fragment>
											) :
												((devices?.length > 0) && Array.isArray(devices)) && edit.type !== 'fan' ? (
													<div>

														<div className='form-item'>
															<label className='form-lbl' style={{ color: 'var(--header-color)' }}>{t.deviceLabel}</label>
															<Select
																classNamePrefix="react-select"
																options={deviceSelectOptions}
																value={deviceSelectOptions.find(device => device.value === edit.deviceId) || null}
																onChange={selectedOption => {
																	const nextDeviceId = selectedOption ? Number(selectedOption.value) : 0;
																	const nextDevice = (devices || []).find(device => Number(device?.id) === nextDeviceId);
																	const nextDeviceDatastreams = ((nextDevice?.template?.datastreams || []).filter(ds => ds));

																	setDeviceDatastream(nextDeviceDatastreams);
																	setEdit(prev => {
																		const previousDevice = (devices || []).find(device => Number(device?.id) === Number(prev.deviceId));
																		const previousTemplateSignature = getDeviceTemplateSignature(previousDevice);
																		const nextTemplateSignature = getDeviceTemplateSignature(nextDevice);
																		const hasSameTemplate = previousTemplateSignature !== ''
																			&& nextTemplateSignature !== ''
																			&& previousTemplateSignature === nextTemplateSignature;
																		const keepSelectedDatastream = hasSameTemplate
																			&& nextDeviceDatastreams.some(ds => Number(ds?.id) === Number(prev.dataId));

																		return {
																			...prev,
																			deviceId: nextDeviceId,
																			dataId: keepSelectedDatastream ? prev.dataId : null,
																			dataPin: keepSelectedDatastream ? prev.dataPin : null
																		};
																	});
																}}
																isClearable={true}
															/>
														</div>

														{((deviceDatastream?.length > 0) && Array.isArray(deviceDatastream)) ?
															<div className='form-item'>
																<label className='form-lbl' style={{ color: 'var(--header-color)' }}>{t.datastreamLabel}</label>
																<Select
																	key={`ds-${edit.deviceId}-${getDeviceTemplateSignature((devices || []).find(device => Number(device?.id) === Number(edit.deviceId)))}`}
																	classNamePrefix="react-select"
																	onChange={selectedOption => {
																		const ds = findDatastreamById(
																			deviceDatastream,
																			selectedOption ? selectedOption.value : null,
																		);

																const newTitle = ds?.name || '';

																const newProps = stripDatastreamManagedProps(edit.props, edit.type);
																const protectedDatastreamColorKeys = [
																	...LIQUID_STYLE_PROTECTED_COLOR_KEYS,
																	...((edit.type === 'elbow' || edit.type === 'pipe' || edit.type === 'water-pump') ? ['color', 'pipeColor', 'pipeBackgroundColor', 'pipeGlassColor', 'frameBg', 'frameBorderColor', 'pipeBorderColor', 'clampColor'] : [])
																];
																Object.keys(newProps).forEach((key) => {
																		if (Object.keys(ds || {}).includes(key)) {
																			newProps[key] = ds[key];
																		}
																		const lowerKey = key.toLowerCase();
																		if (lowerKey.includes('color') && !lowerKey.endsWith('light') && key !== 'changeColorBasedOnValue' && key !== 'colors' && key !== 'colorGrading' && key !== 'inactiveColor' && key !== 'labelShadowUseValueColor' && key !== 'labelShadowColor' && key !== 'labelShadowChangeColorBasedOnValue' && key !== 'labelShadowColorGrading' && key !== 'labelShadowColors' && !protectedDatastreamColorKeys.includes(key)) {
																			newProps[key] = ds?.color;
																		}
													if (key === 'value') {
														newProps[key] = clampToMinMax(ds?.defaultValue, ds, newProps);
													}
																		});
																		const datastreamProps = applyDatastreamDefaultColors(newProps, edit.type, ds);
																		if (VALUE_COLOR_WIDGET_TYPES.includes(edit.type)) {
																			const activeStops = themeColorValue(datastreamProps, 'colors', []);
																			datastreamProps[themeColorKey('colors')] = ds
																				? remapColorStopsForDatastream(activeStops, selectedDatastream, ds)
																				: [];
																			if (edit.type === 'label') {
																				const activeShadowStops = themeColorValue(datastreamProps, 'labelShadowColors', []);
																				datastreamProps[themeColorKey('labelShadowColors')] = ds
																					? remapColorStopsForDatastream(activeShadowStops, selectedDatastream, ds)
																					: [];
																			}
																		}
																		setEdit({
																			...edit,
																			dataId: selectedOption ? (ds?.id ?? null) : null,
																			dataPin: selectedOption ? (ds?.pin ?? null) : null,
																			title: newTitle,
																			transparentBackground: false,
																			props: datastreamProps
																		});
																	}} options={deviceDatastream.map(ds => ({ label: ds.name + ' [' + ds.pin + ']', value: ds.id }))}
																	value={deviceDatastream.map(ds => ({ label: ds.name + ' [' + ds.pin + ']', value: ds.id })).find(ds => String(ds.value) === String(selectedDatastream?.id ?? edit.dataId)) || null}
																	isClearable={true}
																/>
															</div> : null}

													</div>
												) : (!(((devices?.length > 0) && Array.isArray(devices))) && edit.type !== 'fan'&& edit.type !== 'text-title' ? <CreateDatastream onSelect={(cat) => setAddDatastream({
													edit: true,
													pinCategory: cat
												})} /> : null)}

								{edit.type === 'led' ? (
									<div className='form-item'>
										<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
											{t.settingsLabels?.color || 'LED On Color'}
										</label>
										<ColorPicker
											id="ledOnColor"
											color={themeColorValue(edit.props, 'color', '#00f0f0')}
											setColor={(c) =>
												setEdit(prev => ({
													...prev,
													props: { ...prev.props, [themeColorKey('color')]: c }
												}))
											}
										/>
									</div>
								) : null}

								{edit.type === PERIOD_COLUMN_WIDGET_TYPE ? (
									<Fragment>
										<div className='form-item'>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.period || 'Period'}
											</label>
											<Select
												classNamePrefix="react-select"
												options={periodOptions.map(option => ({ label: option.label, value: option.value }))}
												value={periodOptions.map(option => ({ label: option.label, value: option.value })).find(option => option.value === normalizePeriodColumnPeriod(edit.props.period))}
												onChange={selectedOption => setEdit(prev => ({
													...prev,
													props: {
														...prev.props,
														period: selectedOption ? selectedOption.value : 'day'
													}
												}))}
												isClearable={false}
											/>
										</div>

										<div className='form-item'>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.stat || 'Value to Display'}
											</label>
											<Select
												classNamePrefix="react-select"
												options={periodStats.map(option => ({ label: option.label, value: option.value }))}
												value={periodStats.map(option => ({ label: option.label, value: option.value })).find(option => option.value === edit.props.stat)}
												onChange={selectedOption => setEdit(prev => ({
													...prev,
													props: {
														...prev.props,
														stat: selectedOption ? selectedOption.value : 'max'
													}
												}))}
												isClearable={false}
											/>
										</div>

										<div className='form-item'>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.showUnit || 'Show Unit'}
											</label>
											{edit.props.showUnit !== false ? (
												<ToggleOn
													style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
													onClick={() => setEdit(prev => ({
														...prev,
														props: { ...prev.props, showUnit: false }
													}))}
												/>
											) : (
												<ToggleOff
													style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
													onClick={() => setEdit(prev => ({
														...prev,
														props: { ...prev.props, showUnit: true }
													}))}
												/>
											)}
										</div>

										<div className='form-item'>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.changeColorBasedOnValue || 'Change Color Based On Value'}
											</label>
											{edit.props.changeColorBasedOnValue ? (
												<ToggleOn
													style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
													onClick={() => setEdit(prev => ({
														...prev,
														props: { ...prev.props, changeColorBasedOnValue: false }
													}))}
												/>
											) : (
												<ToggleOff
													style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
													onClick={() => setEdit(prev => ({
														...prev,
														props: { ...prev.props, changeColorBasedOnValue: true }
													}))}
												/>
											)}
										</div>

										{edit.props.changeColorBasedOnValue ? (
											<Fragment>
												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
														{t.settingsLabels?.colorGrading || 'Color Grading'}
													</label>
													{edit.props.colorGrading ? (
														<ToggleOn
															style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
															onClick={() => setEdit(prev => ({
																...prev,
																props: { ...prev.props, colorGrading: false }
															}))}
														/>
													) : (
														<ToggleOff
															style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
															onClick={() => setEdit(prev => ({
																...prev,
																props: { ...prev.props, colorGrading: true }
															}))}
														/>
													)}
												</div>

												<LabelColorSlider
													minValue={getDsMinMax(selectedDatastream).min ?? 0}
													maxValue={getDsMinMax(selectedDatastream).max ?? 100}
													stops={themeColorValue(edit.props, 'colors', [])}
													colorGrading={Boolean(edit.props.colorGrading)}
													setStops={(updated) => setEdit(prev => ({
														...prev,
														props: { ...prev.props, [themeColorKey('colors')]: updated }
													}))}
												/>
											</Fragment>
										) : (
											<div className='form-item'>
												<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
													{t.settingsLabels?.color || 'Color'}
												</label>
												<ColorPicker
													id="period-column-color"
													color={themeColorValue(edit.props, 'color', '#3b82f6')}
													setColor={(c) => setEdit(prev => ({
														...prev,
														props: { ...prev.props, [themeColorKey('color')]: c }
													}))}
												/>
											</div>
										)}
									</Fragment>
								) : null}

								{(edit.dataId > 0 || edit.type === WATER_TANK_WIDGET_TYPE) && edit.type !== 'fan' && edit.type !== PERIOD_COLUMN_WIDGET_TYPE ?
									Object.keys(edit.props)
										.filter((key) => {
											if (isHiddenWidgetEditorPropKey(key)) {
												return false;
											}
											if (key === 'wellBackgroundColor' || key === 'wellBorderColor') {
												return false;
											}
											if (key === 'min' || key === 'max' || key === 'unit') {
												return false;
											}
											if ([
												'options', 'precision', 'startColor', 'stopColor',
										'changeColorBasedOnValue', 'colors', 'colorGrading', 'direction', 'color', 'inactiveColor',
										'activeOptionColor', 'activeBackgroundColor', 'activeTextColor',
										'inactiveBackgroundColor', 'inactiveTextColor',
										'labelShadowEnabled', 'labelShadowBlur', 'labelShadowStrength', 'labelShadowUseValueColor', 'labelShadowColor',
										'labelShadowChangeColorBasedOnValue', 'labelShadowColorGrading', 'labelShadowColors',
										'showUnit', 'showValueLine', 'fontSize', 'labelFontSize', 'valueFontSize', 'contentAlignment', 'showTitle', 'statusText'
									].includes(key)) {
										return false;
									}

											if (
												(edit.type === 'slider' || edit.type === 'number') &&
												['buttonBgColor', 'buttonHoverBgColor', 'valueColor', 'valueHoverColor'].includes(key)
											) {
												return false;
											}

											if (edit.type === 'push-button' && ['textColor', 'inactiveTextColor'].includes(key)) {
												return false;
											}

											if (
												edit.type === 'pipe' &&
												[
													'value',
													'direction',
													'flowDirection',
													'color',
													'pipeColor',
													'pipeBackgroundColor',
													'pipeGlassColor',
													'frameBg',
													'frameBorderColor',
													'frameBorderWidth',
													'frameRadius',
													'framePadding',
													'pipeHeight',
													'pipeWidthPercent',
													'pipeBorderColor',
													'clampColor',
													'titleFontSize',
													'statusFontSize',
													'clickToToggle',
													'flowThreshold',
												].includes(key)
											) {
												return false;
											}

											if (
												edit.type === 'elbow' &&
												[
													'value',
													'direction',
													'color',
													'pipeColor',
													'pipeBackgroundColor',
													'pipeGlassColor',
													'pipeBorderColor',
													'clampColor',
													'pipeHeight',
												].includes(key)
											) {
												return false;
											}

											if (
												edit.type === 'water-pump' &&
												[
													'value',
													'direction',
													'color',
													'pipeColor',
													'pipeBackgroundColor',
													'pipeGlassColor',
													'pipeBorderColor',
													'clampColor',
													'pipeHeight',
													'activeBackgroundColor',
													'inactiveBackgroundColor',
													'inactiveColor',
													'offColor',
												].includes(key)
											) {
												return false;
											}

											return true;
										})
										.map((key, idx) => (
											<div className='form-item' key={idx}>
												<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
													{key === 'interactionMode'
														? (t.settingsLabels?.interactionMode || 'Interaction Mode')
														: (t.settingsLabels?.[key] || key)}
												</label>
												{typeof (edit.props[key]) === 'boolean' ?
													(edit.props[key] ?
														<ToggleOn style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
															onClick={() => setEdit({ ...edit, props: { ...edit.props, [key]: false } })} /> :
														<ToggleOff style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
															onClick={() => setEdit({ ...edit, props: { ...edit.props, [key]: true } })} />) :
													key === 'interactionMode' ? (
														<Select
															classNamePrefix="react-select"
															options={[
																{ label: t.pushButtonMode || 'Push Button', value: 'push' },
																{ label: t.switchMode || 'Switch', value: 'switch' }
															]}
															value={[
																{ label: t.pushButtonMode || 'Push Button', value: 'push' },
																{ label: t.switchMode || 'Switch', value: 'switch' }
															].find(o => o.value === edit.props[key]) || { label: t.pushButtonMode || 'Push Button', value: 'push' }}
															onChange={selectedOption => setEdit({
																...edit,
																props: {
																	...edit.props,
																	[key]: selectedOption ? selectedOption.value : 'push'
																}
															})}
															isClearable={false}
														/>
													) : key === 'alignment' ? (
														<Select
															classNamePrefix="react-select"
															options={alignments.map(o => ({ label: o.label, value: o.value }))}
															value={alignments.map(o => ({ label: o.label, value: o.value })).find(o => o.value === edit.props[key])}
															onChange={selectedOption => setEdit({
																...edit,
																props: {
																	...edit.props,
																	[key]: selectedOption ? selectedOption.value : ''
																}
															})}
															isClearable={true}
														/>
													) : (key.toLowerCase().includes('color') && key !== 'changeColorBasedOnValue' && key !== 'colors') ?
														<ColorPicker
															id={key}
															color={themeColorValue(edit.props, key)}
															setColor={(c) => setEdit(prev => ({
																...prev,
																props: { ...prev.props, [themeColorKey(key)]: c }
															}))}
														/> :
													(key === 'value' && (NUMERIC_VALUE_WIDGET_TYPES.has(edit.type) || !isStringDatastream)) ? (
																	(() => {
																		const { min, max } = getEffectiveMinMax(selectedDatastream, edit.props);
																		const sliderInputStep = edit.type === 'slider'
																			? Math.max(0.000001, Number(edit.props?.step) || 1)
																			: 'any';
																		return (
																			<input
																			type='number'
																			className='add-column'
																			placeholder={capitalize(key)}
																			min={min ?? undefined}
																			max={max ?? undefined}
																			step={sliderInputStep}
																			style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
																			value={edit.props[key] !== null && edit.props[key] !== undefined ? edit.props[key] : ''}
																			onChange={(e) => {
																				const next = e.target.value;
																				if (next === '') {
																					setEdit((prev) => ({
																						...prev,
																						props: { ...prev.props, [key]: '' }
																					}));
																					if (edit.type === 'slider') {
																						updateWidgetLivePropsById(edit.id, { [key]: '' });
																					}
																					return;
																				}
																				const normalizedNextValue = edit.type === 'slider'
																					? normalizeSliderValueInputChange(next, edit.props[key], {
																						stepValue: edit.props?.step,
																						ds: selectedDatastream,
																						fallbackProps: edit.props,
																						nativeInputType: e.nativeEvent?.inputType,
																						nativeInputData: e.nativeEvent?.data,
																					})
																					: clampToMinMax(next, selectedDatastream, edit.props);
																				setEdit((prev) => ({
																					...prev,
																					props: { ...prev.props, [key]: normalizedNextValue }
																				}));
																				if (edit.type === 'slider') {
																					updateWidgetLivePropsById(edit.id, { [key]: normalizedNextValue });
																				}
																			}}
																				onBlur={() => {
																				let normalizedValue = clampToMinMax(edit.props[key], selectedDatastream, edit.props);
																				if (edit.type === 'slider' && normalizedValue !== '' && normalizedValue !== null && normalizedValue !== undefined) {
																					normalizedValue = snapValueToStep(
																						normalizedValue,
																						edit.props?.step,
																						min ?? 0
																					);
																					normalizedValue = clampToMinMax(normalizedValue, selectedDatastream, edit.props);
																				}
																				if (normalizedValue !== edit.props[key]) {
																					setEdit(prev => ({ ...prev, props: { ...prev.props, [key]: normalizedValue } }));
																				}
																				if (edit.type === 'slider') {
																					updateWidgetLivePropsById(edit.id, { [key]: normalizedValue });
																				}
																			}}
																		/>
																		);
																	})()
																) : (
																	<input type='text' className='add-column' placeholder={capitalize(key)}
																		style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
																		value={edit.props[key] !== null ? edit.props[key] : ''}
																		onChange={(e) => setEdit({
																			...edit,
																			props: {
																				...edit.props,
																				[key]: e.target.value
																			}
																		})} />)
																}
											</div>
										)) : null}

								{edit.type === 'switch' ? (
									<Fragment>
										<div className='form-item'>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.activeColor || 'Active Color'}
											</label>
											<ColorPicker
												id="switch-active-color"
												color={themeColorValue(edit.props, 'color', '#C52184')}
												setColor={(c) => setEdit(prev => ({
													...prev,
													props: { ...prev.props, [themeColorKey('color')]: c }
												}))}
											/>
										</div>
										<div className='form-item'>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.inactiveColor || 'Inactive Color'}
											</label>
											<ColorPicker
												id="switch-inactive-color"
												color={themeColorValue(edit.props, 'inactiveColor', '#D1D1D6')}
												setColor={(c) => setEdit(prev => ({
													...prev,
													props: { ...prev.props, [themeColorKey('inactiveColor')]: c }
												}))}
											/>
										</div>
									</Fragment>
								) : null}

								{edit.type === 'push-button' ? (
									<Fragment>
										<div className='form-item'>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.activeColor || 'Active Color'}
											</label>
											<ColorPicker
												id="push-button-active-color"
												color={themeColorValue(edit.props, 'color', '#4FC4D3')}
												setColor={(c) => setEdit(prev => ({
													...prev,
													props: { ...prev.props, [themeColorKey('color')]: c }
												}))}
											/>
										</div>
										<div className='form-item'>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.inactiveColor || 'Inactive Color'}
											</label>
											<ColorPicker
												id="push-button-inactive-color"
												color={themeColorValue(edit.props, 'inactiveColor', 'rgba(79, 196, 211, 0.48)')}
												setColor={(c) => setEdit(prev => ({
													...prev,
													props: { ...prev.props, [themeColorKey('inactiveColor')]: c }
												}))}
											/>
										</div>
										<div className='form-item'>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.textColor || 'Text Color'}
											</label>
											<ColorPicker
												id="push-button-text-color"
												color={themeColorValue(edit.props, 'textColor', '#FFFFFF')}
												setColor={(c) => setEdit(prev => ({
													...prev,
													props: { ...prev.props, [themeColorKey('textColor')]: c }
												}))}
											/>
										</div>
										<div className='form-item'>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.inactiveTextColor || 'Inactive Text Color'}
											</label>
											<ColorPicker
												id="push-button-inactive-text-color"
												color={themeColorValue(edit.props, 'inactiveTextColor', '#8CE7EF')}
												setColor={(c) => setEdit(prev => ({
													...prev,
													props: { ...prev.props, [themeColorKey('inactiveTextColor')]: c }
												}))}
											/>
										</div>
									</Fragment>
								) : null}

								{edit.type === 'segment-switch' ? (
									<Fragment>
										<div className='form-item'>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.fontSize || 'Font Size'}
											</label>
											<input
												type='number'
												min='8'
												step='1'
												className='add-column'
												value={edit.props.fontSize ?? 16}
												onBlur={() => {
													if (edit.props.fontSize !== '') return;
													setEdit(prev => ({
														...prev,
														props: { ...prev.props, fontSize: 16 }
													}));
												}}
												onChange={(e) => {
													const nextValue = e.target.value;
													if (nextValue === '') {
														setEdit(prev => ({
															...prev,
															props: { ...prev.props, fontSize: '' }
														}));
														return;
													}
													const parsed = Number(nextValue);
													if (!Number.isFinite(parsed)) return;
													setEdit(prev => ({
														...prev,
														props: { ...prev.props, fontSize: Math.max(8, Math.round(parsed)) }
													}));
												}}
											/>
										</div>
										<div className='form-item'>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.activeBackgroundColor || 'Active Background Color'}
											</label>
											<ColorPicker
												id="segment-active-bg-color"
												color={themeColorValue(
													edit.props,
													'activeBackgroundColor',
													themeColorValue(edit.props, 'activeOptionColor', 'var(--accent-color)')
												)}
												setColor={(c) => setEdit(prev => ({
													...prev,
													props: { ...prev.props, [themeColorKey('activeBackgroundColor')]: c }
												}))}
											/>
										</div>
										<div className='form-item'>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.activeTextColor || 'Active Text Color'}
											</label>
											<ColorPicker
												id="segment-active-text-color"
												color={themeColorValue(edit.props, 'activeTextColor', 'var(--switch-thumb-color, #ffffff)')}
												setColor={(c) => setEdit(prev => ({
													...prev,
													props: { ...prev.props, [themeColorKey('activeTextColor')]: c }
												}))}
											/>
										</div>
										<div className='form-item'>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.inactiveBackgroundColor || 'Inactive Background Color'}
											</label>
											<ColorPicker
												id="segment-inactive-bg-color"
												color={themeColorValue(
													edit.props,
													'inactiveBackgroundColor',
													'var(--switch-track-unchecked, rgba(29, 41, 61, 0.5))'
												)}
												setColor={(c) => setEdit(prev => ({
													...prev,
													props: { ...prev.props, [themeColorKey('inactiveBackgroundColor')]: c }
												}))}
											/>
										</div>
										<div className='form-item'>
											<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.inactiveTextColor || 'Inactive Text Color'}
											</label>
											<ColorPicker
												id="segment-inactive-text-color"
												color={themeColorValue(edit.props, 'inactiveTextColor', 'var(--text-color, #ffffff)')}
												setColor={(c) => setEdit(prev => ({
													...prev,
													props: { ...prev.props, [themeColorKey('inactiveTextColor')]: c }
												}))}
											/>
										</div>
									</Fragment>
								) : null}

								{VALUE_COLOR_WIDGET_TYPES.includes(edit.type) ? (
									<Fragment>
										<div className="form-item">
											<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.changeColorBasedOnValue || "Change color based on value"}
											</label>
											{edit.props.changeColorBasedOnValue ? (
												<ToggleOn
													style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
													onClick={() => setEdit({ ...edit, props: { ...edit.props, changeColorBasedOnValue: false } })} />
											) : (
												<ToggleOff
													style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
													onClick={() => setEdit({ ...edit, props: { ...edit.props, changeColorBasedOnValue: true } })} />
											)}
										</div>

										{edit.props.changeColorBasedOnValue ? (
											<Fragment>
												<div className="form-item">
													<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
														{t.settingsLabels?.colorGrading || "Smooth Gradient Transition"}
													</label>
													{edit.props.colorGrading ? (
														<ToggleOn
															style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
															onClick={() => setEdit({ ...edit, props: { ...edit.props, colorGrading: false } })} />
													) : (
														<ToggleOff
															style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
															onClick={() => setEdit({ ...edit, props: { ...edit.props, colorGrading: true } })} />
													)}
												</div>

												<LabelColorSlider
													minValue={getDsMinMax(selectedDatastream).min ?? edit.props.min}
													maxValue={getDsMinMax(selectedDatastream).max ?? edit.props.max}
													stops={themeColorValue(edit.props, 'colors', [])}
													colorGrading={Boolean(edit.props.colorGrading)}
													setStops={(updated) => setEdit(prev => ({
														...prev,
														props: { ...prev.props, [themeColorKey('colors')]: updated }
													}))}
												/>
											</Fragment>
										) : (
											<div className="form-item">
												<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
													{t.settingsLabels?.color || "Fixed Color"}
												</label>
												<ColorPicker
													id="fixed-color"
													color={themeColorValue(edit.props, 'color')}
													setColor={(c) => setEdit(prev => ({
														...prev,
														props: { ...prev.props, [themeColorKey('color')]: c }
													}))}
												/>
											</div>
										)}
									</Fragment>
								) : null}

								{SHOW_UNIT_WIDGET_TYPES.includes(edit.type) ? (
									<div className="form-item">
										<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
											{t.settingsLabels?.showUnit || "Show Unit"}
										</label>
										{edit.props.showUnit !== false ? (
											<ToggleOn
												style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
												onClick={() => setEdit(prev => ({
													...prev,
													props: { ...prev.props, showUnit: false }
												}))}
											/>
										) : (
											<ToggleOff
												style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
												onClick={() => setEdit(prev => ({
													...prev,
													props: { ...prev.props, showUnit: true }
												}))}
											/>
										)}
									</div>
								) : null}

								{edit.type === 'label' ? (
									<Fragment>
										<div className="form-item">
											<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.showValueLine || "Show Value Line"}
											</label>
											{edit.props.showValueLine !== false ? (
												<ToggleOn
													style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
													onClick={() => setEdit(prev => ({
														...prev,
														props: { ...prev.props, showValueLine: false }
													}))}
												/>
											) : (
												<ToggleOff
													style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
													onClick={() => setEdit(prev => ({
														...prev,
														props: { ...prev.props, showValueLine: true }
													}))}
												/>
											)}
										</div>
										<div className="form-item">
											<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
												{t.settingsLabels?.labelShadowEnabled || "Enable Shadow Glow"}
											</label>
											{edit.props.labelShadowEnabled ? (
												<ToggleOn
													style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
													onClick={() => setEdit(prev => ({
														...prev,
														props: { ...prev.props, labelShadowEnabled: false }
													}))}
												/>
											) : (
												<ToggleOff
													style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
													onClick={() => setEdit(prev => ({
														...prev,
														props: { ...prev.props, labelShadowEnabled: true }
													}))}
												/>
											)}
										</div>

										{edit.props.labelShadowEnabled ? (
											<div className="form-item">
												<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
													{t.settingsLabels?.labelShadowBlur || "Shadow Blur (px)"}
												</label>
												<input
													type='number'
													min='0'
													max='120'
													step='1'
													className='add-column'
													style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
													value={edit.props.labelShadowBlur ?? 28}
													onChange={(e) => {
														const raw = e.target.value;
														const parsed = Number(raw);
														setEdit(prev => ({
															...prev,
															props: {
																...prev.props,
																labelShadowBlur: Number.isFinite(parsed) ? Math.max(0, parsed) : 0
															}
														}));
													}}
												/>
											</div>
										) : null}

										{edit.props.labelShadowEnabled ? (
											<div className="form-item">
												<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
													{t.settingsLabels?.labelShadowStrength || "Shadow Strength"}
												</label>
												<input
													type='number'
													min='0.1'
													step='0.1'
													className='add-column'
													style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
													value={edit.props.labelShadowStrength ?? 2}
													onChange={(e) => {
														const raw = e.target.value;
														const parsed = Number(raw);
														setEdit(prev => ({
															...prev,
															props: {
																...prev.props,
																labelShadowStrength: Number.isFinite(parsed) ? Math.max(0.1, parsed) : 0.1
															}
														}));
													}}
												/>
											</div>
										) : null}

										{edit.props.labelShadowEnabled ? (
											<div className="form-item">
												<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
													{t.settingsLabels?.labelShadowUseValueColor || "Shadow follows text/value color"}
												</label>
												{edit.props.labelShadowUseValueColor ? (
													<ToggleOn
														style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
														onClick={() => setEdit(prev => ({
															...prev,
															props: { ...prev.props, labelShadowUseValueColor: false }
														}))}
													/>
												) : (
													<ToggleOff
														style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
														onClick={() => setEdit(prev => ({
															...prev,
															props: { ...prev.props, labelShadowUseValueColor: true }
														}))}
													/>
												)}
											</div>
										) : null}

										{edit.props.labelShadowEnabled && !edit.props.labelShadowUseValueColor ? (
											<div className="form-item">
												<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
													{t.settingsLabels?.labelShadowChangeColorBasedOnValue || "Change Shadow Color Based On Value"}
												</label>
												{edit.props.labelShadowChangeColorBasedOnValue ? (
													<ToggleOn
														style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
														onClick={() => setEdit(prev => ({
															...prev,
															props: { ...prev.props, labelShadowChangeColorBasedOnValue: false }
														}))}
													/>
												) : (
													<ToggleOff
														style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
														onClick={() => setEdit(prev => ({
															...prev,
															props: { ...prev.props, labelShadowChangeColorBasedOnValue: true }
														}))}
													/>
												)}
											</div>
										) : null}

										{edit.props.labelShadowEnabled && !edit.props.labelShadowUseValueColor && edit.props.labelShadowChangeColorBasedOnValue ? (
											<div className="form-item">
												<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
													{t.settingsLabels?.labelShadowColorGrading || "Shadow Color Grading"}
												</label>
												{edit.props.labelShadowColorGrading ? (
													<ToggleOn
														style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
														onClick={() => setEdit(prev => ({
															...prev,
															props: { ...prev.props, labelShadowColorGrading: false }
														}))}
													/>
												) : (
													<ToggleOff
														style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
														onClick={() => setEdit(prev => ({
															...prev,
															props: { ...prev.props, labelShadowColorGrading: true }
														}))}
													/>
												)}
											</div>
										) : null}

										{edit.props.labelShadowEnabled && !edit.props.labelShadowUseValueColor && edit.props.labelShadowChangeColorBasedOnValue ? (
											<LabelColorSlider
												minValue={getDsMinMax(selectedDatastream).min ?? edit.props.min}
												maxValue={getDsMinMax(selectedDatastream).max ?? edit.props.max}
												stops={themeColorValue(edit.props, 'labelShadowColors', [])}
												colorGrading={Boolean(edit.props.labelShadowColorGrading)}
												setStops={(updated) => setEdit(prev => ({
													...prev,
													props: { ...prev.props, [themeColorKey('labelShadowColors')]: updated }
												}))}
											/>
										) : null}

										{edit.props.labelShadowEnabled && !edit.props.labelShadowUseValueColor && !edit.props.labelShadowChangeColorBasedOnValue ? (
											<div className="form-item">
												<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
													{t.settingsLabels?.labelShadowColor || "Shadow Color"}
												</label>
												<ColorPicker
													id="label-shadow-color"
													color={themeColorValue(
														edit.props,
														'labelShadowColor',
														themeColorValue(edit.props, 'color', '#00f0f0')
													)}
													setColor={(c) => setEdit(prev => ({
														...prev,
														props: { ...prev.props, [themeColorKey('labelShadowColor')]: c }
													}))}
												/>
											</div>
										) : null}
									</Fragment>
								) : null}
								{isWaterNetworkWidgetType(edit.type) ? (
									<div className="form-item">
										<label className="form-lbl" style={{ color: 'var(--header-color)' }}>
											{t.settingsLabels?.linkToNearby || 'Link To Nearby Water Widgets'}
										</label>
										{edit.props.linkToNearby !== false ? (
											<ToggleOn
												style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
												onClick={() => setEdit(prev => ({
													...prev,
													props: { ...prev.props, linkToNearby: false }
												}))}
											/>
										) : (
											<ToggleOff
												style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
												onClick={() => setEdit(prev => ({
													...prev,
													props: { ...prev.props, linkToNearby: true }
												}))}
											/>
										)}
									</div>
								) : null}
								{
									(edit.type === 'menu' || edit.type === 'segment-switch') ? (
										<div>
											<h4 className='form-lbl' style={{ color: 'var(--header-color)', marginTop: '20px' }}>{t.optionsHeading}</h4>
											<DragDropOptionList edit={edit} setEdit={setEdit} />

											<div className='form-item'>
												<button
													style={{ width: '125px', marginTop: '40px' }}
													className='add-btn'
													onClick={() => {
														setEdit(prev => ({
															...prev,
															props: {
																...prev.props,
																options: [...(prev.props.options || []), { value: ((prev.props.options || []).length > 0 ? (prev.props.options || [])[(prev.props.options || []).length - 1].value + 1 : 0), label: 'Option ' + (((prev.props.options || []).length > 0 ? (prev.props.options || [])[(prev.props.options || []).length - 1].value + 1 : 0)) }],
															},
														}));
													}}
												>
													<span>{t.addOption}</span>
												</button>
											</div>
										</div>
									) : edit.type === 'pipe' ? (<Fragment>
	<div className='form-item'>
		<label className='form-lbl' style={{ color: 'var(--header-color)' }}>{t.pipeDirectionLabel || 'Pipe Direction'}</label>
		<Select
			classNamePrefix="react-select"
			value={[
				{ label: directionLabels.horizontal || 'Horizontal', value: 'horizontal' },
				{ label: directionLabels.vertical || 'Vertical', value: 'vertical' }
			].find(o => o.value === edit.props.direction)}
			options={[
				{ label: directionLabels.horizontal || 'Horizontal', value: 'horizontal' },
				{ label: directionLabels.vertical || 'Vertical', value: 'vertical' }
			]}
			onChange={selectedOption => {
				const nextDirection = selectedOption ? selectedOption.value : 'horizontal';
				const nextFlowDirection = nextDirection === 'vertical'
					? (['top-to-bottom', 'bottom-to-top'].includes(edit.props.flowDirection) ? edit.props.flowDirection : 'top-to-bottom')
					: (['left-to-right', 'right-to-left'].includes(edit.props.flowDirection) ? edit.props.flowDirection : 'left-to-right');

				setEdit({
					...edit,
					props: {
						...edit.props,
						direction: nextDirection,
						flowDirection: nextFlowDirection
					}
				});
			}}
			isClearable={false}
		/>
	</div>

	<div className='form-item'>
		<label className='form-lbl' style={{ color: 'var(--header-color)' }}>{t.waterFlowDirectionLabel || 'Water Flow Direction'}</label>
		<Select
			classNamePrefix="react-select"
			value={(edit.props.direction === 'vertical'
				? [
					{ label: directionLabels.topToBottom || 'Top to Bottom', value: 'top-to-bottom' },
					{ label: directionLabels.bottomToTop || 'Bottom to Top', value: 'bottom-to-top' }
				]
				: [
					{ label: directionLabels.leftToRight || 'Left to Right', value: 'left-to-right' },
					{ label: directionLabels.rightToLeft || 'Right to Left', value: 'right-to-left' }
				]
			).find(o => o.value === edit.props.flowDirection) || (edit.props.direction === 'vertical'
				? { label: directionLabels.topToBottom || 'Top to Bottom', value: 'top-to-bottom' }
				: { label: directionLabels.leftToRight || 'Left to Right', value: 'left-to-right' })}
			options={edit.props.direction === 'vertical'
				? [
					{ label: directionLabels.topToBottom || 'Top to Bottom', value: 'top-to-bottom' },
					{ label: directionLabels.bottomToTop || 'Bottom to Top', value: 'bottom-to-top' }
				]
				: [
					{ label: directionLabels.leftToRight || 'Left to Right', value: 'left-to-right' },
					{ label: directionLabels.rightToLeft || 'Right to Left', value: 'right-to-left' }
				]}
			onChange={selectedOption => setEdit({
				...edit,
				props: {
					...edit.props,
					flowDirection: selectedOption
						? selectedOption.value
						: (edit.props.direction === 'vertical' ? 'top-to-bottom' : 'left-to-right')
				}
			})}
			isClearable={false}
		/>
	</div>

	<div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
		<div className='form-item'>
			<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Frame Background</label>
			<ColorPicker
				id="pipe-frame-bg"
				color={themeColorValue(edit.props, 'frameBg')}
				setColor={(c) => setEdit(prev => ({
					...prev,
					props: { ...prev.props, [themeColorKey('frameBg')]: c }
				}))}
			/>
		</div>

		<div className='form-item'>
			<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Frame Border Color</label>
			<ColorPicker
				id="pipe-frame-border-color"
				color={themeColorValue(edit.props, 'frameBorderColor')}
				setColor={(c) => setEdit(prev => ({
					...prev,
					props: { ...prev.props, [themeColorKey('frameBorderColor')]: c }
				}))}
			/>
		</div>

		<div className='form-item'>
			<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Water Color</label>
			<ColorPicker
				id="pipe-water-color"
				color={themeColorValue(edit.props, 'color')}
				setColor={(c) => setEdit(prev => ({
					...prev,
					props: { ...prev.props, [themeColorKey('color')]: c }
				}))}
			/>
		</div>

		<div className='form-item'>
			<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe Shell Color</label>
			<ColorPicker
				id="pipe-shell-color"
				color={themeColorValue(edit.props, 'pipeColor')}
				setColor={(c) => setEdit(prev => ({
					...prev,
					props: { ...prev.props, [themeColorKey('pipeColor')]: c }
				}))}
			/>
		</div>

		<div className='form-item'>
			<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe Background Color</label>
			<ColorPicker
				id="pipe-background-color"
				color={themeColorValue(edit.props, 'pipeBackgroundColor', 'rgba(18, 28, 40, 0.55)')}
				setColor={(c) => setEdit(prev => ({
					...prev,
					props: { ...prev.props, [themeColorKey('pipeBackgroundColor')]: c }
				}))}
			/>
		</div>

		<div className='form-item'>
			<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe Glass Color</label>
			<ColorPicker
				id="pipe-glass-color"
				color={themeColorValue(
					edit.props,
					'pipeGlassColor',
					themeColorValue(edit.props, 'pipeColor')
				)}
				setColor={(c) => setEdit(prev => ({
					...prev,
					props: { ...prev.props, [themeColorKey('pipeGlassColor')]: c }
				}))}
			/>
		</div>

		<div className='form-item'>
			<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe Border Color</label>
			<ColorPicker
				id="pipe-border-color"
				color={themeColorValue(edit.props, 'pipeBorderColor')}
				setColor={(c) => setEdit(prev => ({
					...prev,
					props: { ...prev.props, [themeColorKey('pipeBorderColor')]: c }
				}))}
			/>
		</div>

		<div className='form-item'>
			<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Clamp Color</label>
			<ColorPicker
				id="pipe-clamp-color"
				color={themeColorValue(edit.props, 'clampColor')}
				setColor={(c) => setEdit(prev => ({
					...prev,
					props: { ...prev.props, [themeColorKey('clampColor')]: c }
				}))}
			/>
		</div>

		<div className='form-item'>
			<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Frame Border Width</label>
			<input
				type='number'
				className='add-column'
				min='0'
				max='12'
				step='1'
				style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
				value={edit.props.frameBorderWidth ?? 2}
				onChange={(e) => setEdit(prev => ({
					...prev,
					props: { ...prev.props, frameBorderWidth: Number(e.target.value || 0) }
				}))}
			/>
		</div>

		<div className='form-item'>
			<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Frame Radius</label>
			<input
				type='number'
				className='add-column'
				min='0'
				max='80'
				step='1'
				style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
				value={edit.props.frameRadius ?? 18}
				onChange={(e) => setEdit(prev => ({
					...prev,
					props: { ...prev.props, frameRadius: Number(e.target.value || 0) }
				}))}
			/>
		</div>

		<div className='form-item'>
			<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Frame Padding</label>
			<input
				type='number'
				className='add-column'
				min='0'
				max='80'
				step='1'
				style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
				value={edit.props.framePadding ?? 0}
				onChange={(e) => setEdit(prev => ({
					...prev,
					props: { ...prev.props, framePadding: Number(e.target.value || 0) }
				}))}
			/>
		</div>

		<div className='form-item'>
			<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe Thickness</label>
			<input
				type='number'
				className='add-column'
				min='20'
				max='140'
				step='1'
				style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
				value={edit.props.pipeHeight ?? 70}
				onChange={(e) => setEdit(prev => ({
					...prev,
					props: { ...prev.props, pipeHeight: Number(e.target.value || 0) }
				}))}
			/>
		</div>

		<div className='form-item'>
			<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe Width Percent</label>
			<input
				type='number'
				className='add-column'
				min='20'
				max='100'
				step='1'
				style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
				value={edit.props.pipeWidthPercent ?? 100}
				onChange={(e) => setEdit(prev => ({
					...prev,
					props: { ...prev.props, pipeWidthPercent: Number(e.target.value || 0) }
				}))}
			/>
		</div>

		<div className='form-item'>
			<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Title Font Size</label>
			<input
				type='number'
				className='add-column'
				min='8'
				max='120'
				step='1'
				style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
				value={edit.props.titleFontSize ?? 34}
				onChange={(e) => setEdit(prev => ({
					...prev,
					props: { ...prev.props, titleFontSize: Number(e.target.value || 0) }
				}))}
			/>
		</div>

		<div className='form-item'>
			<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Status Font Size</label>
			<input
				type='number'
				className='add-column'
				min='8'
				max='140'
				step='1'
				style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
				value={edit.props.statusFontSize ?? 30}
				onChange={(e) => setEdit(prev => ({
					...prev,
					props: { ...prev.props, statusFontSize: Number(e.target.value || 0) }
				}))}
			/>
		</div>
	</div>
</Fragment>) : edit.type === 'elbow' ? (
										<>
											<div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>{t.directionLabel || 'Direction'}</label>
													<Select
														classNamePrefix="react-select"
														value={[
															{ label: directionLabels.topLeft || 'Top-Left', value: 'top-left' },
															{ label: directionLabels.topRight || 'Top-Right', value: 'top-right' },
															{ label: directionLabels.bottomLeft || 'Bottom-Left', value: 'bottom-left' },
															{ label: directionLabels.bottomRight || 'Bottom-Right', value: 'bottom-right' }
														].find(o => o.value === (edit.props.direction || 'top-right')) || { label: directionLabels.topRight || 'Top-Right', value: 'top-right' }}
														options={[
															{ label: directionLabels.topLeft || 'Top-Left', value: 'top-left' },
															{ label: directionLabels.topRight || 'Top-Right', value: 'top-right' },
															{ label: directionLabels.bottomLeft || 'Bottom-Left', value: 'bottom-left' },
															{ label: directionLabels.bottomRight || 'Bottom-Right', value: 'bottom-right' }
														]}
														onChange={selectedOption => setEdit({
															...edit,
															props: { ...edit.props, direction: selectedOption ? selectedOption.value : 'top-right' }
														})}
														isClearable={false}
													/>
												</div>

												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe Thickness</label>
													<input
														type='number'
														className='add-column'
														min='40'
														max='120'
														step='1'
														style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
														value={edit.props.pipeHeight ?? 70}
														onChange={(e) => setEdit(prev => ({
															...prev,
															props: { ...prev.props, pipeHeight: Number(e.target.value || 0) }
														}))}
													/>
												</div>

												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Water Flow Color</label>
													<ColorPicker
														id="elbow-water-color"
														color={themeColorValue(edit.props, 'color', '#76B6EA')}
														setColor={(c) => setEdit(prev => ({
															...prev,
															props: { ...prev.props, [themeColorKey('color')]: c }
														}))}
													/>
												</div>

												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe Shell Color</label>
													<ColorPicker
														id="elbow-shell-color"
														color={themeColorValue(edit.props, 'pipeColor')}
														setColor={(c) => setEdit(prev => ({
															...prev,
															props: { ...prev.props, [themeColorKey('pipeColor')]: c }
														}))}
													/>
												</div>

												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe Background Color</label>
													<ColorPicker
														id="elbow-background-color"
														color={themeColorValue(edit.props, 'pipeBackgroundColor', 'rgba(18, 28, 40, 0.55)')}
														setColor={(c) => setEdit(prev => ({
															...prev,
															props: { ...prev.props, [themeColorKey('pipeBackgroundColor')]: c }
														}))}
													/>
												</div>

												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe Glass Color</label>
													<ColorPicker
														id="elbow-glass-color"
														color={themeColorValue(edit.props, 'pipeGlassColor', themeColorValue(edit.props, 'pipeColor'))}
														setColor={(c) => setEdit(prev => ({
															...prev,
															props: { ...prev.props, [themeColorKey('pipeGlassColor')]: c }
														}))}
													/>
												</div>

												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe Border Color</label>
													<ColorPicker
														id="elbow-border-color"
														color={themeColorValue(edit.props, 'pipeBorderColor')}
														setColor={(c) => setEdit(prev => ({
															...prev,
															props: { ...prev.props, [themeColorKey('pipeBorderColor')]: c }
														}))}
													/>
												</div>

												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Clamp Color</label>
													<ColorPicker
														id="elbow-clamp-color"
														color={themeColorValue(edit.props, 'clampColor')}
														setColor={(c) => setEdit(prev => ({
															...prev,
															props: { ...prev.props, [themeColorKey('clampColor')]: c }
														}))}
													/>
												</div>
											</div>
										</>
													) : edit.type === 'water-pump' ? (
										<>
											<div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>{t.directionLabel || 'Direction'}</label>
													<Select
														classNamePrefix="react-select"
														value={[
															{ label: directionLabels.leftToRight || 'Left to Right', value: 'left-to-right' },
															{ label: directionLabels.rightToLeft || 'Right to Left', value: 'right-to-left' }
														].find(o => o.value === (edit.props.direction || 'left-to-right')) || { label: directionLabels.leftToRight || 'Left to Right', value: 'left-to-right' }}
														options={[
															{ label: directionLabels.leftToRight || 'Left to Right', value: 'left-to-right' },
															{ label: directionLabels.rightToLeft || 'Right to Left', value: 'right-to-left' }
														]}
														onChange={selectedOption => setEdit({
															...edit,
															props: { ...edit.props, direction: selectedOption ? selectedOption.value : 'left-to-right' }
														})}
														isClearable={false}
													/>
												</div>

												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe / Elbow Thickness</label>
													<input
														type='number'
														className='add-column'
														min='40'
														max='120'
														step='1'
														style={{ padding: '4px 10px', color: 'var(--cell--color)', caretColor: 'var(--cell--color)' }}
														value={edit.props.pipeHeight ?? 70}
														onChange={(e) => setEdit(prev => ({
															...prev,
															props: { ...prev.props, pipeHeight: Number(e.target.value || 0) }
														}))}
													/>
												</div>

												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Water Flow Color</label>
													<ColorPicker
														id="pump-water-color"
														color={themeColorValue(edit.props, 'color', '#76B6EA')}
														setColor={(c) => setEdit(prev => ({
															...prev,
															props: { ...prev.props, [themeColorKey('color')]: c }
														}))}
													/>
												</div>

												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe Shell Color</label>
													<ColorPicker
														id="pump-shell-color"
														color={themeColorValue(edit.props, 'pipeColor')}
														setColor={(c) => setEdit(prev => ({
															...prev,
															props: { ...prev.props, [themeColorKey('pipeColor')]: c }
														}))}
													/>
												</div>

												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe Background Color</label>
													<ColorPicker
														id="pump-background-color"
														color={themeColorValue(edit.props, 'pipeBackgroundColor', 'rgba(18, 28, 40, 0.55)')}
														setColor={(c) => setEdit(prev => ({
															...prev,
															props: { ...prev.props, [themeColorKey('pipeBackgroundColor')]: c }
														}))}
													/>
												</div>

												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe Glass Color</label>
													<ColorPicker
														id="pump-glass-color"
														color={themeColorValue(edit.props, 'pipeGlassColor', themeColorValue(edit.props, 'pipeColor'))}
														setColor={(c) => setEdit(prev => ({
															...prev,
															props: { ...prev.props, [themeColorKey('pipeGlassColor')]: c }
														}))}
													/>
												</div>

												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Pipe Border Color</label>
													<ColorPicker
														id="pump-border-color"
														color={themeColorValue(edit.props, 'pipeBorderColor')}
														setColor={(c) => setEdit(prev => ({
															...prev,
															props: { ...prev.props, [themeColorKey('pipeBorderColor')]: c }
														}))}
													/>
												</div>

												<div className='form-item'>
													<label className='form-lbl' style={{ color: 'var(--header-color)' }}>Clamp Color</label>
													<ColorPicker
														id="pump-clamp-color"
														color={themeColorValue(edit.props, 'clampColor')}
														setColor={(c) => setEdit(prev => ({
															...prev,
															props: { ...prev.props, [themeColorKey('clampColor')]: c }
														}))}
													/>
												</div>
											</div>
										</>
													) : edit.dataId > 0 && (edit.type === 'slider' || edit.type === 'number') ? (
														<>
															<div className='form-item'>
																<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
																	{t.settingsLabels?.buttonBgColor || 'Buttons Background Color'}
																</label>
																<ColorPicker
																	id="buttonBgColor"
																	color={themeColorValue(
																		edit.props,
																		'buttonBgColor'
																	)}
																	setColor={(c) => setEdit(prev => ({
																		...prev,
																		props: { ...prev.props, [themeColorKey('buttonBgColor')]: c }
																	}))}
																/>
															</div>

															<div className='form-item'>
																<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
																	{t.settingsLabels?.buttonTextColor || 'Buttons Text Color (+ / -)'}
																</label>
																<ColorPicker
																	id="buttonTextColor"
																	color={themeColorValue(
																		edit.props,
																		'color',
																		selectedDatastream?.color || '#C52184'
																	)}
																	setColor={(c) => setEdit(prev => ({
																		...prev,
																		props: { ...prev.props, [themeColorKey('color')]: c }
																	}))}
																/>
															</div>

															{edit.type === 'number' ? (
																<div className='form-item'>
																	<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
																		{t.settingsLabels?.buttonHoverBgColor || 'Buttons Hover Background Color'}
																	</label>
																	<ColorPicker
																		id="buttonHoverBgColor"
																		color={themeColorValue(
																			edit.props,
																			'buttonHoverBgColor',
																			themeColorValue(edit.props, 'buttonBgColor')
																		)}
																		setColor={(c) => setEdit(prev => ({
																			...prev,
																			props: { ...prev.props, [themeColorKey('buttonHoverBgColor')]: c }
																		}))}
																	/>
																</div>
															) : null}

															<div className='form-item'>
																<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
																	{t.settingsLabels?.changeColorBasedOnValue || 'Change Color Based On Value'}
																</label>
																{edit.props.changeColorBasedOnValue ? (
																	<ToggleOn
																		style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
																		onClick={() => setEdit(prev => ({
																			...prev,
																			props: { ...prev.props, changeColorBasedOnValue: false }
																		}))}
																	/>
																) : (
																	<ToggleOff
																		style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
																		onClick={() => setEdit(prev => ({
																			...prev,
																			props: { ...prev.props, changeColorBasedOnValue: true }
																		}))}
																	/>
																)}
															</div>

															{edit.props.changeColorBasedOnValue ? (
																<>
																	<div className='form-item'>
																		<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
																			{t.settingsLabels?.colorGrading || 'Smooth Gradient Transition'}
																		</label>
																		{edit.props.colorGrading ? (
																			<ToggleOn
																				style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
																				onClick={() => setEdit(prev => ({
																					...prev,
																					props: { ...prev.props, colorGrading: false }
																				}))}
																			/>
																		) : (
																			<ToggleOff
																				style={{ color: 'var(--accent)', width: '48px', height: '40px', cursor: 'pointer' }}
																				onClick={() => setEdit(prev => ({
																					...prev,
																					props: { ...prev.props, colorGrading: true }
																				}))}
																			/>
																		)}
																	</div>

																	<LabelColorSlider
																		minValue={getDsMinMax(selectedDatastream).min ?? edit.props.min}
																		maxValue={getDsMinMax(selectedDatastream).max ?? edit.props.max}
																		stops={themeColorValue(edit.props, 'colors', [])}
																		colorGrading={Boolean(edit.props.colorGrading)}
																		setStops={(updated) => setEdit(prev => ({
																			...prev,
																			props: { ...prev.props, [themeColorKey('colors')]: updated }
																		}))}
																	/>
																</>
															) : (
																<div className='form-item'>
																	<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
																		{t.settingsLabels?.valueColor || 'Widget Value Color'}
																	</label>
																	<ColorPicker
																		id="valueColor"
																		color={themeColorValue(
																			edit.props,
																			'valueColor',
																			themeColorValue(
																				edit.props,
																				'color',
																				selectedDatastream?.color || '#C52184'
																			)
																		)}
																		setColor={(c) => setEdit(prev => ({
																			...prev,
																			props: { ...prev.props, [themeColorKey('valueColor')]: c }
																		}))}
																	/>
																</div>
															)}

															{edit.type === 'number' ? (
																<div className='form-item'>
																	<label className='form-lbl' style={{ color: 'var(--header-color)' }}>
																		{t.settingsLabels?.valueHoverColor || 'Widget Value Hover Color'}
																	</label>
																	<ColorPicker
																		id="valueHoverColor"
																		color={themeColorValue(
																			edit.props,
																			'valueHoverColor',
																			themeColorValue(
																				edit.props,
																				'valueColor',
																				themeColorValue(
																					edit.props,
																					'color',
																					selectedDatastream?.color || '#C52184'
																				)
																			)
																		)}
																		setColor={(c) => setEdit(prev => ({
																			...prev,
																			props: { ...prev.props, [themeColorKey('valueHoverColor')]: c }
																		}))}
																	/>
																</div>
															) : null}

															{edit.type === 'slider' ? (
																<div className='form-item'>
																	<label className='form-lbl' style={{ color: 'var(--header-color)' }}>{t.sliderDirection || 'Direction'}</label>
																	<Select
																		classNamePrefix="react-select"
																		value={[
																			{ label: directionLabels.leftToRightShort || 'Left to Right (ltr)', value: 'ltr' },
																			{ label: directionLabels.rightToLeftShort || 'Right to Left (rtl)', value: 'rtl' }
																		].find(o => o.value === edit.props.direction)}
																		options={[
																			{ label: directionLabels.leftToRightShort || 'Left to Right (ltr)', value: 'ltr' },
																			{ label: directionLabels.rightToLeftShort || 'Right to Left (rtl)', value: 'rtl' }
																		]}
																		onChange={selectedOption => setEdit({
																			...edit,
																			props: { ...edit.props, direction: selectedOption ? selectedOption.value : '' }
																		})}
																		isClearable={true}
																	/>
																</div>
															) : null}
														</>
													) : edit.dataId > 0 && edit.type === 'image-button' ? (
										<div>
											<h4 className='form-lbl' style={{ color: 'var(--header-color)', marginTop: '20px' }}>{t.imagesHeading}</h4>
											<ImageButtonSettings edit={edit} setEdit={setEdit} />
										</div>
									) : null}

							</div>
							<div
								style={{
									flex: 1,
									width: '100%',
									height: '100%',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									backgroundColor: 'var(--header-background)'
								}}
							>
								{(() => {
									const previewDatastreamsBase = [
										...((deviceDatastream || []).filter(ds => ds)),
										...((validDatastreams || []).filter(ds => ds)),
									];

									const selectedDs = findDatastreamByReference(
										previewDatastreamsBase,
										edit.dataId,
										edit.dataPin,
									);
									const previewDatastreams = previewDatastreamsBase.map((datastream) => {
										if (String(datastream?.id) !== String(selectedDs?.id)) {
											return datastream;
										}

										const nextDatastream = { ...datastream };
										const liveValue = edit.props?.value;
										const shouldUseEditorLiveValue =
											edit.type === 'slider'
											|| !selectedDs;

										if (edit.props?.min !== undefined && edit.props?.min !== null && edit.props?.min !== '') {
											nextDatastream.min = edit.props.min;
										}
										if (edit.props?.max !== undefined && edit.props?.max !== null && edit.props?.max !== '') {
											nextDatastream.max = edit.props.max;
										}
										if (edit.props?.unit !== undefined && edit.props?.unit !== null && String(edit.props.unit).trim() !== '') {
											nextDatastream.unit = edit.props.unit;
										}
										if (shouldUseEditorLiveValue && liveValue !== undefined && liveValue !== null && liveValue !== '') {
											nextDatastream.value = liveValue;
											nextDatastream.defaultValue = liveValue;
										}

										return nextDatastream;
									});
									const previewDims =
										edit.type === ENVIRONMENTAL_STATUS_WIDGET_TYPE
											? {
												width: Math.max(220, Math.min(360, Math.round((Number(dims?.width) || 1280) * 0.26))),
												height: Math.max(240, Math.min(320, Math.round((Number(dims?.height) || 720) * 0.32))),
											}
											: {
												width: 268,
												height: 268,
											};

									let pinForWidgetProps = null;
									if ((edit.type === 'chart' || edit.type === 'table') && edit.props?.curves?.length > 0) {
										pinForWidgetProps = edit.props.curves[0].pin;
									} else {
										pinForWidgetProps = selectedDs?.pin;
									}

									return (
										<Widget
											transparentBackground={edit.transparentBackground}
											draggable={false}
											title={preserveTitleSpaces(edit.title.replace(/^\[.*?\]\s*/, ''))}
											dataId={edit.dataId}
											dataPin={pinForWidgetProps || ''}
											decimals={selectedDs?.decimals || ''}
											deviceId={edit.deviceId}
											resizeWidget={resizeWidget}
											resize={false}
											id={edit.type + '-' + edit.id}
											width={previewDims.width}
											height={previewDims.height}
											type={edit.type}
											defaultDivHeight={defaultDivHeight}
											defaultDivWidth={defaultDivWidth}
											extraProps={edit.props}
											onTerminalCleared={handleTerminalCleared}
											datastreams={previewDatastreams}
											onWidgetPropsChange={edit.type === 'slider'
												? (partialProps) => updateWidgetLivePropsById(edit.id, partialProps, { syncEdit: true })
												: undefined}
										/>
									);
								})()}
							</div>

						</div>
						<div className='modal-footer' style={{ backgroundColor: 'var(--header-background)' }}>
							<button className='btn btn-cancel'
								onClick={() => setEdit({
									open: false,
									id: '',
									deviceId: 0,
									dataId: 0,
									dataPin: '',
									title: '',
									transparentBackground: false,
									type: '',
									width: 0,
									height: 0,
									top: 0,
									left: 0,
									props: {},
									showCurves: []
								})}
							>{t.cancel}</button>
							<button className='btn btn-apply'
								onClick={() => {
									const currentWidgets = dashboard?.widgets || [];
									const metrics = getEditableMetrics();
									const editDatastreamSource = devices?.length > 0
										? ((((devices || []).find(device => Number(device?.id) === Number(edit.deviceId)))?.template?.datastreams || []).filter(ds => ds))
										: validDatastreams;
									const matchedEditDatastream = findDatastreamByReference(
										editDatastreamSource,
										edit.dataId,
										edit.dataPin,
									);
									const resolvedEditDataId = matchedEditDatastream?.id ?? edit.dataId ?? 0;
									const resolvedEditDataPin = normalizeDatastreamPin(
										matchedEditDatastream?.pin ?? edit.dataPin,
									);
									let geometryConflict = false;

									const updatedWidgets = currentWidgets.map((wd) => {
										if (wd.id !== edit.id) {
											return wd;
										}

										let newProps = sanitizeWidgetFontSizeProps(cloneDashboardValue(edit.props || {}));
										Object.keys(newProps).forEach((k) => {
											if (typeof (wd.extra_props?.[k]) === 'number' && !['speed'].includes(k)) {
												if (!isNaN(parseFloat(newProps[k])) && isFinite(newProps[k])) {
													newProps[k] = parseFloat(newProps[k]);
												}
											}
										});

										if (Number(edit.dataId) > 0) {
											newProps = stripDatastreamManagedProps(newProps, edit.type);
										}

										const nextTitle = edit.type === 'text-title'
											? String(edit.props?.title || '').trim()
											: edit.title.replace(/^\[.*?\]\s*/, '').trim();

										let nextWidget = {
											...wd,
											extra_props: newProps,
											title: nextTitle,
											transparentBackground: edit.transparentBackground,
											data_id: resolvedEditDataId,
											data_pin: resolvedEditDataPin,
											...(devices?.length > 0 && { device_id: edit.deviceId })
										};
										const disabledFreeMovement = (
											isWidgetFreeMovementEnabled(wd) &&
											!isWidgetFreeMovementEnabled(nextWidget)
										);

										if (showFreeGeometrySettings) {
											const currentRect = getWidgetRectPx(wd, metrics);
											const canvasWidth = Number.isFinite(metrics?.width) ? metrics.width : null;
											const canvasHeight = Number.isFinite(metrics?.height) ? metrics.height : null;

											const nextWidthPx = roundGeometryValue(Math.max(
												1,
												isPercentageLayout
													? geometryWidthValue
													: toLengthInputNumber(edit.width, currentRect.width),
											));
											const nextHeightPx = roundGeometryValue(Math.max(
												1,
												isPercentageLayout
													? geometryHeightValue
													: toLengthInputNumber(edit.height, currentRect.height),
											));

											let nextLeftPx = roundGeometryValue(Math.max(
												0,
												isPercentageLayout
													? geometryOriginLeft + geometryLeftValue
													: toLengthInputNumber(edit.left, currentRect.left),
											));
											let nextTopPx = roundGeometryValue(Math.max(
												0,
												isPercentageLayout
													? geometryOriginTop + geometryTopValue
													: toLengthInputNumber(edit.top, currentRect.top),
											));

											if (canvasWidth !== null) {
												const maxLeft = Math.max(0, canvasWidth - nextWidthPx);
												nextLeftPx = Math.min(nextLeftPx, maxLeft);
											}
											if (canvasHeight !== null) {
												const maxTop = Math.max(0, canvasHeight - nextHeightPx);
												nextTopPx = Math.min(nextTopPx, maxTop);
											}
											const constrainedRect = constrainRectToBackgroundImage(
												nextLeftPx,
												nextTopPx,
												nextWidthPx,
												nextHeightPx,
												currentWidgets,
												wd.id,
											);
											const candidateRect = constrainedRect.constrained
												? constrainedRect
												: {
													left: nextLeftPx,
													top: nextTopPx,
													width: nextWidthPx,
													height: nextHeightPx,
												};
											const adjustedCandidateRect = isWidgetFreeMovementEnabled(wd) && isWaterNetworkWidgetType(wd)
												? snapWaterNetworkMoveRect(
													candidateRect,
													wd,
													currentWidgets,
													getWidgetRectPx,
													metrics,
													wd.id,
												)
												: candidateRect;

											const wouldOverlap = checkOverlap(
												adjustedCandidateRect.left,
												adjustedCandidateRect.top,
												adjustedCandidateRect.width,
												adjustedCandidateRect.height,
												currentWidgets,
												wd.id,
												wd,
											);

											if (wouldOverlap) {
												geometryConflict = true;
											} else {
												nextWidget = {
													...nextWidget,
													...(isPercentageLayout
														? serializeRectForStorage(adjustedCandidateRect, metrics)
														: {
															width: adjustedCandidateRect.width,
															height: adjustedCandidateRect.height,
															left: adjustedCandidateRect.left,
															top: adjustedCandidateRect.top,
														}),
												};
											}
										}

										if (disabledFreeMovement) {
											const sourceRect = getWidgetRectPx(nextWidget, metrics);
											let gridRect = getNearestGridRect(sourceRect, metrics);
											const hasConflictAtNearestCell = checkOverlap(
												gridRect.left,
												gridRect.top,
												gridRect.width,
												gridRect.height,
												currentWidgets,
												wd.id,
												nextWidget,
											);

											if (hasConflictAtNearestCell) {
												const fallbackSpot = findNearestEmptySpotForWidgets(
													gridRect.left,
													gridRect.top,
													gridRect.width,
													gridRect.height,
													currentWidgets.filter((widget) => widget?.id !== wd.id),
												);

												if (fallbackSpot.found) {
													gridRect = {
														...gridRect,
														left: fallbackSpot.left,
														top: fallbackSpot.top,
													};
												} else {
													geometryConflict = true;
													newProps = {
														...newProps,
														freeMovement: true,
													};
													nextWidget = {
														...nextWidget,
														extra_props: newProps,
													};
												}
											}

											if (!isWidgetFreeMovementEnabled(nextWidget)) {
												nextWidget = storeWidgetPosition(nextWidget, gridRect, metrics);
											}
										}

										return nextWidget;
									});

									setDashboard({
										...dashboard,
										widgets: updatedWidgets
									});
									clearWidgetLivePropsById(edit.id);

									if (geometryConflict) {
										setAlertMessage(
											t.noOverlapOnBackgroundImage ||
											'Widgets cannot overlap, even on top of a background image.'
										);
										setAlertType('warning');
										setShowAlert(true);
									}

									setEdit({
										open: false,
										deviceId: 0,
										id: '',
										dataId: 0,
										dataPin: '',
										title: '',
										type: '',
										transparentBackground: false,
										width: 0,
										height: 0,
										top: 0,
										left: 0,
										props: {},
										showCurves: []
									});
								}}>{t.save}</button>
						</div>
					</div>
				</div> : null
				}
				{
					mode === 'edit' && permissions['edit_templates'] ?
						<Fragment>
							<div className='widgets-box'>
								<div className='widgets-box-header'>
									<div className='widget-box-icon'>
										<div className='widget-box-icon-layer widget-box-icon-base' />

										<div className='widget-box-icon-layer widget-box-icon-layer-5' />

										<div className='widget-box-icon-layer widget-box-icon-layer-4' />

										<div className='widget-box-icon-layer widget-box-icon-layer-3' />

										<div className='widget-box-icon-layer widget-box-icon-layer-2' />

										<div className='widget-box-icon-layer widget-box-icon-layer-1'>
											<WidgetBoxFirstLayer />
										</div>
									</div>

									<h2 className='widgets-box-title'>{t.widgetBoxTitle}</h2>
								</div>

								<WidgetBoxDivider />

								<div className='widgets-box-search'>
									<input
										type='search'
										className='widgets-box-search-input'
										value={widgetSearchQuery}
										onChange={(event) => setWidgetSearchQuery(event.target.value)}
										placeholder={t.widgetSearchPlaceholder || 'Search widgets'}
										aria-label={t.widgetSearchPlaceholder || 'Search widgets'}
									/>
								</div>

								<div className='widgets-list'>
									{filteredWidgetOptions.length > 0
										? filteredWidgetOptions.map(w => w)
										: (
											<div className='widgets-box-empty'>
												{t.widgetSearchEmpty || 'No widgets found'}
											</div>
										)}
								</div>
							</div>

							<div
								className='diverted-dashboard'
								id='diverted'
								dir='ltr'
								onDragOver={handleDashboardDragOver}
								onDrop={drop}
							>
								{mode === 'edit' && !isFixedWidthMode && fixedWidthGuide?.width ? (
									<div
										aria-hidden="true"
										style={{
											position: 'absolute',
											inset: 0,
											width: `${fixedWidthGuide.width}px`,
											height: '100%',
											pointerEvents: 'none',
											border: '2px dashed rgba(255, 255, 255, 0.45)',
											borderRadius: '10px',
											boxSizing: 'border-box',
											backgroundImage:
												'repeating-linear-gradient(45deg, rgba(255,255,255,0.08), rgba(255,255,255,0.08) 8px, rgba(255,255,255,0.03) 8px, rgba(255,255,255,0.03) 16px)',
											backdropFilter: 'blur(0px)',
											opacity: 0.95,
											zIndex: 2,
										}}
									>
										<div
											style={{
												position: 'sticky',
												top: 8,
												left: 8,
												width: 'fit-content',
												maxWidth: 'calc(100% - 16px)',
												margin: 8,
												padding: '6px 10px',
												borderRadius: 999,
												background: 'rgba(0,0,0,0.45)',
												color: 'rgba(255,255,255,0.92)',
												fontSize: 12,
												lineHeight: 1.2,
												letterSpacing: 0.2,
												boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
												border: '1px solid rgba(255,255,255,0.18)',
											}}
										>
											{t.fixedWidthGuideLabel ||
												'Fixed width area (keep widgets inside to enable Fixed Width)'}
										</div>
									</div>
								) : null}

								{divs}
								<div
									className={`editable-dashboard${multiSelectEnabled ? ' editable-dashboard--multi-select' : ''}`}
									id='editable'
									dir='ltr'
									onDragOver={handleDashboardDragOver}
									onDrop={drop}
									onPointerDown={handleEditablePointerDown}
									onClickCapture={handleEditableClickCapture}
								>
									<WaterNetworkOverlay
										width={editableWaterNetworkState?.width}
										height={editableWaterNetworkState?.height}
										connections={editableWaterNetworkState?.connections || []}
									/>
									{(dashboard?.widgets || []).map(widget => {
										const matchDevice = (devices || []).find(d => Number(d?.id) === Number(widget.device_id));
										const datastreamSource = matchDevice ? (matchDevice.template?.datastreams || []).filter(ds => ds) : validDatastreams;
										let pinToDisplay = null;
										const percentageRenderRect = isPercentageLayout ? getWidgetRectPx(widget) : null;
										const linkedDatastream = findDatastreamByReference(
											datastreamSource,
											widget.data_id ?? widget.dataId,
											widget.data_pin ?? widget.dataPin,
										);
										if (linkedDatastream) {
											pinToDisplay = linkedDatastream.pin;
										} else if ((widget.type === 'chart' || widget.type === 'table') && widget.extra_props?.curves?.length > 0) {
											const firstCurvePin = widget.extra_props.curves[0].pin;
											if (firstCurvePin && firstCurvePin !== 'Pin') {
												pinToDisplay = firstCurvePin;
											}
										}

										const cleanStoredTitle = widget.title.replace(/^\[.*?\]\s*/, '').trim();
										const pinFormatted = formatPinForTitle(pinToDisplay);
										const displayTitleForEditMode = pinFormatted ? `${pinFormatted} ${cleanStoredTitle}` : cleanStoredTitle;
										const dynamicProps = {
											...widget.extra_props,
											...((widget.type === 'slider' && widgetLiveProps?.[widget.id]) ? widgetLiveProps[widget.id] : {}),
										};
										if (
											widget.type === 'slider' &&
											edit.open &&
											String(edit.id) === String(widget.id)
										) {
											Object.assign(dynamicProps, edit.props || {});
										}

										return (
											<Widget
												transparentBackground={widget.transparentBackground}
												draggable={!(multiSelectEnabled && selectedWidgetIdSet.has(String(widget.id)))}
												title={preserveTitleSpaces(displayTitleForEditMode)}
												dataId={widget.data_id}
												resizeWidget={resizeWidget}
												resize={true}
												id={widget.id}
												key={widget.id}
												deviceId={widget.device_id}
												width={widget.width}
												height={widget.height}
												type={widget.type}
												selected={multiSelectEnabled && selectedWidgetIdSet.has(String(widget.id))}
												dragOffset={multiSelectEnabled && selectedWidgetIdSet.has(String(widget.id))
													? selectedGroupDragOffset
													: null}
												dataPin={linkedDatastream?.pin || widget.data_pin || widget.dataPin || pinToDisplay || ''}
											decimals={linkedDatastream?.decimals || ''}
											defaultDivHeight={defaultDivHeight}
											position='absolute'
												layoutMode={layoutMode}
												setEdit={setEdit}
												devices={devices}
													setDeviceDatastream={devices?.length > 0 ? setDeviceDatastream : null}
													top={widget.top}
													left={widget.left}
													extraProps={dynamicProps}
													settingsExtraProps={widget.type === 'slider' ? dynamicProps : widget.extra_props}
													onTerminalCleared={handleTerminalCleared}
													defaultDivWidth={defaultDivWidth}
													renderRect={percentageRenderRect}
													duplicate={duplicate}
													remove={remove}
												datastreams={datastreamSource}
												onWidgetPropsChange={widget.type === 'slider'
													? (partialProps) => updateWidgetLivePropsById(widget.id, partialProps, { syncEdit: true })
													: undefined}
											/>
										);
									})}
									{multiSelectEnabled && selectionRect ? (
										<div
											className="dashboard-selection-marquee"
											style={{
												left: `${selectionRect.left}px`,
												top: `${selectionRect.top}px`,
												width: `${selectionRect.width}px`,
												height: `${selectionRect.height}px`,
											}}
										/>
									) : null}
									{multiSelectEnabled && selectedWidgetBounds && selectedWidgetIds.length > 0 ? (
										<div
											className="dashboard-selection-bounds"
											onPointerDown={startSelectedGroupDrag}
											style={{
												left: `${selectedWidgetBounds.left}px`,
												top: `${selectedWidgetBounds.top}px`,
												width: `${selectedWidgetBounds.width}px`,
												height: `${selectedWidgetBounds.height}px`,
												transform: selectedGroupDragOffset
													? `translate(${selectedGroupDragOffset.x || 0}px, ${selectedGroupDragOffset.y || 0}px)`
													: undefined,
											}}
										/>
									) : null}
								</div>
							</div>
						</Fragment> :
						<div className='diverted-dashboard' id='diverted-view'
							dir='ltr'
							style={{
								width: (isFixedWidthMode && fixedViewWidth) ? `${fixedViewWidth}px` : '100%',
								position: 'relative',
								justifyContent: 'flex-start',
								alignContent: 'flex-start',
								overflowX: isPercentageLayout ? 'hidden' : 'auto',
								overflowY: isPercentageLayout ? 'hidden' : 'auto'
							}}>
							<div
								style={
									isPercentageLayout
										? {
											position: 'relative',
											width: '100%',
											height: viewPercentageCanvas.height > 0 ? `${viewPercentageCanvas.height}px` : '100%',
											minWidth: '100%',
											minHeight: '100%',
										}
										: undefined
								}
							>
								<WaterNetworkOverlay
									width={viewWaterNetworkState?.width}
									height={viewWaterNetworkState?.height}
									connections={viewWaterNetworkState?.connections || []}
								/>
								{(dashboard?.widgets || []).map(widget => {
									const matchDevice = (devices || []).find(d => Number(d?.id) === Number(widget.device_id));
									const datastreamSource = matchDevice ? (matchDevice.template?.datastreams || []).filter(ds => ds) : validDatastreams;
									const percentageRenderRect = isPercentageLayout ? getWidgetRectPx(widget) : null;
									const linkedDatastream = findDatastreamByReference(
										datastreamSource,
										widget.data_id ?? widget.dataId,
										widget.data_pin ?? widget.dataPin,
									);

									const dynamicProps = { ...widget.extra_props };

									const displayTitleForViewMode = widget.title.replace(/^\[.*?\]\s*/, '').trim();

									return (<Widget
										transparentBackground={widget.transparentBackground}
										draggable={false}
										title={preserveTitleSpaces(displayTitleForViewMode)}
										did={matchDevice ? matchDevice.did : did}
										deviceId={widget.device_id}
										tid={matchDevice ? matchDevice.template?.tid : tid}
										devices={devices}
										resizeWidget={resizeWidget} resize={false} id={widget.id} key={widget.id}
										dataId={widget.data_id}
										width={widget.width} height={widget.height} type={widget.type}
										dataPin={linkedDatastream?.pin || widget.data_pin || widget.dataPin || ''}
										decimals={linkedDatastream?.decimals || ''}
										defaultDivHeight={defaultDivHeight} position={'absolute'}
										layoutMode={layoutMode}
										top={widget.top} left={widget.left} extraProps={dynamicProps}
										onTerminalCleared={handleTerminalCleared}
										defaultDivWidth={defaultDivWidth}
										renderRect={percentageRenderRect}
										duplicate={duplicate} remove={remove} datastreams={datastreamSource} />);
								})}
							</div>
						</div>
				}
			</div >

			<AppDialog
				open={removeBackgroundDialog.open}
				type="confirm"
				variant="info"
				icon="alert"
				title={t.removeBackgroundDialogTitle || 'Remove Background Image'}
				message={removeBackgroundDialogMessage}
				confirmText={t.continueReposition || 'Continue'}
				cancelText={t.cancel || 'Cancel'}
				showCancel
				onConfirm={continueRemoveBackground}
				onCancel={resetRemoveBackgroundDialog}
				onClose={resetRemoveBackgroundDialog}
			/>

			{showAlert && (
				<Alert
					type={alertType}
					position={alertPosition}
					message={alertMessage}
					open={showAlert}
					close={() => setShowAlert(false)}
				/>
			)}
		</>
	);
};


export default WebDashboard;
